<template>
  <!-- FI-1818 tenant admin for the ops desk surface. one composite GET on
       mount hydrates every section; per-tab save calls hit granular
       PATCH endpoints so a slug-uniqueness error doesn't roll back the
       distribution edit on the next tab. the host is the Event Response
       Manager settings page (tenant_config_erm.vue, with the page prop), where
       the window scrolls. the settings modal the Event Log and the Event Board
       shared retired on 2026-10-01; the modal-body paths left here (modalBody,
       the .modal-content midnight rules) are what the page prop switches off. -->
  <div class="od-set-shell" :class="{ 'od-set-layout--page': page }">
    <div v-if="loading" class="od-set-loading">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading settings...
    </div>

    <!-- FI-2166: on desktop the sections are a left rail (the tenant settings
         shell's squared nav), rendered as the shared tab bar in vertical mode so
         the sections' aria-labelledby ids, the roving tabindex and the arrow keys
         are one code path with the phone tab bar. only one bar is in the dom at
         a time. every section change goes through selectSection, which is where
         the dirty guard lands. -->
    <div v-else class="od-set-layout" :class="{ 'od-set-layout--rail': !isMobileView }">
      <fi-tab-bar
        v-if="!isMobileView"
        vertical
        class="od-set-nav"
        :value="activeTab"
        :tabs="tabDefs"
        id-prefix="od-set-tab"
        aria-label="Event Response Manager settings sections"
        @input="selectSection"
      />
      <div
        class="od-set-content"
        :class="{
          'od-set-content--list': phoneListShown,
          'od-set-content--fwd': phoneDrill && drillDir === 'fwd',
          'od-set-content--back': phoneDrill && drillDir === 'back',
        }"
      >
      <!-- classic keeps the phone tab strip; horizon and midnight drill in
           (the list below) -->
      <fi-tab-bar
        v-if="isMobileView && !phoneDrill"
        :value="activeTab"
        :tabs="tabDefs"
        id-prefix="od-set-tab"
        aria-label="Event Response Manager settings sections"
        @input="selectSection"
      />
      <!-- the phone drill-in: the desktop section rail as the first screen,
           one row per section with what it holds, so nothing sits off the
           edge of a swipe strip. a row opens its section and the host's
           header (the settings shell's FiPageRail) carries the back, from
           headerRail. v-show, not v-if, so the
           rows keep the od-set-tab-<id> ids the sections' aria-labelledby
           point at; only one bar of those ids is ever in the dom. -->
      <nav
        v-if="phoneDrill"
        v-show="phoneList"
        class="od-set-sections"
        aria-label="Event Response Manager settings sections"
        @touchstart.passive="tapHold"
      >
        <button
          v-for="t in tabDefs"
          :key="t.id"
          :id="'od-set-tab-' + t.id"
          type="button"
          class="od-set-sections-row"
          @click="openPhoneSection(t.id)"
        >
          <span class="od-set-sections-icon" aria-hidden="true"><i class="fa" :class="t.icon"></i></span>
          <span class="od-set-sections-text">
            <span class="od-set-sections-label">{{ t.label }}<span v-if="t.dot" class="od-set-sections-dot" aria-hidden="true"></span><span v-if="t.dot" class="sr-only">, unsaved changes</span></span>
            <span v-if="t.desc" class="od-set-sections-desc">{{ t.desc }}</span>
          </span>
          <i class="fa fa-chevron-right od-set-sections-chev" aria-hidden="true"></i>
        </button>
      </nav>

      <!-- under horizon / midnight the open section's Save lives in the
           pinned header (headerRail), so its save error shows here, first
           thing in the section, rather than under a save row the header
           replaced. a category page shows its own at the top of its body. -->
      <div v-if="headerMode && !phoneListShown && !catPageOpen && sectionSave && sectionSave.error"
           class="od-set-save-error od-set-section-error" role="alert">{{ sectionSave.error }}</div>

      <!-- ===== General ===== -->
      <!-- FI-2201 (#1815): settings that apply across ERM, under one Save
           like Departments and Huddle, so its dot and the close guard cover
           it. the duplicate check saves through settings/general; the two
           email rules came over from the default recipients page and keep
           settings/notifications -->
      <section v-show="activeTab === 'general'" role="tabpanel" aria-labelledby="od-set-tab-general">
        <div class="od-set-section">
          <div class="od-set-lead">
            <p class="od-set-lead-text">Settings that apply across Event Response Manager.</p>
          </div>

          <h4 class="od-set-sect-head">Filing</h4>
          <label class="od-set-label od-set-label--small">Duplicate check</label>
          <div class="od-set-access-row">
            <od-toggle
              v-model="form.duplicate_check_enabled"
              :disabled="savingGeneral"
              class="od-set-access-toggle"
              :aria-label="form.duplicate_check_enabled ? 'Duplicate check is on' : 'Duplicate check is off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.duplicate_check_enabled">Flag possible duplicates</span>
                <span v-else>No duplicate check</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.duplicate_check_enabled">
                  When a new issue has the same charter and category as one logged
                  in the last 7 days, your team sees the earlier event in Add entry
                  before saving, and both events link to each other. Nothing is
                  blocked, and people using the public form aren't shown other events.
                </span>
                <span v-else>
                  Issues are logged without looking for an earlier one on the same charter.
                </span>
              </div>
            </div>
          </div>

          <h4 class="od-set-sect-head">Email</h4>
          <!-- FI-2087 status-change emails toggle. governs only the email to
               whoever filed the event; the in-app status cards always flow. -->
          <label class="od-set-label od-set-label--small">Status update emails</label>
          <div class="od-set-access-row">
            <od-toggle
              v-model="form.status_change_emails_enabled"
              :disabled="savingGeneral"
              class="od-set-access-toggle"
              :aria-label="form.status_change_emails_enabled ? 'Status update emails are on' : 'Status update emails are off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.status_change_emails_enabled">Submitters get status updates by email</span>
                <span v-else>In-app updates only</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.status_change_emails_enabled">
                  Whoever filed an event gets an email each time it moves to a
                  new status, like In Progress or Resolved.
                </span>
                <span v-else>
                  Status changes don't send emails. Whoever filed the event
                  still sees updates in their in-app notifications.
                </span>
              </div>
            </div>
          </div>

          <!-- FI-2097 tenant default for email subject ordering; a person
               can override it on their own profile. -->
          <label class="od-set-label od-set-label--small">Email subject style</label>
          <b-form-select
            v-model="form.email_subject_format"
            :options="emailSubjectFormatOptions"
            size="sm"
            class="od-set-subject-format"
            :disabled="savingGeneral"
          />
          <div class="od-set-access-sub od-set-subject-format-sub">
            <span v-if="form.email_subject_format === 'readable'">
              Subjects lead with the event details, like "Maintenance 063 J. Alvarez Event #214".
            </span>
            <span v-else>
              Subjects lead with the event number, like "Ops Event #214: Maintenance - J. Alvarez - 063".
            </span>
            This is the default for everyone here; each person can pick their own style on their profile.
          </div>

          <!-- classic's save row; horizon / midnight save from the header -->
          <div v-if="!headerMode" class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="generalDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="generalSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(generalSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingGeneral || !generalDirty" @click="saveGeneral">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingGeneral">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="!headerMode && generalError" class="od-set-save-error">{{ generalError }}</div>
        </div>
      </section>

      <!-- ===== Public form ===== -->
      <section v-show="activeTab === 'form'" role="tabpanel" aria-labelledby="od-set-tab-form">
        <div class="od-set-section">
          <div class="od-set-lead">
            <p class="od-set-lead-text">
              The form path is the end of your form's URL. Use lowercase
              letters, digits, and hyphens, 3 to 40 characters, starting
              with a letter.
            </p>
          </div>

          <label class="od-set-label">Form path</label>
          <div class="od-set-slug-row">
            <b-form-input
              v-model="form.slug"
              placeholder="e.g. acme-fleet"
              :state="slugInputState"
              class="od-set-slug-input"
            />
            <!-- at rest the row shows a single quiet "Saved" chip in the
                 button slot — no Save button, no in-input check, no status
                 line (see slugInputState / slugStatusText). the moment the
                 admin edits, the chip flips to an actionable Save and the
                 validation affordances light up. -->
            <!-- classic saves the path here; horizon / midnight save it with
                 the header's Save, which takes the whole section -->
            <b-button
              v-if="!headerMode && (slugIsDraft || !savedSlug)"
              variant="primary" size="sm"
              :disabled="savingSlug || !slugCanSave"
              @click="saveSlug"
              class="od-set-slug-save-btn"
            >
              <span v-if="savingSlug">Saving...</span><span v-else>Save</span>
            </b-button>
            <span v-else-if="!slugIsDraft && savedSlug" class="od-set-slug-saved-chip" :title="slugSavedAt ? ('Last saved ' + formatRelative(slugSavedAt)) : 'Current form path'">
              <i class="fa fa-check"></i>&nbsp;Saved
            </span>
          </div>
          <!-- status line + error only surface while editing a draft; the
               resting "owned_by_self" verdict is intentionally silent so the
               chip is the lone confirmation. -->
          <div class="od-set-slug-status" :class="slugStatusClass" v-if="slugStatusText">
            <i class="fa" :class="slugStatusIcon"></i>&nbsp;{{ slugStatusText }}
          </div>
          <!-- under horizon / midnight a refused save is the section's error
               at the top, from the header's Save -->
          <div v-if="slugError && !headerMode" class="od-set-error">{{ slugError }}</div>

          <label class="od-set-label od-set-label--small">Form URL</label>
          <div class="od-set-url-box">
            <!-- the url renders as one inline-block span per path piece so a
                 narrow box wraps after a slash instead of mid-word -->
            <code v-if="publicUrlPreview"><span v-for="(seg, i) in publicUrlSegments" :key="i" class="od-set-url-seg">{{ seg }}</span></code>
            <code v-else>(set a form path first)</code>
            <div class="od-set-url-actions" v-if="publicUrlPreview">
              <b-button size="sm" variant="outline-secondary" class="od-set-url-btn" @click="copyPublicUrl">
                <i class="fa" :class="copiedAt ? 'fa-check' : 'fa-clipboard'"></i>&nbsp;<span v-if="copiedAt">Copied</span><span v-else>Copy</span>
              </b-button>
              <b-button size="sm" variant="outline-secondary" class="od-set-url-btn" :href="publicUrlPreview" target="_blank" rel="noopener">
                <i class="fa fa-external-link"></i>&nbsp;Open
              </b-button>
            </div>
          </div>
          <div class="od-set-url-hint" v-if="slugIsDraft && publicUrlPreview">
            <i class="fa fa-info-circle"></i>&nbsp;This link goes live when you save.
          </div>

          <!-- FI-1938: a scannable copy of the url. a sign in the yard or a
               visor card is how this link actually travels, so the code
               renders from the SAVED short url (never a half-typed draft)
               and only while the form accepts public submissions: a poster
               pointing at a disabled form is a support ticket waiting to
               happen. drawn client-side, nothing leaves the browser. -->
          <div v-if="qrUrl" class="od-set-qr">
            <canvas ref="qrCanvas" class="od-set-qr-canvas" aria-label="QR code for the public form"></canvas>
            <div class="od-set-qr-text">
              <div class="od-set-qr-headline">Scan to open the form</div>
              <div class="od-set-qr-sub">Points at the saved URL above. Print it for a sign in the yard or a visor card.</div>
              <b-button size="sm" variant="outline-secondary" class="od-set-url-btn" :disabled="qrDownloading" @click="downloadQr">
                <i class="fa fa-download"></i>&nbsp;Download PNG
              </b-button>
              <div v-if="qrError" class="od-set-error">{{ qrError }}</div>
            </div>
          </div>

          <!-- access toggle: when off, the public form returns a friendly
               "unavailable" state to non-authed visitors. authed staff
               still file in-app via "+ Add entry" without using the path.
               FI-2201 (#1815) it rides the tab's Save below, not a flip -->
          <label class="od-set-label od-set-label--small">Public access</label>
          <div class="od-set-access-row">
            <od-toggle
              v-model="form.public_form_enabled"
              :disabled="savingAccess"
              class="od-set-access-toggle"
              :aria-label="form.public_form_enabled ? 'Public access is on' : 'Public access is off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.public_form_enabled">Anyone with the link can submit</span>
                <span v-else>Logged-in users only</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.public_form_enabled">
                  Visitors don't need an account. Share the link above by email,
                  on posters, or as a QR code.
                </span>
                <span v-else>
                  The link still works, but anyone who isn't signed in sees a
                  "form unavailable" message. Your team can still add entries
                  from inside Event Response Manager.
                </span>
              </div>
            </div>
          </div>

          <!-- the Public access Save, last on the tab; the form path above
               keeps its own Save beside the input (classic; horizon /
               midnight save both from the header) -->
          <div v-if="!headerMode" class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="accessDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="accessSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(accessSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingAccess || !accessDirty" @click="saveAccess">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingAccess">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="!headerMode && accessError" class="od-set-save-error">{{ accessError }}</div>
        </div>
      </section>

      <!-- ===== Categories ===== -->
      <!-- FI-2166: one table, one row per category. FI-2201 (#1815) the On
           switch, All / None and Restore defaults edit a draft of the enabled
           lists (listEdits), and the list's Save sends them in one PATCH
           (saveEnabledLists). a row's recipients are edited on the
           category's page (pass B), never in the list. -->
      <section v-show="activeTab === 'categories'" role="tabpanel" aria-labelledby="od-set-tab-categories">
        <div class="od-set-section">
          <!-- the list stays mounted under a page (v-show) so a Back lands on
               the same rows at the same scroll position; the page is built
               fresh each time it opens (v-if) so its draft never outlives it -->
          <div v-show="!catPage" class="od-set-list">
          <div class="od-set-lead" tabindex="-1">
            <p class="od-set-lead-text">Enabled categories and notes show in forms. Disabling one archives its events and removes it from forms. Enabling it restores the archived events.</p>
            <div class="od-set-lead-actions">
              <b-button variant="link" size="sm" class="od-set-action-secondary" :disabled="categoriesBusy"
                        title="Turn every category and notes on. Names and recipients are not changed."
                        @click="restoreDefaultCategories">
                <i class="fa fa-undo"></i>&nbsp;Restore defaults
              </b-button>
              <!-- FI-2201 (#1815) the list's save state in view of the top
                   rows, where the FI-2166 hint sat: while the table has
                   unsaved changes the corner carries its Save too, the same
                   Save as the row under the table -->
              <template v-if="!headerMode && (listDirty || savingList)">
                <span v-if="categoriesError" class="od-set-rail-hint od-set-rail-hint--bad">{{ categoriesError }}</span>
                <span v-else class="od-set-rail-hint">Unsaved changes</span>
                <b-button variant="primary" size="sm" :disabled="categoriesBusy || !listDirty" @click="saveEnabledLists">
                  <i class="fa fa-save"></i>&nbsp;<span v-if="savingList">Saving...</span><span v-else>Save</span>
                </b-button>
              </template>
              <span v-else-if="!headerMode && categoriesSavedAt" class="od-set-rail-hint od-set-rail-hint--ok">Saved {{ formatRelative(categoriesSavedAt) }}</span>
            </div>
          </div>

          <!-- the default recipients are not a category and have no On switch,
               so they live above the table as a card and the thead governs
               only category rows: a row below wearing the Default chip is
               pointing at this card. the opener is a real button; the card
               click is a mouse convenience on top. -->
          <div class="od-set-every" @click="openEveryPage">
            <div class="od-set-every-main">
              <div class="od-set-every-name">Default recipients</div>
              <p class="od-set-every-sub" id="od-set-every-sub">Every new event and comment goes to these recipients unless a category or notes below has its own.</p>
            </div>
            <div class="od-set-every-recip">
              <!-- portal users are not shown here; they are on the page -->
              <span v-if="form.distribution_departments.length" class="od-set-chips">
                <span v-for="d in form.distribution_departments" :key="'every-' + d" class="od-tag-input-pill od-tag-input-pill--info">{{ d }}</span>
                <span v-if="form.email_only.length" class="od-set-cell-muted">+{{ form.email_only.length }} outside</span>
              </span>
              <span v-else-if="form.email_only.length" class="od-set-cell-muted">{{ form.email_only.length }} outside {{ form.email_only.length === 1 ? 'address' : 'addresses' }}</span>
              <span v-else-if="form.fi_user_ids.length" class="od-set-cell-muted">Portal users only</span>
              <span v-else class="od-set-cell-muted">No recipients yet</span>
            </div>
            <button type="button" class="od-set-chev" ref="chev-every" aria-label="Open Default recipients" title="Open Default recipients" aria-describedby="od-set-every-sub" :disabled="categoriesBusy" @click.stop="openEveryPage">
              <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>

          <!-- a plain table rather than b-table: the group rows and the
               per-row switch are hand-built, and rc.28's slot model would
               fight both. the openers are real buttons; the tr click is a
               mouse convenience on top. -->
          <table class="od-set-table" aria-label="Categories">
            <thead>
              <tr>
                <th class="od-set-cell-tg">On</th>
                <th>Category</th>
                <th class="od-set-col-recip">Recipients</th>
                <!-- FI-2162: Who can see column mounts here -->
                <th class="od-set-cell-ch"><span class="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="g in categoryGroups">
                <tr :key="g.kind + '-grp'" class="od-set-grp">
                  <td colspan="4">
                    <div class="od-set-grp-in">
                      <span>{{ g.label }}</span>
                      <!-- a one-row group (Notes) has nothing to bulk-flip -->
                      <span v-if="g.cats.length > 1" class="od-set-bulk">
                        <button type="button" class="od-set-bulk-btn"
                                :aria-label="'Turn on every ' + g.noun + ' category'"
                                :title="'Turn on every ' + g.noun + ' category that is already offered'"
                                :disabled="categoriesBusy"
                                @click.stop="selectAllCategories(g.kind)">All</button>
                        <span class="od-set-bulk-sep" aria-hidden="true">/</span>
                        <button type="button" class="od-set-bulk-btn"
                                :aria-label="'Turn off every ' + g.noun + ' category'"
                                :title="'Turn off every ' + g.noun + ' category (confirms first)'"
                                :disabled="categoriesBusy"
                                @click.stop="clearCategories(g.kind)">None</button>
                      </span>
                    </div>
                  </td>
                </tr>
                <!-- an empty incident set is allowed (the public form hides the
                     category picker and submits without one), so say what the
                     form will look like rather than block it -->
                <tr v-if="g.kind === 'incident' && !listDraft.enabled_incident.length" :key="g.kind + '-empty'" class="od-set-note-row">
                  <td colspan="4"><i class="fa fa-info-circle" aria-hidden="true"></i>&nbsp;No issue categories are on. Reporters cannot file an issue until one is.</td>
                </tr>
                <tr v-for="(cat, i) in g.cats" :key="g.kind + '-' + cat"
                    class="od-set-row"
                    :class="{ 'od-set-row--stripe': i % 2 === 1, 'od-set-row--off': !rowChecked(g.kind, cat) }"
                    @click="openCategoryPage(g.kind, cat)">
                  <td class="od-set-cell-tg">
                    <!-- the accessible name is the category alone; the count
                         pill and the sub line describe it. od_toggle's root
                         click does not stop propagation, hence .native.stop -->
                    <od-toggle
                      :size="isMobileView ? 'md' : 'sm'"
                      :value="rowChecked(g.kind, cat)"
                      :disabled="categoriesBusy"
                      :aria-label="categoryLabel(cat)"
                      :aria-describedby="rowDescribedBy(g.kind, cat)"
                      @input="onRowToggle(g.kind, cat, $event)"
                      @click.native.stop
                    />
                  </td>
                  <td>
                    <!-- FI-1944 a fleet's own category, or a renamed built-in,
                         wears a muted tag after its name -->
                    <div class="od-set-row-name">
                      {{ categoryLabel(cat) }}<span
                        v-if="isCustomCategory(cat)"
                        class="od-set-custom-tag"
                        title="Added by your fleet"
                      >Custom</span><span
                        v-else-if="isRenamedCategory(cat)"
                        class="od-set-custom-tag"
                        :title="'Originally ' + readableLabel(cat)"
                      >Renamed</span><span
                        v-if="categoryUsage[cat]"
                        :id="rowId(g.kind, cat, 'count')"
                        class="od-set-cat-count"
                        :title="usageTitle(cat)"
                      >{{ categoryUsage[cat] }}</span>
                    </div>
                    <div v-if="rowSummary(g.kind, cat)" :id="rowId(g.kind, cat, 'sub')" class="od-set-row-sub">{{ rowSummary(g.kind, cat) }}</div>
                  </td>
                  <td class="od-set-col-recip od-set-cell-recip">
                    <!-- 'other' is one slug in both lists; its route belongs to
                         the issue row (FI-1936 single-owner rule) -->
                    <span v-if="g.kind === 'kudos' && cat === 'other'" class="od-set-cell-muted">Same as Other under issues</span>
                    <span v-else-if="routeChipsBySlug[cat]" class="od-set-chips">
                      <span v-for="d in routeChipsBySlug[cat].departments" :key="g.kind + '-' + cat + '-' + d" class="od-tag-input-pill od-tag-input-pill--info">{{ d }}</span>
                      <span v-if="routeChipsBySlug[cat].outside" class="od-set-cell-muted">+{{ routeChipsBySlug[cat].outside }} outside</span>
                      <span v-if="!routeChipsBySlug[cat].departments.length && !routeChipsBySlug[cat].outside" class="od-set-cell-muted">No one</span>
                    </span>
                    <span v-else class="od-set-default-chip" title="Uses the default recipients">Default</span>
                  </td>
                  <td class="od-set-cell-ch">
                    <!-- inside v-for the ref is an array; pass B reads [0] -->
                    <button type="button" class="od-set-chev"
                            :ref="'chev-' + g.kind + '-' + cat"
                            :aria-label="'Open ' + categoryLabel(cat)"
                            :title="'Open ' + categoryLabel(cat)"
                            :disabled="categoriesBusy"
                            @click.stop="openCategoryPage(g.kind, cat)">
                      <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </button>
                  </td>
                </tr>
                <!-- FI-1944 (#1816) the group's Add row: a fleet's own
                     category, added on and saved on Add (an explicit action,
                     so it does not wait for the list's Save). notes are one
                     fixed slug, so they get none -->
                <tr v-if="g.kind !== 'note'" :key="g.kind + '-add'" class="od-set-add-row">
                  <td colspan="4">
                    <div class="od-set-add">
                      <b-form-input
                        :ref="'addInput-' + g.kind"
                        v-model="customDraft[g.kind]"
                        size="sm"
                        maxlength="40"
                        class="od-set-add-input"
                        :placeholder="g.kind === 'kudos' ? 'New kudos category' : 'New issue category'"
                        :aria-label="g.kind === 'kudos' ? 'Name of a new kudos category' : 'Name of a new issue category'"
                        :disabled="customAtLimit"
                        @input="customError[g.kind] = ''"
                        @keydown.enter.prevent="addCustomCategory(g.kind)"
                      />
                      <b-button size="sm" variant="outline-secondary"
                                title="Add this category and turn it on"
                                :disabled="categoriesBusy || customAtLimit || !(customDraft[g.kind] || '').trim()"
                                @click="addCustomCategory(g.kind)">
                        <i class="fa fa-plus" aria-hidden="true"></i>&nbsp;Add
                      </b-button>
                    </div>
                    <div v-if="customError[g.kind]" class="od-set-hint od-set-hint--bad" role="alert">{{ customError[g.kind] }}</div>
                    <div v-else-if="customAtLimit" class="od-set-hint">You can have up to 20 custom categories. Remove one that no events use to add another.</div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <!-- FI-2201 (#1815) the list's Save row: the switches, All / None
               and Restore defaults above wait for it (the lead band's corner
               carries the same Save while there is something to save).
               classic only: horizon / midnight save from the header -->
          <div v-if="!headerMode" class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="listDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="categoriesSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(categoriesSavedAt) }}</span>
              <b-button variant="link" size="sm" class="od-set-action-secondary" :disabled="categoriesBusy || !listDirty" @click="discardList">
                <i class="fa fa-times" aria-hidden="true"></i>&nbsp;Discard
              </b-button>
              <b-button variant="primary" size="sm" :disabled="categoriesBusy || !listDirty" @click="saveEnabledLists">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingList">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="!headerMode && categoriesError" class="od-set-save-error">{{ categoriesError }}</div>
          </div>

          <!-- a category's page, or the default recipients page (the distribution
               list). the ERM event
               page's edit rail: back, eyebrow + title, save state and the
               Save / Discard pair in the corner. the category page edits a
               draft and rides this Save, not the list's (its access switch
               is the row's On switch, seeded from the saved lists); the Every
               page edits form directly and Save is the distribution save. -->
          <div v-if="catPage" class="od-set-page">
            <!-- classic's page rail. under horizon / midnight the page is its
                 own step in the pinned header (headerRail): the back, the
                 eyebrow and title and the Save / Discard pair in the corner -->
            <div v-if="!headerMode" class="od-rail">
              <!-- held while either page's save is in flight: leaving
                   mid-save would let Discard restore lists the server is
                   about to confirm -->
              <button class="od-rail-back" type="button" ref="pageBack" :disabled="categoriesBusy || pageBusy"
                      aria-label="Back to categories" title="Back to categories" @click="backToList">
                <i class="fa fa-arrow-left"></i>
              </button>
              <div class="od-rail-text">
                <div class="od-rail-eyebrow">{{ pageEyebrow }}</div>
                <h3 class="od-rail-title">{{ pageTitle }}</h3>
              </div>
              <div class="od-rail-actions">
                <span v-if="pageErrorText" class="od-set-rail-hint od-set-rail-hint--bad">{{ pageErrorText }}</span>
                <span v-else-if="pageBusy" class="od-set-rail-hint">Saving...</span>
                <span v-else-if="pageDirty" class="od-set-rail-hint">Unsaved changes</span>
                <span v-else-if="pageSavedAtValue" class="od-set-rail-hint od-set-rail-hint--ok">Saved {{ formatRelative(pageSavedAtValue) }}</span>
                <!-- the labels hide under 600px, so the names live on the
                     buttons themselves and the glyphs stay out of them -->
                <b-button class="od-act-right" variant="primary" size="sm" :disabled="!pageCanSave"
                          title="Save changes" aria-label="Save" @click="savePage">
                  <i class="fa fa-check" aria-hidden="true"></i><span class="od-btn-label">&nbsp;Save</span>
                </b-button>
                <b-button variant="link" size="sm" :disabled="!pageDirty || pageBusy"
                          title="Discard changes" aria-label="Discard" @click="discardPage">
                  <i class="fa fa-times" aria-hidden="true"></i><span class="od-btn-label">&nbsp;Discard</span>
                </b-button>
              </div>
            </div>

            <div class="od-set-page-body">
              <!-- horizon / midnight show the page's save error here, under
                   the header that carries its Save (headerRail) -->
              <div v-if="headerMode && pageErrorText" class="od-set-save-error od-set-page-error" role="alert">{{ pageErrorText }}</div>
              <div class="od-set-form-col">
                <!-- the default recipients page -->
                <template v-if="catPage.kind === 'every'">
                  <div class="od-set-hint od-set-hint--lead">
                    These recipients get every new event and comment unless a
                    category or notes has its own. Portal users get an in-app
                    notification and an email; outside addresses get the email
                    only.
                  </div>

                  <label class="od-set-label">Portal users</label>
                  <!-- restrict only when we actually have a roster to pick from.
                       get_users is soft-caught to [] on failure, so an
                       unconditional restrict would silently block every entry if
                       that call ever fails. fall back to free entry in that case. -->
                  <od-tag-input
                    v-model="form.fi_user_ids"
                    separator=" ,;"
                    :placeholder="tenantUsersLoading ? 'Loading users...' : 'Start typing a name or username...'"
                    variant="primary"
                    class="od-set-tags"
                    :suggestions="fiUserSuggestions"
                    :restrict-to-suggestions="tenantUsersLoaded && tenantUserCandidates.length > 0"
                    invalid-text="Pick a user from the list"
                  />
                  <div class="od-set-hint" v-if="tenantUsersLoaded && !tenantUserCandidates.length">
                    No portal users to pick from yet.
                  </div>

                  <label class="od-set-label">Departments</label>
                  <!-- closed set: only departments defined in the Departments section
                       make sense here, and names resolve to members at send time. -->
                  <od-tag-input
                    v-model="form.distribution_departments"
                    separator=","
                    :placeholder="form.departments.length ? 'Add a department...' : 'No departments defined yet'"
                    variant="info"
                    class="od-set-tags"
                    :suggestions="departmentSuggestions"
                    :restrict-to-suggestions="true"
                    invalid-text="Pick a department from the list"
                    duplicate-text="Already in list"
                  />
                  <div class="od-set-hint" v-if="!form.departments.length">
                    Define departments under Departments to route by team.
                  </div>
                  <div class="od-set-hint">
                    Departments deliver to whoever is a member when the notification
                    goes out, so roster changes apply automatically.
                  </div>

                  <label class="od-set-label">Outside email addresses</label>
                  <od-tag-input
                    v-model="form.email_only"
                    separator=" ,;"
                    placeholder="Add email and press Enter..."
                    variant="secondary"
                    class="od-set-tags"
                    :validator="emailValidator"
                    :suggestions="emailOnlySuggestions"
                    invalid-text="Not a valid email address"
                    duplicate-text="Already in list"
                  />
                  <!-- FI-2201 (#1815) the status update emails and subject
                       style rules moved to General: neither picks recipients -->

                </template>

                <!-- one category -->
                <template v-else>
                  <!-- FI-1944 (#1816) the Name rides the page draft and its
                       Save. a built-in keeps its original name as the
                       placeholder and the Reset target; Other and notes
                       keep their names -->
                  <div v-if="pageHasName" class="od-set-name-block">
                    <label class="od-set-label od-set-label--small" for="od-set-cat-name">Name</label>
                    <b-form-input
                      id="od-set-cat-name"
                      v-model="pageDraft.label"
                      size="sm"
                      maxlength="40"
                      class="od-set-name-input"
                      :placeholder="isCustomPage ? '' : pageOriginalName"
                      :state="pageNameError ? false : null"
                      aria-describedby="od-set-cat-name-error"
                    />
                    <!-- checked as the admin types, so announced politely:
                         the region stays mounted and only its text changes -->
                    <div id="od-set-cat-name-error" aria-live="polite">
                      <div v-if="pageNameError" class="od-set-hint od-set-hint--bad">{{ pageNameError }}</div>
                    </div>
                    <div v-if="pageOriginalShown" class="od-set-hint">
                      Original name: {{ pageOriginalName }}<b-button
                        variant="link" size="sm" class="od-set-action-secondary od-set-name-reset"
                        title="Use the original name" @click="resetPageName">Reset</b-button>
                    </div>
                    <div class="od-set-hint">
                      Renaming changes the name only. {{ pageLabel }} keeps its questions, its starting severity, and its recipients.
                    </div>
                    <div v-if="pageRenameUsageText" class="od-set-hint">{{ pageRenameUsageText }}</div>
                  </div>
                  <div v-else class="od-set-hint od-set-hint--lead od-set-name-block">Other and notes keep their names.</div>

                  <div class="od-set-access-row">
                    <od-toggle
                      v-model="pageDraft.enabled"
                      class="od-set-access-toggle"
                      :aria-label="pageDraft.enabled ? 'Shown in all submission forms is on' : 'Shown in all submission forms is off'"
                    />
                    <div class="od-set-access-text">
                      <div class="od-set-access-headline">Shown in all submission forms</div>
                      <!-- FI-1944 the mobile apps still show a category's
                           original name, so a category is not named here -->
                      <div class="od-set-access-sub">
                        Reporters can pick {{ isNotePage ? pageLabel : 'this category' }} on the public form, in Add entry, and in the mobile apps.
                      </div>
                    </div>
                  </div>
                  <!-- off means archived: say what happens to the events it
                       already has before the admin saves. the note page
                       names notes rather than a category (FI-2173) -->
                  <div v-if="!pageDraft.enabled && isNotePage" class="od-set-archive-note">
                    {{ noteArchiveText }}
                  </div>
                  <div v-else-if="!pageDraft.enabled" class="od-set-archive-note">
                    While it is off, its {{ archiveNoteCount }} archived. The event log, analytics and exports
                    show {{ archiveNotePronoun }} only when archived events are included, and the board and the
                    huddle leave {{ archiveNotePronoun }} out. Turning it back on restores {{ archiveNotePronoun }}.
                  </div>

                  <!-- FI-2162: Who can see section mounts here -->

                  <label class="od-set-label od-set-label--small">Who gets notified</label>
                  <!-- 'other' is one slug in both lists and its route belongs
                       to the issue row (FI-1936 single-owner rule), so the
                       kudos page points there instead of offering pickers -->
                  <template v-if="isKudosOtherPage">
                    <div class="od-set-hint od-set-hint--lead">Recipients for Other are set on the issue Other page.</div>
                    <b-button variant="link" size="sm" class="od-set-action-secondary" :disabled="categoriesBusy" @click="openIssueOtherPage">
                      <i class="fa fa-arrow-right"></i>&nbsp;Open the issue Other page
                    </b-button>
                  </template>
                  <template v-else>
                    <!-- a routed page cannot go back to inheriting by emptying
                         the pickers (pageInvalid), so the hint points at the
                         link instead; an unrouted page says what it inherits.
                         the note page says notes, and that its one row covers
                         them all (FI-2173) -->
                    <div v-if="pageDraft.routed && isNotePage" class="od-set-hint od-set-hint--lead">
                      These recipients replace the default recipients for notes.
                      To go back to the default recipients, use the link below.
                    </div>
                    <div v-else-if="pageDraft.routed" class="od-set-hint od-set-hint--lead">
                      These recipients replace the default recipients for this
                      category. To go back to the default recipients, use the
                      link below.
                    </div>
                    <div v-else-if="isNotePage" class="od-set-hint od-set-hint--lead">
                      Notes have no categories, so this one row covers every note.
                      Notes use the default recipients. Add recipients here to
                      replace them for notes.
                    </div>
                    <div v-else class="od-set-hint od-set-hint--lead">
                      This category uses the default recipients. Add recipients
                      here to replace them for this category.
                    </div>

                    <label class="od-set-label od-set-label--small">Departments</label>
                    <od-tag-input
                      v-model="pageDraft.departments"
                      separator=","
                      :placeholder="form.departments.length ? 'Add a department...' : 'No departments defined yet'"
                      variant="info"
                      class="od-set-tags"
                      :suggestions="departmentSuggestions"
                      :restrict-to-suggestions="true"
                      invalid-text="Pick a department from the list"
                      duplicate-text="Already in list"
                      @input="syncPageRouted"
                    />
                    <div class="od-set-hint" v-if="!form.departments.length">
                      Define departments under Departments to route by team.
                    </div>

                    <label class="od-set-label od-set-label--small">Outside email addresses</label>
                    <od-tag-input
                      v-model="pageDraft.extra_emails"
                      separator=" ,;"
                      placeholder="Add email and press Enter..."
                      variant="secondary"
                      class="od-set-tags"
                      :validator="emailValidator"
                      :suggestions="emailOnlySuggestions"
                      invalid-text="Not a valid email address"
                      duplicate-text="Already in list"
                      @input="syncPageRouted"
                    />
                    <!-- clearing both pickers by hand would silence the
                         category; the link below is how it goes back to
                         inheriting -->
                    <div v-if="pageInvalid" class="od-set-hint od-set-hint--bad">
                      Pick at least one recipient, or use the default recipients.
                    </div>
                    <b-button v-if="pageDraft.routed || pageDraft.departments.length || pageDraft.extra_emails.length"
                              variant="link" size="sm" class="od-set-action-secondary" @click="usePageDistributionList">
                      <i class="fa fa-undo"></i>&nbsp;Use the default recipients
                    </b-button>
                  </template>

                  <label class="od-set-label od-set-label--small">Huddle and Brief</label>
                  <div class="od-set-status-line" :class="huddleStatusLine.ok ? 'od-set-status-line--ok' : 'od-set-status-line--bad'">
                    <i class="fa" :class="huddleStatusLine.ok ? 'fa-check' : 'fa-times'" aria-hidden="true"></i>&nbsp;{{ huddleStatusLine.text }}
                  </div>

                  <!-- FI-1944 (#1816) a fleet's own category can go only while
                       no event uses it (archived ones count); once one does,
                       turning it off is how it leaves the forms -->
                  <template v-if="isCustomPage">
                    <label class="od-set-label od-set-label--small">Remove category</label>
                    <div class="od-set-hint od-set-hint--lead">{{ removeHintText }}</div>
                    <b-button v-if="!pageEventCount" variant="link" size="sm" class="od-set-remove-cat od-act-danger"
                              :disabled="categoriesBusy || pageBusy" @click="removeCustomCategory">
                      <i class="fa fa-trash-o" aria-hidden="true"></i>&nbsp;Remove
                    </b-button>
                    <div v-if="customRemoveError" class="od-set-hint od-set-hint--bad" role="alert">{{ customRemoveError }}</div>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== Departments ===== -->
      <section v-show="activeTab === 'departments'" role="tabpanel" aria-labelledby="od-set-tab-departments">
        <div class="od-set-section">
          <div class="od-set-lead">
            <p class="od-set-lead-text">
              Departments are the teams that respond to events, like Maintenance,
              Safety, or Dispatch. Add a department and choose who's on it.
            </p>
          </div>

          <!-- FI-2201 (#1815) the add controls lead and the list follows
               under its own label; a new department lands at the top of it -->
          <label class="od-set-label od-set-label--small">Add a department</label>
          <div class="od-dept-custom-row">
            <b-form-input
              v-model="deptDraft"
              placeholder="e.g. Charter Sales"
              size="sm"
              class="od-dept-custom-input"
              @keydown.enter.prevent="commitDeptDraft"
            />
            <b-button size="sm" variant="outline-secondary" :disabled="!deptDraft.trim()" @click="commitDeptDraft">
              Add
            </b-button>
          </div>

          <template v-if="availableDeptSuggestions.length">
            <label class="od-set-label od-set-label--small">Quick add</label>
            <div class="od-dept-suggest-row">
              <button
                v-for="d in availableDeptSuggestions"
                :key="'sug-' + d"
                type="button"
                class="od-dept-chip od-dept-chip--suggest"
                @click="addDepartment(d)"
              >
                <i class="fa fa-plus"></i>&nbsp;{{ d }}
              </button>
            </div>
          </template>

          <label id="od-dept-list-label" class="od-set-label od-set-label--small">Your departments</label>
          <div v-if="form.departments.length" class="od-dept-list" role="list" aria-labelledby="od-dept-list-label">
            <!-- keyed per object, not by index: a prepend would otherwise
                 hand each card's member-box draft to its neighbour -->
            <div v-for="(dept, i) in form.departments" :key="deptKey(dept)" class="od-dept-card" role="listitem">
              <div class="od-dept-card-head">
                <b-form-input
                  v-model="dept.name"
                  size="sm"
                  class="od-dept-name-input"
                  placeholder="Department name"
                />
                <button
                  type="button"
                  class="od-dept-remove"
                  :aria-label="'Remove ' + (dept.name || 'department')"
                  title="Remove department"
                  @click="removeDepartment(i)"
                >
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
              <div v-if="departmentsBlankAttempted && !(dept.name || '').trim()" class="od-dept-name-error">
                Name this department or remove it.
              </div>
              <label class="od-set-label od-set-label--small">Members</label>
              <od-tag-input
                v-model="dept.members"
                separator=" ,;"
                :placeholder="tenantUsersLoading ? 'Loading users...' : 'Add a member by name...'"
                variant="info"
                :suggestions="fiUserSuggestions"
                :restrict-to-suggestions="tenantUsersLoaded && tenantUserCandidates.length > 0"
                invalid-text="Pick a user from the list"
              />
              <div class="od-dept-member-empty" v-if="!dept.members.length">
                No members yet. Add the people who handle this team's work.
              </div>
            </div>
          </div>
          <div v-else class="od-dept-empty">
            No departments yet. Add one above to get started.
          </div>

          <div v-if="!headerMode" class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="departmentsDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="departmentsSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(departmentsSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingDepartments || !departmentsDirty" @click="saveDepartments">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingDepartments">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="!headerMode && departmentsError" class="od-set-save-error">{{ departmentsError }}</div>
        </div>
      </section>

      <!-- ===== Huddle ===== -->
      <section v-show="activeTab === 'huddle'" role="tabpanel" aria-labelledby="od-set-tab-huddle">
        <div class="od-set-section">
          <div class="od-set-lead">
            <p class="od-set-lead-text">
              A recap of recent event activity, emailed to your distribution
              list on the schedule you set. Pick the days and time that work for
              your team, and manage who receives it on the Default recipients
              card under Categories.
            </p>
          </div>

          <!-- section heads give the tab room to grow: upcoming huddle
               expansion (insights, stats) slots in as new sections. -->
          <h4 class="od-set-sect-head">Delivery</h4>
          <div class="od-set-access-row od-set-access-row--compact">
            <od-toggle
              v-model="form.huddle_enabled"
              class="od-set-access-toggle"
              :aria-label="form.huddle_enabled ? 'Scheduled sends are on' : 'Scheduled sends are off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.huddle_enabled">Runs on the schedule below</span>
                <span v-else>No scheduled sends</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.huddle_enabled">
                  Subscribers receive the recap automatically. Contact support if you
                  need it sent outside the schedule.
                </span>
                <span v-else>
                  Subscriptions stay intact; nothing sends until you turn this on.
                  Contact support if you need a recap sent in the meantime.
                </span>
              </div>
            </div>
          </div>

          <label class="od-set-label">Send on</label>
          <div class="od-huddle-day-chips" role="group" aria-label="Days to send the huddle">
            <button v-for="d in huddleDayOptions" :key="d.value"
                    type="button"
                    :class="['od-huddle-day-chip', { 'od-huddle-day-chip--active': huddleDays.includes(d.value) }]"
                    @click="toggleHuddleDay(d.value)">
              {{ d.label }}
            </button>
          </div>

          <div class="od-huddle-when-row">
            <div class="od-huddle-when-col">
              <label class="od-set-label">At</label>
              <div class="od-huddle-time-picker">
                <b-form-select v-model="huddleHour12" :options="huddleHourOptions" size="sm" class="od-huddle-time-sel"/>
                <span class="od-huddle-time-colon">:</span>
                <b-form-select v-model="huddleMinute" :options="huddleMinuteOptions" size="sm" class="od-huddle-time-sel"/>
                <b-form-select v-model="huddleAmPm" :options="huddleAmPmOptions" size="sm" class="od-huddle-time-sel od-huddle-time-sel--ampm"/>
              </div>
            </div>
            <div class="od-huddle-when-col">
              <label class="od-set-label">Timezone</label>
              <b-form-select v-model="form.huddle_tz" :options="tzOptions" size="sm" class="od-set-select"/>
            </div>
          </div>

          <label class="od-set-label od-set-label--small">Preview</label>
          <div class="od-set-preview-box">
            <i class="fa fa-calendar-o"></i>&nbsp;{{ huddleScheduleLabel || 'Pick at least one day' }}
          </div>

          <div v-if="huddleCronUnparseable" class="od-huddle-advanced-warning">
            <i class="fa fa-info-circle"></i>
            Your existing schedule uses an advanced expression
            (<code>{{ form.huddle_cron }}</code>) that this picker can't represent.
            Saving a schedule change will replace it with the simple schedule above.
          </div>

          <!-- 2026-07-31 review: the today board was the only opt-in surface
               and customers never see it, so enrollment lives here. FI-2201
               (#1815) an add or a remove edits a draft (huddleSubsDraft) that
               rides the tab's Save below.
               these rows are ADDITIONS: the sender unions them with the
               notifications distribution list, so this list never shows
               (or silences) people the distribution carries. -->
          <h4 class="od-set-sect-head">Additional recipients</h4>
          <p class="od-set-help">The huddle goes to your default recipients (the card under Categories). Anyone added here receives it as well.</p>
          <div class="od-set-sub-list">
            <div v-for="s in huddleSubsDraft" :key="s.user_id" class="od-set-sub-row">
              <span class="od-set-sub-id">{{ s.email || s.user_id }}</span>
              <span v-if="s.email && s.user_id !== s.email" class="od-set-sub-user">{{ s.user_id }}</span>
              <button type="button" class="od-set-sub-remove" :disabled="savingHuddle"
                      :title="'Remove ' + (s.email || s.user_id)"
                      :aria-label="'Remove ' + (s.email || s.user_id)"
                      @click="removeHuddleSubscriber(s)">
                <i class="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div v-if="!huddleSubsDraft.length" class="od-set-sub-empty">No additional recipients.</div>
          </div>
          <div class="od-set-sub-add">
            <b-form-input v-model="huddleSubAdd" size="sm" placeholder="user@example.com"
                          class="od-set-sub-input" @keyup.enter="addHuddleSubscriber"/>
            <b-button size="sm" variant="outline-secondary" :disabled="savingHuddle || !huddleSubAdd.trim()" @click="addHuddleSubscriber">
              <i class="fa fa-plus"></i>&nbsp;Add
            </b-button>
          </div>

          <!-- FI-2201 (#1815) rides the tab's Save with the schedule (the
               huddle row carries both), so it lights the dot like any edit -->
          <h4 class="od-set-sect-head">Fleet Assistant Brief</h4>
          <div class="od-set-access-row od-set-access-row--compact">
            <od-toggle
              v-model="form.fa_brief_enabled"
              :disabled="!faEnabled"
              class="od-set-access-toggle"
              :aria-label="form.fa_brief_enabled ? 'Fleet Assistant Brief is on' : 'Fleet Assistant Brief is off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="!faEnabled">Requires Fleet Assistant</span>
                <span v-else-if="form.fa_brief_enabled">Daily brief is on</span>
                <span v-else>Daily brief is off</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="!faEnabled">Turn on Fleet Assistant for this tenant to enable the Brief.</span>
                <span v-else-if="form.fa_brief_enabled">Fleet Assistant writes a short daily summary of your open events and refreshes it as they change. Generation counts toward your monthly AI budget.</span>
                <span v-else>Turn this on for a short daily summary of your open events, written by Fleet Assistant.</span>
              </div>
            </div>
          </div>

          <!-- the tab's Save (schedule, additional recipients and Brief), last
               on the tab so nothing sits below it. FI-2201 (#1815) a Save that
               sent every edit says Saved even over an advanced cron it left
               alone (a recipients or Brief only Save); the amber warning is
               then the one cue that a further Save replaces the cron -->
          <div v-if="!headerMode" class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="huddleEdited" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="huddleSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(huddleSavedAt) }}</span>
              <span v-else-if="huddleDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <b-button variant="primary" size="sm" :disabled="savingHuddle || !huddleDays.length || !huddleDirty" @click="saveHuddle">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingHuddle">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="!headerMode && huddleError" class="od-set-save-error">{{ huddleError }}</div>
        </div>
      </section>

      <!-- ===== Import ===== -->
      <!-- importTabVisible as well as the activeTab match: staff switching
           ?tenant= away from Anderson while sitting on this tab would
           otherwise keep the panel up with no tab in the bar to leave it by -->
      <section v-show="importTabVisible && activeTab === 'import'" role="tabpanel" aria-labelledby="od-set-tab-import">
        <div class="od-set-section">

          <!-- connect wizard: no sheet saved yet, or the admin is changing sheets -->
          <template v-if="importWizardActive">
            <div class="od-set-lead">
              <p class="od-set-lead-text">
                Keep your existing Google Sheet and Event Response Manager in step. Connect the
                sheet below; Fleet Integrations reads it on a schedule and mirrors each
                row into Event Response Manager. It's one-way and read-only, so nothing about your
                sheet changes and entries your team works in Event Response Manager are kept.
              </p>
            </div>

            <div class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">1</span> Share your sheet</div>
              <div class="od-imp-step-body">
                <div class="od-set-preview-box od-imp-sa">
                  <span class="od-imp-sa-email">{{ importServiceAccount || 'unavailable' }}</span>
                  <b-button variant="link" size="sm" class="od-imp-copy" :disabled="!importServiceAccount"
                            :title="importSaCopied ? 'Copied' : 'Copy address'" @click="copyServiceAccount">
                    <i :class="importSaCopied ? 'fa fa-check' : 'fa fa-clipboard'"></i>
                  </b-button>
                </div>
                <p class="od-set-help od-imp-help-sub">
                  Add this address as a Viewer in the sheet's Share dialog. It's a
                  service account, not a person, so no one is emailed.
                </p>
              </div>
            </div>

            <div class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">2</span> Paste the sheet link</div>
              <div class="od-imp-step-body">
                <div class="od-imp-connect-row">
                  <b-form-input v-model="form.import_spreadsheet_id" size="sm" class="od-set-input"
                                placeholder="https://docs.google.com/spreadsheets/d/…"
                                @input="resetImportInspect"
                                @keydown.enter.prevent="inspectImport"/>
                  <b-button variant="primary" size="sm"
                            :disabled="inspectingImport || !(form.import_spreadsheet_id || '').trim()"
                            @click="inspectImport">
                    <span v-if="inspectingImport">Connecting...</span><span v-else>Connect</span>
                  </b-button>
                </div>
                <div v-if="importInspectOk" class="od-set-preview-box od-imp-test-ok">
                  <i class="fa fa-check-circle"></i>&nbsp;Found <strong>{{ importInspect.title || 'your sheet' }}</strong>.
                </div>
                <div v-else-if="importInspect" class="od-set-save-error">{{ importInspect.message }}</div>
              </div>
            </div>

            <div v-if="importInspectOk" class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">3</span> Choose the tab to import</div>
              <div class="od-imp-step-body">
                <div class="od-imp-tab-list" role="radiogroup" aria-label="Sheet tabs">
                  <label v-for="t in importInspect.tabs" :key="t.title" class="od-imp-tab-opt"
                         :class="{ 'od-imp-tab-opt--active': form.import_tab === t.title }">
                    <input type="radio" name="od-imp-tab" class="od-imp-tab-radio"
                           :value="t.title" :checked="form.import_tab === t.title"
                           @change="pickImportTab(t.title)"/>
                    <i class="fa fa-table" aria-hidden="true"></i>
                    <span class="od-imp-tab-name">{{ t.title }}</span>
                  </label>
                </div>
                <div v-if="testingImport" class="od-set-help od-imp-help-sub"><i class="fa fa-circle-o-notch fa-spin"></i>&nbsp;Checking the tab...</div>
                <div v-else-if="importTestResult && importTestResult.ok" class="od-set-preview-box od-imp-test-ok">
                  <i class="fa fa-check-circle"></i>&nbsp;{{ importTestResult.message }}
                </div>
                <div v-else-if="importTestResult" class="od-set-save-error">{{ importTestResult.message }}</div>
              </div>
            </div>

            <div v-if="importInspectOk && importTestResult && importTestResult.ok" class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">4</span> Preview what will sync</div>
              <div class="od-imp-step-body">
                <p class="od-set-help od-imp-help-sub">
                  See exactly what a sync would bring into Event Response Manager before anything
                  is written. Rows you've already worked are never overwritten.
                </p>
                <div class="od-set-actions">
                  <b-button variant="outline-secondary" size="sm" :disabled="savingImport || previewingImport" @click="wizardPreview">
                    <i class="fa fa-search"></i>&nbsp;<span v-if="savingImport || previewingImport">Checking...</span><span v-else>Preview import</span>
                  </b-button>
                </div>
                <div v-if="importError" class="od-set-save-error">{{ importError }}</div>
                <div v-if="importRunError" class="od-set-save-error">{{ importRunError }}</div>
                <div v-if="importSummary" class="od-set-preview-box od-imp-summary">
                  <div class="od-imp-summary-head">
                    {{ importSummary.dry_run ? 'Preview' : 'Sync complete' }} &middot; {{ importSummary.sheet_rows }} rows read
                  </div>
                  <div class="od-imp-counts">
                    <span class="od-imp-c od-imp-c--new">{{ importCount('new') }} new</span>
                    <span class="od-imp-c od-imp-c--update">{{ importCount('update') }} updated</span>
                    <span class="od-imp-c">{{ importCount('unchanged') }} unchanged</span>
                    <span v-if="importCount('skipped')" class="od-imp-c od-imp-c--muted">{{ importCount('skipped') }} skipped</span>
                  </div>
                  <p v-if="importCount('skipped')" class="od-set-help od-imp-help-sub od-imp-skip-note">
                    {{ importCount('skipped') }} rows are blank and hold nothing to sync.
                  </p>
                  <div v-if="importAttentionCount" class="od-imp-attn">
                    <div class="od-imp-attn-head">{{ importAttentionCount }} {{ importAttentionCount === 1 ? 'row needs' : 'rows need' }} attention</div>
                    <div v-for="(g, gi) in importErrorGroups" :key="'g' + gi" class="od-imp-err-group">
                      <div class="od-imp-err-group-head">{{ g.count }} {{ g.count === 1 ? 'row' : 'rows' }}: {{ g.reason }}</div>
                      <div class="od-imp-err-group-items">{{ g.items }}</div>
                    </div>
                    <ul v-if="(importSummary.conflicts || []).length" class="od-imp-issues">
                      <li v-for="(c, i) in importSummary.conflicts" :key="'c' + i">Ticket {{ c.ticket || '&mdash;' }}: {{ c.reason }}</li>
                      <li v-if="importSummary.conflicts_truncated" class="od-imp-issue-more">...and {{ importSummary.conflicts_truncated }} more</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="importInspectOk && importSummary" class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">5</span> Turn on automatic sync</div>
              <div class="od-imp-step-body">
                <div class="od-set-access-row od-set-access-row--compact">
                  <od-toggle
                    v-model="form.import_enabled"
                    class="od-set-access-toggle"
                    :aria-label="form.import_enabled ? 'Automatic sync is on' : 'Automatic sync is off'"
                  />
                  <div class="od-set-access-text">
                    <div class="od-set-access-headline">
                      <span v-if="form.import_enabled">Syncs {{ importScheduleLabel }}</span>
                      <span v-else>Manual only</span>
                    </div>
                    <div class="od-set-access-sub">
                      <span v-if="form.import_enabled">New and updated rows flow into Event Response Manager automatically.</span>
                      <span v-else>Nothing syncs on a schedule. You can turn this on any time.</span>
                    </div>
                  </div>
                </div>
                <div v-if="form.import_enabled" class="od-huddle-when-row">
                  <div class="od-huddle-when-col">
                    <label class="od-set-label">Frequency</label>
                    <b-form-select v-model="importFreq" :options="importFreqOptions" size="sm" class="od-set-select"/>
                  </div>
                  <template v-if="importFreq === 'daily'">
                    <div class="od-huddle-when-col">
                      <label class="od-set-label">At</label>
                      <div class="od-huddle-time-picker">
                        <b-form-select v-model="importHour12" :options="huddleHourOptions" size="sm" class="od-huddle-time-sel"/>
                        <span class="od-huddle-time-colon">:00</span>
                        <b-form-select v-model="importAmPm" :options="huddleAmPmOptions" size="sm" class="od-huddle-time-sel od-huddle-time-sel--ampm"/>
                      </div>
                    </div>
                    <div class="od-huddle-when-col">
                      <label class="od-set-label">Timezone</label>
                      <b-form-select v-model="form.import_tz" :options="tzOptions" size="sm" class="od-set-select"/>
                    </div>
                  </template>
                </div>
                <div class="od-set-actions">
                  <b-button variant="primary" size="sm" :disabled="savingImport" @click="finishImportWizard">
                    <i class="fa fa-check"></i>&nbsp;<span v-if="savingImport">Saving...</span><span v-else>Finish setup</span>
                  </b-button>
                </div>
                <div v-if="importError" class="od-set-save-error">{{ importError }}</div>
              </div>
            </div>

            <div v-if="importWasConfigured" class="od-set-actions">
              <b-button variant="link" size="sm" class="od-imp-cancel-wizard" @click="cancelImportWizard">
                Cancel and keep the current connection
              </b-button>
            </div>
          </template>

          <!-- connected: compact management view -->
          <template v-else>
          <div class="od-set-lead">
            <p class="od-set-lead-text">
              Rows from your Google Sheet mirror into Event Response Manager on the schedule
              below. It's one-way and read-only, so nothing about your sheet changes,
              and entries your team works in Event Response Manager are kept.
            </p>
          </div>

          <div class="od-set-preview-box od-imp-connected">
            <i class="fa fa-file-excel-o od-imp-connected-icon" aria-hidden="true"></i>
            <div class="od-imp-connected-text">
              <div class="od-imp-connected-title">{{ importTitle || 'Google Sheet' }}</div>
              <div class="od-imp-connected-sub">Tab: {{ form.import_tab }}</div>
            </div>
            <b-button variant="link" size="sm" class="od-imp-change" @click="changeImportSheet">Change sheet</b-button>
          </div>
          <p v-if="importLastRunLabel" class="od-set-help od-imp-help-sub">{{ importLastRunLabel }}</p>

          <label class="od-set-label">Automatic sync</label>
          <div class="od-set-access-row od-set-access-row--compact">
            <od-toggle
              v-model="form.import_enabled"
              class="od-set-access-toggle"
              :aria-label="form.import_enabled ? 'Automatic sync is on' : 'Automatic sync is off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.import_enabled">Syncs {{ importScheduleLabel }}</span>
                <span v-else>Manual only</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.import_enabled">New and updated rows flow into Event Response Manager automatically. You can still sync now below.</span>
                <span v-else>Nothing syncs on a schedule. Use "Sync now" below to pull the latest.</span>
              </div>
            </div>
          </div>

          <div v-if="form.import_enabled" class="od-huddle-when-row">
            <div class="od-huddle-when-col">
              <label class="od-set-label">Frequency</label>
              <b-form-select v-model="importFreq" :options="importFreqOptions" size="sm" class="od-set-select"/>
            </div>
            <template v-if="importFreq === 'daily'">
              <div class="od-huddle-when-col">
                <label class="od-set-label">At</label>
                <div class="od-huddle-time-picker">
                  <b-form-select v-model="importHour12" :options="huddleHourOptions" size="sm" class="od-huddle-time-sel"/>
                  <span class="od-huddle-time-colon">:00</span>
                  <b-form-select v-model="importAmPm" :options="huddleAmPmOptions" size="sm" class="od-huddle-time-sel od-huddle-time-sel--ampm"/>
                </div>
              </div>
              <div class="od-huddle-when-col">
                <label class="od-set-label">Timezone</label>
                <b-form-select v-model="form.import_tz" :options="tzOptions" size="sm" class="od-set-select"/>
              </div>
            </template>
          </div>

          <div v-if="!headerMode" class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="importDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="importSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(importSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingImport || !importDirty" @click="saveImport">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingImport">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="!headerMode && importError" class="od-set-save-error">{{ importError }}</div>

          <label class="od-set-label od-imp-run-label">Sync now</label>
          <p class="od-set-help od-imp-help-sub">
            Preview shows what a sync would change without writing. Sync now applies new
            and updated rows. Conflicts (a row you edited in Event Response Manager that also changed
            in the sheet) and unmapped rows are reported, never overwritten.
          </p>
          <div class="od-set-actions">
            <b-button variant="outline-secondary" size="sm" :disabled="importBusy || !importConfigured" @click="previewImport">
              <i class="fa fa-search"></i>&nbsp;<span v-if="previewingImport">Checking...</span><span v-else>Preview changes</span>
            </b-button>
            <b-button variant="primary" size="sm" :disabled="importBusy || !importConfigured" @click="runImport">
              <i class="fa fa-refresh"></i>&nbsp;<span v-if="runningImport">Syncing...</span><span v-else>Sync now</span>
            </b-button>
          </div>
          <p v-if="importDirty" class="od-set-help od-imp-help-sub">{{ headerMode ? 'Save your changes before syncing.' : 'Save your changes above before syncing.' }}</p>
          <div v-if="importRunError" class="od-set-save-error">{{ importRunError }}</div>

          <div v-if="importSummary" class="od-set-preview-box od-imp-summary">
            <div class="od-imp-summary-head">
              {{ importSummary.dry_run ? 'Preview' : 'Sync complete' }} &middot; {{ importSummary.sheet_rows }} rows read
            </div>
            <div class="od-imp-counts">
              <span class="od-imp-c od-imp-c--new">{{ importCount('new') }} new</span>
              <span class="od-imp-c od-imp-c--update">{{ importCount('update') }} updated</span>
              <span class="od-imp-c">{{ importCount('unchanged') }} unchanged</span>
              <span v-if="importCount('skipped')" class="od-imp-c od-imp-c--muted">{{ importCount('skipped') }} skipped</span>
            </div>
            <div v-if="!importSummary.dry_run && importSummary.applied" class="od-imp-applied">
              Wrote {{ importSummary.applied.created }} new and {{ importSummary.applied.updated }} updated {{ (importSummary.applied.created + importSummary.applied.updated) === 1 ? 'event' : 'events' }}.
            </div>
            <p v-if="importCount('skipped')" class="od-set-help od-imp-help-sub od-imp-skip-note">
              {{ importCount('skipped') }} rows are blank and hold nothing to sync.
            </p>
            <div v-if="importAttentionCount" class="od-imp-attn">
              <div class="od-imp-attn-head">{{ importAttentionCount }} {{ importAttentionCount === 1 ? 'row needs' : 'rows need' }} attention</div>
              <div v-for="(g, gi) in importErrorGroups" :key="'g' + gi" class="od-imp-err-group">
                <div class="od-imp-err-group-head">{{ g.count }} {{ g.count === 1 ? 'row' : 'rows' }}: {{ g.reason }}</div>
                <div class="od-imp-err-group-items">{{ g.items }}</div>
              </div>
              <ul v-if="(importSummary.conflicts || []).length" class="od-imp-issues">
                <li v-for="(c, i) in importSummary.conflicts" :key="'c' + i">Ticket {{ c.ticket || '&mdash;' }}: {{ c.reason }}</li>
                <li v-if="importSummary.conflicts_truncated" class="od-imp-issue-more">...and {{ importSummary.conflicts_truncated }} more</li>
              </ul>
            </div>
          </div>
          </template>
        </div>
      </section>

      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment/moment'
// the project ships bootstrap-vue 2.0.0-rc.28, which predates BFormTags
// (added in BV 2.2.0). od_tag_input.vue is a small local replacement
// that matches the original markup's v-model + separator + validator
// shape, kept as its own SFC so the runtime-only Vue build can use the
// pre-compiled template instead of a template string.
import OdTagInput from './od_tag_input.vue'
import OdToggle from './od_toggle.vue'
// FI-1938: zero-dependency QR encoder, drawn onto a canvas here. the cjs
// build is imported by path so webpack 4 never resolves the .mjs entry.
import qrcodeGenerator from 'qrcode-generator/dist/qrcode.js'
import FiTabBar from './fi_tab_bar.vue'
import horizonNav from '@/nav/horizon_nav_mixin'

// the header eyebrow over an open section (headerRail)
const SECTION_EYEBROW = 'ERM Settings'
import {
  get_ops_desk_settings,
  update_ops_desk_slug,
  update_ops_desk_access,
  update_ops_desk_notification_settings,
  update_ops_desk_general_settings,
  check_ops_desk_slug,
  update_ops_desk_distribution,
  update_ops_desk_categories,
  add_ops_desk_custom_category,
  remove_ops_desk_custom_category,
  get_huddle_subscribers,
  update_huddle_subscribers,
  update_ops_desk_departments,
  update_ops_desk_huddle_schedule,
  get_ops_desk_import_config,
  update_ops_desk_import_config,
  inspect_ops_desk_import,
  test_ops_desk_import,
  preview_ops_desk_import,
  run_ops_desk_import,
  get_users,
} from '../networking/apis'
import {
  categoryLabel as opsCategoryLabel,
  customCategoryLabels,
  isCustomCategory,
} from '../utils/ops_labels'

// the universe of categories arrives in the /settings response under
// available_incident_categories / available_kudos_categories /
// available_note_categories, which keeps the admin picker in lock-step with
// the backend constants instead of asking the frontend to mirror them. an
// empty fallback is fine; loadAll always overwrites these before the
// categories tab is interactive.

// FI-2173 the three groups of the categories table share one row machinery
// keyed by kind: the enabled list on form and the available list on data.
// notes are a one-slug group ('note') so the same switch, recipients and
// archive apply to them; nothing below branches on kind for a list.
const LIST_KEY = { incident: 'enabled_incident', kudos: 'enabled_kudos', note: 'enabled_note' }
const AVAIL_KEY = { incident: 'incidentCategories', kudos: 'kudosCategories', note: 'noteCategories' }
// the page eyebrow's noun per kind: a note page names the type, not a category
const KIND_NOUN = { incident: 'Issue category', kudos: 'Kudos category', note: 'Entry type' }

// pure: one enabled list with one slug added or removed, kept in the
// available order (a slug the server no longer offers keeps its place at the
// end; the response drops it). module-level so the list's draft and a page's
// save build their lists the same way
function withSlug(available, list, slug, on) {
  const offered = available || []
  const set = new Set(list || [])
  if (on) set.add(slug)
  else set.delete(slug)
  const ordered = offered.filter(c => set.has(c))
  const stray = Array.from(set).filter(c => !offered.includes(c))
  return ordered.concat(stray)
}

// FI-1944 (#1816) category names. a fleet holds up to 20 of its own
// categories; a name is 1-40 characters with a letter or number in it,
// compared with its whitespace collapsed and its case ignored, the way the
// server compares them
const CUSTOM_CATEGORY_MAX = 20
const CATEGORY_NAME_MAX = 40
const cleanCategoryName = v => String(v == null ? '' : v).replace(/\s+/g, ' ').trim()
// a name as the server slugs it (slugify_category_label): accents fold to
// ascii, anything else outside ascii drops, and every other run of
// non-alphanumerics becomes one underscore. 'Driver-Error' -> 'driver_error'
const categoryNameSlug = v => {
  const ascii = Array.from(String(v == null ? '' : v).normalize('NFKD'))
    .filter(ch => ch.charCodeAt(0) < 128).join('').toLowerCase()
  return (ascii.match(/[a-z0-9]+/g) || []).join('_')
}
// the pages whose Name can change: every issue and kudos category but Other,
// which is one slug shared by both groups and the public form's catch-all.
// notes are an entry type with one fixed slug
const hasEditableName = (kind, slug) => (kind === 'incident' || kind === 'kudos') && !!slug && slug !== 'other'
// a refusal's sentence out of a failed call: the api answers a 400 with the
// copy to show ({message}, or the older error_data wrapper). anything else
// is '' and gets the caller's generic line
function apiRefusal(e) {
  const res = e && e.response
  const body = res && res.data
  const status = (res && res.status) || (body && body.status_code)
  if (Number(status) !== 400 || !body || typeof body !== 'object') return ''
  return body.message
    || (body.error_data && (body.error_data.error_message || body.error_data.message))
    || ''
}

// AWS cron grammar the backend matcher actually understands. anything
// outside this is "advanced" — see huddleCronUnparseable below.
const DAY_OPTIONS = [
  { value: 'SUN', label: 'Sun' },
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
]

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const h = i + 1
  return { value: String(h), text: String(h) }
})

const MINUTE_OPTIONS = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55']
  .map(m => ({ value: m, text: m }))

const AMPM_OPTIONS = [
  { value: 'am', text: 'AM' },
  { value: 'pm', text: 'PM' },
]

// FI-2166 a category page draft's canonical form: pick order and email case
// are not edits. module-level so savePage can baseline a draft it has not
// installed yet (the pageSnapshot computed only sees the live one).
function snapshotDraft(d) {
  if (!d) return ''
  const snap = {
    en: !!d.enabled,
    routed: !!d.routed,
    dep: (d.departments || []).slice().sort(),
    em: (d.extra_emails || []).map(e => String(e).toLowerCase()).sort(),
  }
  // FI-1944 the Name, on the pages that have one; spacing is not an edit
  if (typeof d.label === 'string') snap.name = cleanCategoryName(d.label)
  return JSON.stringify(snap)
}

const TZ_OPTIONS = [
  { value: 'America/New_York', text: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', text: 'Central (America/Chicago)' },
  { value: 'America/Denver', text: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', text: 'Arizona (America/Phoenix, no DST)' },
  { value: 'America/Los_Angeles', text: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', text: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', text: 'Hawaii (Pacific/Honolulu, no DST)' },
  { value: 'UTC', text: 'UTC' },
]

export default {
  name: 'ops_desk_settings',
  components: { OdTagInput, OdToggle, FiTabBar },
  // horizonNavActive: the phone drill-in is horizon / midnight only
  mixins: [horizonNav],

  props: {
    // section to open on mount. both hosts pass ?settings_tab=<id> through
    // so deep links (e.g. the huddle panel's manage link) land on the right
    // section; no tab, or one unrecognized, opens General, the first section
    // (resolveInitialSection). FI-2201 (#1815)
    initialTab: { type: String, default: 'general' },
    // FI-2172 page mode (tenant_config_erm.vue): the window is the scroll
    // container, the sticky table header clears the Horizon top bar, the rail
    // keeps its width and the page pays the side gutters. off by default so
    // the modal host is untouched.
    page: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: true,
      activeTab: 'general',
      // the phone breakpoint shared with the modal's full-screen rule
      // (erm_settings_modal.vue .od-settings-modal) and the section rail swap
      isMobileView: typeof window !== 'undefined' && window.innerWidth <= 768,
      // the phone drill-in (phoneDrill): true while the sections list is
      // the screen, false while a section is open. set at mount from the
      // landing; kept across a resize to desktop and back.
      phoneList: true,
      // a header Save just landed: the button outlines green for a moment
      // (headerRail), then goes quiet
      headerSavedFlash: false,
      // the phone drill-in's last move: 'fwd' into a section or a category
      // page, 'back' out to the list or the table. the screen that appears
      // slides in from that side (.od-set-content--fwd / --back) and the
      // host's header follows (headerRail motion, seq per move)
      drillDir: '',
      drillSeq: 0,

      incidentCategories: [],
      kudosCategories: [],
      // FI-2173 the Notes group's one slug; hydrated as ['note'] when the api
      // predates the key
      noteCategories: [],
      // per-category event tallies from the settings payload; a missing key
      // means no events, so the row shows no count pill
      categoryUsage: {},
      // FI-1944 (#1816) the fleet's own categories [{key, label, entry_type}]
      // and its renamed built-ins {slug: label}, off the categories row. the
      // Add rows keep a draft and an inline refusal per group; savingCustom
      // is an add or a remove in flight (one categories call at a time)
      customCategories: [],
      labelOverrides: {},
      customDraft: { incident: '', kudos: '' },
      customError: { incident: '', kudos: '' },
      savingCustom: false,
      customRemoveError: '',
      tzOptions: TZ_OPTIONS,

      // huddle picker state. the form still ultimately persists a cron
      // string so the backend matcher doesn't have to learn a new shape;
      // these inputs compose into form.huddle_cron via watcher.
      huddleDayOptions: DAY_OPTIONS,
      huddleHourOptions: HOUR_OPTIONS,
      huddleMinuteOptions: MINUTE_OPTIONS,
      huddleAmPmOptions: AMPM_OPTIONS,
      huddleDays: ['SUN'],
      huddleHour12: '11',
      huddleMinute: '00',
      huddleAmPm: 'pm',
      // true when the cron string returned by the backend can't be expressed
      // by the picker (e.g. a hand-edited expression with ranges or step
      // values). picker stays usable; we just warn the user that saving
      // will overwrite the existing advanced form.
      huddleCronUnparseable: false,
      // huddle recipient management (admin). huddleSubscribers is what the
      // server holds; FI-2201 (#1815) adds and removes edit the draft rows,
      // and the Huddle Save sends the difference
      huddleSubscribers: [],
      huddleSubsDraft: [],
      huddleSubAdd: '',

      // canonical share URL for the public form. backed by the existing
      // shortener service so customers see a branded short link instead
      // of the raw web path. populated on settings GET and refreshed by
      // the slug PATCH response so a successful save lands the new URL
      // without a second round-trip.
      publicShortUrl: '',
      // FI-1938 qr block state. qrDrawnFor is the url the canvas currently
      // shows, so updated() can draw exactly once whenever the block is
      // (re)created with a url the canvas has not seen.
      qrDownloading: false,
      qrError: '',
      qrDrawnFor: '',
      // FI-2166 categories list save state. one categories PATCH at a time
      // (categoriesBusy). FI-2201 (#1815) the list edits a draft: listEdits
      // holds each row the admin changed, keyed kind:slug because 'other'
      // sits in both groups, as on / off over the saved lists in form, which
      // change only when the server answers. savingList is the list's Save
      // in flight.
      listEdits: {},
      savingList: false,
      // FI-2166 the page that replaced the list: null, { kind: 'every' } or
      // { kind: 'incident' | 'kudos', slug }. a category page edits a draft
      // ({ enabled, routed, departments, extra_emails }) against a json
      // baseline and commits into form only when its PATCH succeeds; the
      // Every page edits form directly and rides the distribution save.
      // listScrollTop is the modal body's scroll position when a row opened,
      // restored on Back so the admin lands on the row they left.
      catPage: null,
      pageDraft: null,
      pageBaseline: '',
      pageSaving: false,
      pageError: '',
      // FI-1944 the server's refusal of a name the page sent: {kind, slug,
      // name, message}, shown under Name while that name is in the box
      pageNameRefusal: null,
      pageSavedAt: null,
      listScrollTop: 0,
      // the saved distribution fields as a form-shaped copy, the restore
      // source for Discard on the Every page (the json snapshot only carries
      // sorted lists, so it cannot restore the form). FI-2201 (#1815) general
      // is the General tab's, which its Save diffs against to send only what
      // changed
      savedForms: { distribution: null, general: null },

      // FI-2097 subject-format picker options
      emailSubjectFormatOptions: [
        { value: 'default', text: 'Ticket number first' },
        { value: 'readable', text: 'Event details first' },
      ],

      form: {
        slug: '',
        public_form_enabled: true,
        // FI-2201 (#1815) on unless a fleet turns it off, like the server
        duplicate_check_enabled: true,
        status_change_emails_enabled: true,
        email_subject_format: 'default',
        fi_user_ids: [],
        email_only: [],
        // department NAMES routed by the distribution; distinct from
        // form.departments (the {name, members[]} entities themselves).
        distribution_departments: [],
        // FI-1936 per-category routes [{category, departments[], extra_emails[]}],
        // read by the Categories list and edited on a category's page; a
        // routed category fans out to its route instead of the
        // distribution list.
        category_routes: [],
        enabled_incident: [],
        enabled_kudos: [],
        // FI-2173 the Notes row: ['note'] on, [] off. hydration reads an
        // absent key as on, so this seed never reaches the server on its own
        enabled_note: [],
        departments: [],
        huddle_enabled: false,
        huddle_cron: '0 23 ? * SUN *',
        huddle_tz: 'America/New_York',
        fa_brief_enabled: false,
        // FI-1964 google sheet -> ops events import
        import_spreadsheet_id: '',
        import_tab: 'Sheet1',
        import_enabled: false,
        import_tz: 'America/New_York',
      },

      // FI-1964 import tab. the daily-cron picker composes into a cron string
      // the backend matcher understands, same as the huddle picker.
      // the connect flow is a wizard (share -> link -> pick tab -> preview ->
      // schedule) until a sheet is saved; after that a compact management
      // view renders and "Change sheet" re-enters the wizard.
      importWizardActive: false,
      importWasConfigured: false, // gates the wizard's cancel affordance
      inspectingImport: false,
      importInspect: null,      // { ok, title, tabs[], message } from inspect
      importTitle: '',          // saved spreadsheet title for the connected view
      importServiceAccount: '',
      // hourly rides the dispatcher's every-hour tick (hour=* cron); daily
      // composes the picker time below. the picker keeps its value while
      // hourly is selected so switching back to daily restores it.
      importFreq: 'daily',
      importFreqOptions: [
        { value: 'hourly', text: 'Every hour' },
        { value: 'daily', text: 'Once a day' },
      ],
      importHour12: '8',
      importMinute: '00',
      importAmPm: 'am',
      savingImport: false,
      importSavedAt: null,
      importError: '',
      testingImport: false,
      importTestResult: null,   // { ok, message }
      previewingImport: false,
      runningImport: false,
      importRunError: '',
      importSummary: null,      // last preview / run summary
      importSaCopied: false,
      importLastRun: null,      // { at, status, error } from the config row

      // live tenant-level fleet assistant flag from the settings GET;
      // defaults on so the brief toggle doesn't flash disabled while
      // the page loads.
      faTenantEnabled: true,

      // tenant user candidate pool for the FI users typeahead. populated
      // lazily on the first Distribution-tab focus; cached for the page.
      tenantUserCandidates: [],
      tenantUsersLoaded: false,
      tenantUsersLoading: false,

      // department picker state. defaults come from the canonical "common
      // teams" list shared across fleets; the picker filters out anything
      // already in form.departments so the suggestion row collapses as
      // the admin clicks through.
      deptDraft: '',
      deptSuggestions: [
        'Maintenance', 'Safety', 'Dispatch', 'Customer Care',
        'Operations', 'Charter Sales', 'HR', 'Training', 'IT',
      ],

      // per-tab save state
      savingSlug: false,
      savingAccess: false,
      savingGeneral: false,
      savingDistribution: false,
      savingCategories: false,
      savingDepartments: false,
      savingHuddle: false,

      // per-tab saved-at timestamps; ui shows "Saved 5 sec ago"
      slugSavedAt: null,
      accessSavedAt: null,
      generalSavedAt: null,
      distributionSavedAt: null,
      categoriesSavedAt: null,
      departmentsSavedAt: null,
      huddleSavedAt: null,

      // per-tab inline save errors; a failed save renders the red box and
      // clears the saved-at hint so the page never asserts success after
      // a failure.
      accessError: '',
      generalError: '',
      distributionError: '',
      categoriesError: '',
      departmentsError: '',
      huddleError: '',

      // set when a departments save was blocked by a blank-named card.
      // gates the per-card "name this" hint so a mid-edit cleared name
      // doesn't nag before the admin tries to save.
      departmentsBlankAttempted: false,

      // post-load baselines for the dirty checks (json snapshots). null
      // until the first successful loadAll so a failed load can't light
      // up Save over data we never had.
      savedSnapshots: {
        general: null,
        access: null,
        distribution: null,
        categories: null,
        departments: null,
        huddle: null,
        import: null,
      },

      // slug-specific validation error (uniqueness conflict, bad shape)
      slugError: '',

      // last persisted slug from the backend — distinguishes "the value
      // in the input is unsaved" from "this is what's live".
      savedSlug: '',

      // realtime availability + short-url preview state. populated by a
      // debounced check on every slug edit so the admin sees
      // Available / Taken / Invalid as they type, plus a preview of
      // the short URL that will get created on save.
      slugCheck: {
        slug: '',
        available: null,        // true | false | null (unknown / pending)
        reason: '',             // empty | invalid_shape | taken | free | owned_by_self
        previewShortUrl: '',
      },
      slugChecking: false,
      slugCheckSeq: 0,         // last-write-wins guard for in-flight checks
      slugCheckTimer: null,

      // transient "copied!" affordance on the copy button. seconds-grained
      // so a second click doesn't re-flash the icon prematurely.
      copiedAt: null,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // FI-1964 sheet import is Anderson-only: the tab is a wizard for wiring
    // up a Google service account, and every other tenant would be shown a
    // setup flow for a feed they have no sheet behind. keys on `tenant` (so
    // ?tenant= wins) rather than user.tenant, which keeps the tab working for
    // staff managing Anderson from their own login.
    //
    // first-segment prefix, not startsWith: `anderson-*` siblings are one
    // fleet group (anderson-759g468f, anderson-qa) but `andersonville-coach`
    // is an unrelated tenant that merely shares the letters. same rule as
    // navbar's visibleTenantOptions and the server's
    // fleet_service._get_multi_tenant_prefixes.
    importTabVisible() {
      const tenant = this.tenant
      if (!tenant || typeof tenant !== 'string') return false
      return tenant.split('-', 1)[0] === 'anderson'
    },
    // section definitions; dots mirror each section's dirty state. FI-2201
    // (#1815) General leads and is where Settings opens: the settings that
    // apply across ERM. Categories follows: an admin asks what happens when
    // someone files a Breakdown, and that table is the answer.
    // desc is the phone drill-in list's second line (customer-facing copy)
    // and icon its tile; the tab bars ignore both.
    tabDefs() {
      const tabs = [
        { id: 'general', label: 'General', icon: 'fa-sliders', dot: this.generalDirty, desc: 'Duplicate check and status emails' },
        // the default recipients page lives on this section, so its dirty
        // state is this section's
        { id: 'categories', label: 'Categories', icon: 'fa-tags', dot: this.categoriesSectionDirty, desc: 'What can be filed and who hears about it' },
        { id: 'departments', label: 'Departments', icon: 'fa-users', dot: this.departmentsDirty, desc: 'The teams that respond to events' },
        // the dot covers both the form path and Public access
        { id: 'form', label: 'Public form', icon: 'fa-globe', dot: this.accessDirty || this.slugIsDraft, desc: 'Your form link and who can use it' },
        { id: 'huddle', label: 'Huddle', icon: 'fa-envelope-o', dot: this.huddleDirty, desc: 'The recap email and its schedule' },
      ]
      if (this.importTabVisible)
        tabs.push({ id: 'import', label: 'Import', icon: 'fa-table', dot: this.importDirty, desc: 'Mirror events from a Google Sheet' })
      return tabs
    },
    // the phone drill-in: under horizon / midnight a phone gets the sections
    // as a list that opens one section at a time, in place of the swipe
    // strip, whose off-screen tabs gave no sign they were there. classic
    // keeps the strip.
    phoneDrill() {
      return this.isMobileView && this.horizonNavActive
    },
    phoneListShown() {
      return this.phoneDrill && this.phoneList
    },
    // under horizon / midnight, at every width, the settings shell's
    // FiPageRail is this body's header (2026-10-01): it names where the
    // admin is and holds that place's Discard + Save, and the shell pins it
    // under the top bar so Save never scrolls away. classic keeps the body's
    // own save rows and the shell's plain banner.
    headerMode() {
      return this.horizonNavActive
    },
    // the open section's save model, one shape for every section and the
    // category page, so the header's pair works the same everywhere. null
    // for a place with no section-wide Save (the Import wizard, whose steps
    // carry their own buttons). discardable is what Discard can put back
    // (Huddle's advanced cron keeps it dirty with nothing to discard).
    sectionSave() {
      if (this.catPageOpen) {
        return { dirty: this.pageDirty, canSave: this.pageCanSave, busy: this.pageBusy, error: this.pageErrorText, discardable: this.pageDirty }
      }
      switch (this.activeTab) {
        case 'general':
          return { dirty: this.generalDirty, canSave: !this.savingGeneral && this.generalDirty, busy: this.savingGeneral, error: this.generalError, discardable: this.generalDirty }
        case 'categories':
          // an Add or a Restore in flight holds Discard as well as Save
          // (discardList waits on categoriesBusy); busy stays the list's own
          // save, so the spinner means a table save only
          return { dirty: this.listDirty, canSave: !this.categoriesBusy && this.listDirty, busy: this.savingList, error: this.categoriesError, discardable: this.listDirty && !this.categoriesBusy }
        case 'departments':
          return { dirty: this.departmentsDirty, canSave: !this.savingDepartments && this.departmentsDirty, busy: this.savingDepartments, error: this.departmentsError, discardable: this.departmentsDirty }
        case 'form': {
          // two saves under one Save: the form path (once its availability
          // check passes) and Public access. a path still being checked or
          // refused keeps its own status line beside the input; a path the
          // server refuses on save is the section's error, like access's
          const dirty = this.accessDirty || this.slugIsDraft
          const busy = this.savingAccess || this.savingSlug
          return { dirty, canSave: !busy && (this.accessDirty || this.slugCanSave), busy, error: this.accessError || this.slugError, discardable: dirty }
        }
        case 'huddle':
          // dirty follows the classic row's order (FI-2201 #1815): a Save
          // that sent every edit reads as saved even over an advanced cron
          // it left alone, which keeps Save live (huddleDirty) for the one
          // further Save that replaces the cron
          return { dirty: this.huddleEdited || (this.huddleDirty && !this.huddleSavedAt), canSave: !this.savingHuddle && this.huddleDays.length > 0 && this.huddleDirty, busy: this.savingHuddle, error: this.huddleError, discardable: this.huddleEdited }
        case 'import':
          if (this.importWizardActive) return null
          return { dirty: this.importDirty, canSave: !this.savingImport && this.importDirty, busy: this.savingImport, error: this.importError, discardable: this.importDirty }
      }
      return null
    },
    // what the header says, every level an eyebrow over a title. the phone
    // list (and the load before it) is the root: Event Response Manager over
    // Settings, with the settings glyph where the levels below put their
    // back, so the eyebrow and title hold still from one level to the next.
    // a section names itself under ERM Settings (short, so the eyebrow never
    // wraps on a phone; Chaz 2026-10-01), with a
    // back to the list on a phone and the glyph on a desktop (whose section
    // rail stays in view). a category's page (or the default recipients
    // page) is a step of its own: the page's own eyebrow and name, and back
    // steps up to the Categories table. busy holds the back while a save is
    // in flight. the actions are the place's Discard + Save (Save
    // rightmost); the buttons carry the save state, so the header never
    // changes height: Save sits quiet (a grey outline) until there is
    // something to save, fills when there is, spins while saving and
    // outlines green for a moment after (headerSavedFlash). status is the
    // same state in words, for a screen reader only. null hands the header
    // back to the shell.
    headerRail() {
      if (!this.headerMode) return null
      const root = { eyebrow: 'Event Response Manager', title: 'Settings', icon: 'fa-cog' }
      if (this.loading) return this.phoneDrill ? root : null
      // a phone drill-in move tells the header which way to slide its title
      const moved = r => (this.phoneDrill && this.drillSeq ? { ...r, motion: { dir: this.drillDir, seq: this.drillSeq } } : r)
      if (this.phoneListShown) return moved(root)
      const t = this.tabDefs.find(d => d.id === this.activeTab)
      if (!t) return null
      const busy = !!(this.categoriesBusy || this.pageBusy)
      let rail
      if (this.catPageOpen) {
        rail = { eyebrow: this.pageEyebrow, title: this.pageTitle, backLabel: 'Back to categories', busy }
      } else if (this.phoneDrill) {
        rail = { eyebrow: SECTION_EYEBROW, title: t.label, backLabel: 'Back to Event Response Manager settings', busy }
      } else {
        rail = { eyebrow: SECTION_EYEBROW, title: t.label, icon: 'fa-cog' }
      }
      const s = this.sectionSave
      if (s) {
        rail.actions = [
          { id: 'discard', label: 'Discard', icon: 'fa-times', variant: 'link', disabled: !s.discardable || s.busy },
          {
            id: 'save',
            label: 'Save',
            icon: s.busy ? 'fa-spinner fa-spin' : 'fa-check',
            // the flash outranks a Save that stays live after a clean save
            // (Huddle over an advanced cron)
            variant: s.busy ? 'primary' : (this.headerSavedFlash && !s.dirty ? 'outline-success' : (s.canSave ? 'primary' : 'outline-secondary')),
            disabled: !s.canSave,
          },
        ]
        // an error is the section's role=alert box's to announce, once
        rail.status = s.error ? '' : (s.busy ? 'Saving...' : (s.dirty ? 'Unsaved changes' : (this.headerSavedFlash ? 'Saved' : '')))
      }
      return moved(rail)
    },
    // a category page counts only under Categories: a clean page stays set
    // when the rail moves on (selectSection), and it must not follow the
    // admin into another section in the header
    catPageOpen() {
      return this.activeTab === 'categories' && !!this.catPage
    },
    // fleet assistant availability for this tenant, hydrated from the
    // settings GET. the login features map drops explicit-false flags so
    // it cannot answer this; the composite response carries the live value.
    faEnabled() {
      return this.faTenantEnabled
    },
    // a draft slug is one the user is typing but hasn't saved yet.
    // used to decide whether the preview URL is "real" (saved) or
    // "predicted" (will be created on save).
    slugIsDraft() {
      const cur = (this.form.slug || '').trim().toLowerCase()
      return !!cur && cur !== (this.savedSlug || '').toLowerCase()
    },
    publicUrlPreview() {
      // when the field matches the saved slug, show the live short URL
      // the server already created. when the field is a draft, show the
      // predicted short URL from the slug-check probe so the admin can
      // see what their save is about to create.
      if (!this.slugIsDraft) return this.publicShortUrl || ''
      return this.slugCheck.previewShortUrl || ''
    },
    // FI-1938: what the qr encodes. the saved short url only (a draft path
    // has no live form behind it) and nothing at all while public access is
    // off, so the block disappears rather than advertising a dead link.
    // FI-2201 (#1815) the SAVED access, since the switch now waits for Save
    qrUrl() {
      if (!this.publicAccessSaved) return ''
      return this.publicShortUrl || ''
    },
    // the Public access Save's snapshot and dirty state
    accessSnapshot() {
      return JSON.stringify({ on: !!this.form.public_form_enabled })
    },
    accessDirty() {
      return this.savedSnapshots.access !== null
        && this.accessSnapshot !== this.savedSnapshots.access
    },
    // what the live form does now: a one-switch draft that differs from its
    // baseline is the opposite of what is saved
    publicAccessSaved() {
      return this.accessDirty ? !this.form.public_form_enabled : !!this.form.public_form_enabled
    },
    // combined input state for b-form-input's :state binding.
    // null = neutral, true = green (available + valid), false = red.
    slugInputState() {
      const cur = (this.form.slug || '').trim().toLowerCase()
      if (!cur) return null
      if (this.slugError) return false
      // at rest (input matches the saved value) stay neutral — the "Saved"
      // chip is the single confirmation. validation coloring only applies
      // while the admin is actively typing a new path.
      if (!this.slugIsDraft) return null
      if (this.slugCheck.slug !== cur) return null  // pending re-check
      if (this.slugCheck.available === true) return true
      if (this.slugCheck.available === false) return false
      return null
    },
    // short banner under the input — the human-readable verdict.
    slugStatusText() {
      if (this.slugChecking) return 'Checking availability...'
      const cur = (this.form.slug || '').trim().toLowerCase()
      if (!cur || this.slugCheck.slug !== cur) return ''
      switch (this.slugCheck.reason) {
        case 'free':           return 'Available'
        // owned_by_self == resting state; stay silent so the "Saved" chip
        // is the only confirmation (no redundant status line).
        case 'owned_by_self':  return ''
        case 'taken':          return 'Already in use by another account'
        case 'invalid_shape':  return 'Lowercase letters, digits, and hyphens; 3 to 40 chars; must start with a letter'
        case 'empty':          return ''
        default:               return ''
      }
    },
    slugStatusClass() {
      if (this.slugChecking) return 'od-set-slug-status--neutral'
      if (this.slugInputState === true) return 'od-set-slug-status--ok'
      if (this.slugInputState === false) return 'od-set-slug-status--bad'
      return 'od-set-slug-status--neutral'
    },
    slugStatusIcon() {
      if (this.slugChecking) return 'fa-spinner fa-spin'
      if (this.slugInputState === true) return 'fa-check-circle'
      if (this.slugInputState === false) return 'fa-times-circle'
      return 'fa-info-circle'
    },
    slugCanSave() {
      const cur = (this.form.slug || '').trim().toLowerCase()
      if (!cur) return false
      if (this.slugChecking) return false
      // require a real edit before Save lights up — otherwise the button
      // reads as "you have unsaved work" when the input still matches
      // what's on the server. the row also switches to a "Saved" chip
      // when not draft, so this is belt-and-suspenders.
      if (!this.slugIsDraft) return false
      // block save when the slug is taken by someone else or shape-invalid.
      return this.slugCheck.slug === cur && this.slugCheck.available === true
    },
    // FI-1818 tenant user candidates pre-formatted for od-tag-input.
    // each suggestion shows username + role/name as sublabel so admins
    // recognize teammates without round-tripping to settings.
    fiUserSuggestions() {
      const candidates = Array.isArray(this.tenantUserCandidates) ? this.tenantUserCandidates : []
      return candidates.map(u => {
        const username = (u.username || '').trim()
        // user records carry first_name / last_name (not a combined name).
        const fullName = [u.first_name, u.last_name]
          .map(p => (p || '').trim()).filter(Boolean).join(' ')
        const role = (u.role || '').trim()
        return {
          value: username,
          // name leads, email is the secondary line, role rides as a chip.
          // fall back to the email as the label when no name is on file.
          label: fullName || username,
          sublabel: fullName ? username : undefined,
          chip: role ? this.readableLabel(role) : undefined,
        }
      }).filter(s => !!s.value)
    },
    // departments defined in the Departments section, offered as a closed
    // set for the distribution. member count rides as the chip so the
    // admin can see what a pick fans out to.
    departmentSuggestions() {
      return (this.form.departments || [])
        .map(d => {
          const name = (d.name || '').trim()
          const n = (d.members || []).length
          return {
            value: name,
            label: name,
            chip: n === 1 ? '1 member' : n + ' members',
          }
        })
        .filter(s => !!s.value)
    },
    // for the email-only field we surface the same tenant users as
    // gentle suggestions (their usernames are emails for admin / dispatch
    // accounts) PLUS anything already saved as email_only — so a teammate
    // who's been hand-typed before can be re-picked from the dropdown.
    // not restrictive: an external partner's email is still valid input.
    emailOnlySuggestions() {
      const out = []
      const seen = new Set()
      for (const u of (this.tenantUserCandidates || [])) {
        const v = (u.username || '').trim()
        if (!v || seen.has(v.toLowerCase())) continue
        if (!this.emailValidator(v)) continue
        seen.add(v.toLowerCase())
        const fullName = [u.first_name, u.last_name]
          .map(p => (p || '').trim()).filter(Boolean).join(' ')
        const role = (u.role || '').trim()
        out.push({
          value: v,
          label: v,
          sublabel: fullName || undefined,
          chip: role ? this.readableLabel(role) : undefined,
        })
      }
      return out
    },
    availableDeptSuggestions() {
      const taken = new Set((this.form.departments || []).map(d => (d.name || '').toLowerCase()))
      return this.deptSuggestions.filter(d => !taken.has(d.toLowerCase()))
    },
    huddleScheduleLabel() {
      const days = this.huddleDays || []
      if (!days.length) return ''
      const tz = this.form.huddle_tz || ''
      const tzShort = tz.split('/').pop().replace(/_/g, ' ')
      const dowPluralMap = {
        SUN: 'Sundays', MON: 'Mondays', TUE: 'Tuesdays', WED: 'Wednesdays',
        THU: 'Thursdays', FRI: 'Fridays', SAT: 'Saturdays',
      }
      const dowShortMap = {
        SUN: 'Sun', MON: 'Mon', TUE: 'Tue', WED: 'Wed',
        THU: 'Thu', FRI: 'Fri', SAT: 'Sat',
      }
      const orderedDays = DAY_OPTIONS.map(d => d.value).filter(v => days.includes(v))
      const weekdaySet = ['MON', 'TUE', 'WED', 'THU', 'FRI']
      const weekendSet = ['SAT', 'SUN']
      let dowLabel
      if (orderedDays.length === 7) dowLabel = 'Every day'
      else if (orderedDays.length === 5 && weekdaySet.every(d => orderedDays.includes(d))) dowLabel = 'Weekdays'
      else if (orderedDays.length === 2 && weekendSet.every(d => orderedDays.includes(d))) dowLabel = 'Weekends'
      else if (orderedDays.length === 1) dowLabel = dowPluralMap[orderedDays[0]] || orderedDays[0]
      else dowLabel = orderedDays.map(d => dowShortMap[d] || d).join(', ')
      const minute = this.huddleMinute || '00'
      const minuteText = minute === '00' ? '' : ':' + minute
      const timeText = (this.huddleHour12 || '12') + minuteText + (this.huddleAmPm || 'pm')
      return dowLabel + ' at ' + timeText + (tzShort ? ' ' + tzShort : '')
    },
    // canonical serializations for the dirty checks. tag and checkbox
    // arrays sort first so click order doesn't read as an edit;
    // departments keep order because the cards are an ordered list.
    distributionSnapshot() {
      return JSON.stringify({
        fi: (this.form.fi_user_ids || []).slice().sort(),
        em: (this.form.email_only || []).slice().sort(),
        dep: (this.form.distribution_departments || []).slice().sort(),
      })
    },
    // FI-2201 (#1815) the General tab: the duplicate check and the two email
    // rules that moved off the default recipients page
    generalSnapshot() {
      return this.generalSnapshotOf(this.form)
    },
    generalDirty() {
      return this.savedSnapshots.general !== null
        && this.generalSnapshot !== this.savedSnapshots.general
    },
    categoriesSnapshot() {
      return JSON.stringify({
        inc: (this.form.enabled_incident || []).slice().sort(),
        kud: (this.form.enabled_kudos || []).slice().sort(),
        note: (Array.isArray(this.form.enabled_note) ? this.form.enabled_note : []).slice().sort(),
        // FI-1936 routes: row and pick order are not edits, so both
        // normalize away here.
        routes: (this.form.category_routes || [])
          .filter(r => !!String((r && r.category) || '').trim())
          .map(r => ({
            c: String(r.category).trim().toLowerCase(),
            dep: (r.departments || []).slice().sort(),
            em: (r.extra_emails || []).map(e => String(e).toLowerCase()).sort(),
          }))
          .sort((a, b) => (a.c < b.c ? -1 : a.c > b.c ? 1 : 0)),
      })
    },
    departmentsSnapshot() {
      return JSON.stringify((this.form.departments || []).map(d => ({
        name: d.name || '',
        members: (d.members || []).slice(),
      })))
    },
    huddleSnapshot() {
      // FI-2201 (#1815) fa_brief_enabled rides the tab's Save, so it is an
      // edit like the schedule
      return JSON.stringify({
        enabled: !!this.form.huddle_enabled,
        fa: !!this.form.fa_brief_enabled,
        tz: this.form.huddle_tz,
        days: DAY_OPTIONS.map(d => d.value).filter(v => (this.huddleDays || []).includes(v)),
        hour: this.huddleHour12,
        minute: this.huddleMinute,
        ampm: this.huddleAmPm,
      })
    },
    distributionDirty() {
      return this.savedSnapshots.distribution !== null
        && this.distributionSnapshot !== this.savedSnapshots.distribution
    },
    // FI-1936 save gate. a route with a category and nothing to send to
    // would silence that category. rows without a category are dropped by
    // routePayloadRows and are never invalid.
    routesInvalid() {
      return (this.form.category_routes || []).some(r => {
        const hasCategory = !!String((r && r.category) || '').trim()
        const hasTargets = ((r && r.departments) || []).length > 0
          || ((r && r.extra_emails) || []).length > 0
        return hasCategory && !hasTargets
      })
    },
    // FI-1936 slug -> route, first wins on a duplicate. the row templates
    // bind v-model through this map, so each route object stays the
    // reactive one inside form.category_routes.
    routesBySlug() {
      const map = {}
      for (const r of (this.form.category_routes || [])) {
        const slug = String((r && r.category) || '').trim().toLowerCase()
        if (!slug || map[slug]) continue
        map[slug] = r
      }
      return map
    },
    categoriesDirty() {
      return this.savedSnapshots.categories !== null
        && this.categoriesSnapshot !== this.savedSnapshots.categories
    },
    // FI-2166 one categories PATCH at a time. while one is in flight every
    // row switch, All / None / Restore defaults, the list's Save and the row
    // openers are disabled, so two calls can never race for the same row.
    categoriesBusy() {
      return !!(this.savingList || this.savingCategories || this.pageSaving || this.savingCustom)
    },
    // FI-2201 (#1815) the list's draft: the saved enabled lists with the
    // row edits applied, what the switches show and the list's Save sends
    listDraft() {
      return this.listsWithEdits(this.listEdits)
    },
    // the draft against the saved lists, order aside; an edit put back is
    // no edit
    listDirty() {
      return this.enabledListsSnapshotOf(this.listDraft) !== this.enabledListsSnapshotOf(this.enabledListsCopy())
    },
    // ---- FI-1944 (#1816) category names ----
    // slug -> the fleet's name, for its own categories and renamed built-ins
    categoryLabels() {
      return customCategoryLabels(this.customCategories, this.labelOverrides)
    },
    customAtLimit() {
      return (this.customCategories || []).length >= CUSTOM_CATEGORY_MAX
    },
    isCustomPage() {
      return !!this.catPage && isCustomCategory(this.catPage.slug)
    },
    // a page with a Name field: every issue and kudos category but Other
    pageHasName() {
      return !!this.catPage && !!this.pageDraft && hasEditableName(this.catPage.kind, this.catPage.slug)
    },
    // a built-in's own name, the placeholder and the Reset target
    pageOriginalName() {
      return this.catPage && this.catPage.slug ? opsCategoryLabel(this.catPage.slug) : ''
    },
    // the name the draft would save: a built-in's blank Name is its
    // original name, a custom category's blank Name is refused
    pageDraftName() {
      if (!this.pageHasName) return ''
      const name = cleanCategoryName(this.pageDraft.label)
      return (!name && !this.isCustomPage) ? this.pageOriginalName : name
    },
    pageNameChanged() {
      return this.pageHasName && this.pageDraftName !== this.pageLabel
    },
    pageNameError() {
      if (!this.pageNameChanged) return ''
      const own = this.categoryNameError(this.pageDraftName, this.catPage)
      if (own) return own
      // the server's word on the name last sent, for as long as that name is
      // still in the box, compared without case as the server does (it may
      // know of a rename made elsewhere)
      const r = this.pageNameRefusal
      const p = this.catPage
      const same = r && p && r.kind === p.kind && r.slug === p.slug
        && r.name.toLowerCase() === this.pageDraftName.toLowerCase()
      return same ? r.message : ''
    },
    pageOriginalShown() {
      return this.pageHasName && !this.isCustomPage && this.pageDraftName !== this.pageOriginalName
    },
    pageRenameUsageText() {
      const n = this.pageEventCount
      if (!this.pageNameChanged || !n) return ''
      return n === 1
        ? '1 event uses this category and will show the new name.'
        : n + ' events use this category and will show the new name.'
    },
    // the custom page's Remove block: removable only while nothing uses it,
    // archived events included. the on / off branches say what the admin
    // can do instead from where the category stands now
    removeHintText() {
      const n = this.pageEventCount
      if (!n) return 'No events use this category yet, so it can be removed.'
      const on = !!this.catPage && this.isEnabled(this.catPage.kind, this.catPage.slug)
      if (on) {
        return n === 1
          ? "1 event uses this category, so it can't be removed. Turn it off instead to take it out of the forms and archive its event."
          : n + " events use this category, so it can't be removed. Turn it off instead to take it out of the forms and archive its events."
      }
      return n === 1
        ? "It's off, so its 1 event is archived. It can't be removed while events use it."
        : "It's off, so its " + n + " events are archived. It can't be removed while events use it."
    },
    // the three groups of the categories table, in table order. notes are a
    // one-slug group (FI-2173): same row, switch and page as a category, no
    // All / None since there is one row to flip
    categoryGroups() {
      return [
        { kind: 'incident', label: 'Issue categories', noun: 'issue', cats: this.incidentCategories },
        { kind: 'kudos', label: 'Kudos categories', noun: 'kudos', cats: this.kudosCategories },
        { kind: 'note', label: 'Notes', noun: 'note', cats: this.noteCategories },
      ]
    },
    // slug -> what the Recipients cell shows for a routed category: the
    // department chips and a count of outside addresses (the addresses
    // themselves are on the page). unrouted slugs are absent.
    routeChipsBySlug() {
      const map = {}
      for (const slug of Object.keys(this.routesBySlug)) {
        const r = this.routesBySlug[slug]
        map[slug] = {
          departments: (r.departments || []).slice(),
          outside: (r.extra_emails || []).length,
        }
      }
      return map
    },
    // ---- FI-2166 category page ----
    pageSnapshot() {
      // FI-1944 the Name as Save would read it: a built-in's blank Name is
      // its original name, so clearing the box on one never renamed is not
      // an edit
      const d = this.pageDraft
      if (d && this.pageHasName && typeof d.label === 'string') {
        return snapshotDraft({ ...d, label: this.pageDraftName })
      }
      return snapshotDraft(d)
    },
    catPageDirty() {
      return !!this.pageDraft && this.pageSnapshot !== this.pageBaseline
    },
    pageBaselineParsed() {
      try { return JSON.parse(this.pageBaseline || '{}') || {} } catch (e) { return {} }
    },
    // a routed category with both pickers emptied by hand would be silenced.
    // the page blocks that when the saved route had targets; the way to stop
    // routing is the Use the default recipients link (routed = false). a
    // target-less route the server already holds stays saveable as it is.
    pageInvalid() {
      const d = this.pageDraft
      if (!d || !d.routed) return false
      if ((d.departments || []).length || (d.extra_emails || []).length) return false
      const base = this.pageBaselineParsed
      return !!((base.dep || []).length || (base.em || []).length)
    },
    isKudosOtherPage() {
      return !!this.catPage && this.catPage.kind === 'kudos' && this.catPage.slug === 'other'
    },
    // the category's saved name (FI-1944: the fleet's own name when it has one)
    pageLabel() {
      return this.catPage && this.catPage.slug ? opsCategoryLabel(this.catPage.slug, this.categoryLabels) : ''
    },
    pageEventCount() {
      return (this.catPage && this.catPage.slug && this.categoryUsage[this.catPage.slug]) || 0
    },
    pageEyebrow() {
      if (!this.catPage) return ''
      if (this.catPage.kind === 'every') return 'Every category and note'
      const n = this.pageEventCount
      const noun = KIND_NOUN[this.catPage.kind] || KIND_NOUN.incident
      return noun + ' \u00b7 ' + n + (n === 1 ? ' event' : ' events')
    },
    pageTitle() {
      if (!this.catPage) return ''
      return this.catPage.kind === 'every' ? 'Default recipients' : this.pageLabel
    },
    // the archive note names the count when there is one
    archiveNoteCount() {
      const n = this.pageEventCount
      if (!n) return 'events are'
      return n + (n === 1 ? ' event is' : ' events are')
    },
    archiveNotePronoun() {
      return this.pageEventCount === 1 ? 'it' : 'them'
    },
    // the note page's archive note: notes are a type, not a category, so
    // the sentence names them (FI-2173)
    isNotePage() {
      return !!this.catPage && this.catPage.kind === 'note'
    },
    noteArchiveText() {
      return this.noteArchiveSentence(this.pageEventCount)
    },
    // the Every page is the distribution form; a category page is its draft
    pageDirty() {
      if (!this.catPage) return false
      return this.catPage.kind === 'every' ? this.distributionDirty : this.catPageDirty
    },
    pageBusy() {
      if (!this.catPage) return false
      return this.catPage.kind === 'every' ? this.savingDistribution : this.pageSaving
    },
    pageErrorText() {
      if (!this.catPage) return ''
      return this.catPage.kind === 'every' ? this.distributionError : this.pageError
    },
    pageSavedAtValue() {
      if (!this.catPage) return null
      return this.catPage.kind === 'every' ? this.distributionSavedAt : this.pageSavedAt
    },
    pageCanSave() {
      if (!this.catPage) return false
      if (this.catPage.kind === 'every') return !this.savingDistribution && this.distributionDirty
      return !this.categoriesBusy && this.catPageDirty && !this.pageInvalid && !this.pageNameError
    },
    // FI-2162 grows this with the restricted clause; it stays a function of
    // the draft's { enabled } and the page's kind
    huddleStatusLine() {
      return this.huddleStatus(
        { enabled: !!(this.pageDraft && this.pageDraft.enabled) },
        this.catPage && this.catPage.kind,
      )
    },
    // the section dot: a dirty page of either kind, or the list's draft
    categoriesSectionDirty() {
      return !!(this.listDirty || this.catPageDirty || this.distributionDirty)
    },
    departmentsDirty() {
      return this.savedSnapshots.departments !== null
        && this.departmentsSnapshot !== this.savedSnapshots.departments
    },
    // what the admin actually changed on the Huddle section; unlike
    // huddleDirty it ignores an unparseable cron, which is dirty from
    // hydration and must not make the close guard ask about nothing
    huddleEdited() {
      return (this.savedSnapshots.huddle !== null
        && this.huddleSnapshot !== this.savedSnapshots.huddle)
        || !!this.huddleRecipientsDirty
    },
    // FI-2201 (#1815) the Additional recipients draft against the saved
    // rows, by id; row order is not an edit
    huddleRecipientsDirty() {
      const ids = rows => JSON.stringify((rows || []).map(s => s.user_id).sort())
      return ids(this.huddleSubsDraft) !== ids(this.huddleSubscribers)
    },
    huddleDirty() {
      // an advanced cron the picker can't represent counts as dirty: the
      // picker shows a schedule that isn't what's stored, and saving
      // replaces it (the warning box explains).
      if (this.huddleCronUnparseable) return true
      return this.huddleEdited
    },
    // FI-1964 import tab
    importSnapshot() {
      return JSON.stringify({
        sheet: (this.form.import_spreadsheet_id || '').trim(),
        tab: (this.form.import_tab || '').trim(),
        enabled: !!this.form.import_enabled,
        freq: this.importFreq,
        tz: this.form.import_tz,
        hour: this.importHour12,
        minute: this.importMinute,
        ampm: this.importAmPm,
      })
    },
    importDirty() {
      return this.savedSnapshots.import !== null
        && this.importSnapshot !== this.savedSnapshots.import
    },
    // read by the modal host's close guard through the ref and mirrored to
    // the page host by the dirty watcher: anything an admin typed and has
    // not saved, snapshot inequality only, plus a typed form path
    anyDirty() {
      return !!(this.generalDirty || this.accessDirty || this.slugIsDraft
        || this.distributionDirty || this.listDirty || this.catPageDirty || this.departmentsDirty
        || this.huddleEdited || this.importDirty)
    },
    // preview / run read the SAVED config server-side, so they're only
    // available once a sheet is saved and there are no pending edits.
    importConfigured() {
      return !this.importDirty && !!(this.form.import_spreadsheet_id || '').trim()
    },
    importBusy() {
      return this.previewingImport || this.runningImport
    },
    importInspectOk() {
      return !!(this.importInspect && this.importInspect.ok)
    },
    // rows the admin has to act on: mapping/duplicate errors + conflicts.
    // rendered as its own section below the skipped note so the number can't
    // read as a caption for the skip explanation.
    importAttentionCount() {
      return this.importCount('error') + this.importCount('conflict')
    },
    // errors grouped by reason so 400 identical rows render as one line.
    // counts come from the server's exact error_reasons aggregate; the
    // itemized (capped) samples supply the ticket/row pointers, deduped so a
    // ticket that appears on 3 rows lists once (the reason carries the count).
    importErrorGroups() {
      const s = this.importSummary
      if (!s || !(s.errors || []).length) return []
      const byReason = {}
      const order = []
      for (const er of s.errors) {
        const reason = er.reason || 'error'
        if (!byReason[reason]) { byReason[reason] = []; order.push(reason) }
        const label = er.ticket ? `Ticket ${er.ticket}` : `Row ${er.row}`
        if (!byReason[reason].includes(label)) byReason[reason].push(label)
      }
      const exact = s.error_reasons || {}
      return order.map(reason => {
        const items = byReason[reason]
        const count = exact[reason] || items.length
        const shown = items.slice(0, 12)
        let label = shown.join(', ')
        // the itemized samples are capped server-side, so past the display cap
        // we only know more exist, not how many distinct tickets.
        if (items.length > shown.length || s.errors_truncated) label += ', and more'
        return { reason, count, items: label }
      })
    },
    importScheduleLabel() {
      if (this.importFreq === 'hourly') return 'every hour'
      const tz = (this.tzOptions.find(o => o.value === this.form.import_tz) || {}).text || this.form.import_tz
      const tzShort = String(tz).split(' (')[0]
      return `daily at ${this.importHour12}:00 ${this.importAmPm.toUpperCase()} ${tzShort}`
    },
    importLastRunLabel() {
      if (!this.importLastRun || !this.importLastRun.at) return ''
      const when = this.formatRelative(this.importLastRun.at)
      if (this.importLastRun.status === 'error') {
        return `Last sync ${when} didn't finish: ${this.importLastRun.error || 'unknown error'}`
      }
      return `Last synced ${when}.`
    },
    // path pieces with their trailing slash so the url box wraps after a
    // segment instead of mid-word. consecutive slashes (the scheme's //)
    // stay glued to the piece before them.
    publicUrlSegments() {
      const url = this.publicUrlPreview
      if (!url) return []
      const parts = url.split('/')
      const out = []
      for (let i = 0; i < parts.length; i++) {
        const piece = parts[i] + (i < parts.length - 1 ? '/' : '')
        if (!parts[i] && out.length) out[out.length - 1] += piece
        else if (piece) out.push(piece)
      }
      return out
    },
  },

  async mounted() {
    const landing = this.resolveInitialSection(this.initialTab)
    this.activeTab = landing.section
    // on a phone a deep link lands in its section; a plain open (General, the
    // default) lands on the sections list
    this.phoneList = !landing.page && landing.section === 'general'
    window.addEventListener('resize', this.onResize)
    // tenant user candidates power the default recipients typeahead. fire it
    // off in parallel: a cheap one-tenant scan whose result populates the
    // dropdown long before the admin opens that page.
    this.loadTenantUsers()
    await this.loadAll()
    // a settings_tab=distribution deep link lands on the default recipients
    // page once the form has data (loadAll resets the page state). nothing
    // to come back to, so no scroll stash.
    if (landing.page === 'every') this.openEveryPage()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    clearTimeout(this._savedFlashTimer)
    // the modal unmounts the body on close: hand the header back
    this.$emit('rail', null)
  },

  watch: {
    tenant() { this.loadAll() },
    // the page host (tenant_config_erm.vue) mirrors this into the settings
    // shell's isDirty; the modal host reads anyDirty through the ref instead
    anyDirty(val) { this.$emit('dirty', val) },
    // the phone drill-in's header, painted by the host: tenant_config_erm.vue
    // relays it to the settings shell's FiPageRail, erm_settings_modal.vue
    // puts it in the modal title. immediate, so a host mounted mid-section
    // (a resize, a deep link) hears it on the first render.
    headerRail: {
      handler(val) { this.$emit('rail', val) },
      immediate: true,
    },
    // a new place in the header starts quiet
    activeTab() { this.endSavedFlash() },
    catPageOpen() { this.endSavedFlash() },
    // the Import section can disappear under the user (staff switching
    // ?tenant= away from Anderson). fall back to the first section (General)
    // so the settings body is never left showing nothing.
    importTabVisible(val) {
      if (!val && this.activeTab === 'import') this.activeTab = 'general'
    },
    // FI-1938: redraw when the encoded url changes on a canvas that already
    // exists (a slug save). the first paint after the block is created by a
    // render pass is updated()'s job, since the ref only exists after that.
    qrUrl() {
      this.$nextTick(this.renderQr)
    },
    // realtime availability + preview short-url. debounce 300ms so a
    // burst of keystrokes only fires one check, and use a sequence
    // counter to drop stale responses (typing 'an' then 'and' shouldn't
    // let the slower 'an' check overwrite the result for 'and').
    'form.slug'(val) {
      const cleaned = (val || '').trim().toLowerCase()
      this.slugError = ''
      if (this.slugCheckTimer) {
        clearTimeout(this.slugCheckTimer)
        this.slugCheckTimer = null
      }
      if (!cleaned) {
        this.slugCheck = { slug: '', available: null, reason: 'empty', previewShortUrl: '' }
        this.slugChecking = false
        return
      }
      // no check needed when the value already matches the saved slug. this
      // fires on load (loadAll sets form.slug to the stored value) and when
      // the admin types their current path back; loadAll already seeded the
      // owned_by_self verdict, so hitting the endpoint would be wasted.
      if (cleaned === (this.savedSlug || '').trim().toLowerCase()) {
        this.slugCheck = {
          slug: cleaned,
          available: true,
          reason: 'owned_by_self',
          previewShortUrl: this.publicShortUrl || '',
        }
        this.slugChecking = false
        return
      }
      this.slugCheckTimer = setTimeout(() => { this.runSlugCheck(cleaned) }, 300)
    },
  },

  // FI-1938: the qr canvas lives inside a v-if, so the first paint has to
  // follow the render pass that creates it; qrDrawnFor keeps this to one
  // draw per url, and it is not read by the template, so no render loop.
  updated() {
    if (this.qrUrl && this.qrDrawnFor !== this.qrUrl) this.renderQr()
  },
  methods: {
    // the one path every section change takes (rail click, tab click, arrow
    // keys). an id that is not a section is ignored, which also covers the
    // Import section disappearing under a staff tenant switch.
    selectSection(id) {
      if (!this.tabDefs.some(t => t.id === id)) return
      if (id === this.activeTab) return
      // a save in flight (a categories PATCH, or the Every page's) holds the
      // rail too: the page or row it is saving must stay where the answer
      // lands. the tab bar refocuses the active tab on its own tick.
      if (this.categoriesBusy || this.pageBusy) return
      // leaving a dirty page asks first; on cancel the focus goes back to
      // the section the admin is still on, since the rail already moved
      if (this.catPage && this.pageDirty) {
        if (!this.confirmDiscard()) {
          this.$nextTick(() => this.focusTab(this.activeTab))
          return
        }
        this.discardPage()
        this.closePage()
      }
      this.activeTab = id
      // a section chosen on the desktop rail stays open if the layout then
      // crosses to a phone, instead of dropping back to the list
      if (!this.phoneDrill) this.phoneList = false
      // the pinned rail can switch sections from deep in a long one: open
      // the new one at its top, just under the pinned header
      if (this.headerMode && this.page && !this.phoneDrill) this.$nextTick(() => this.revealSectionTop())
    },
    revealSectionTop() {
      const host = (this.$el && this.$el.closest && this.$el.closest('.tc-erm')) || this.$el
      if (!host || !host.getBoundingClientRect || !host.scrollIntoView) return
      const pad = parseFloat(window.getComputedStyle(document.documentElement).scrollPaddingTop) || 0
      if (host.getBoundingClientRect().top < pad) host.scrollIntoView({ block: 'start' })
    },
    // the phone drill-in: a list row opens its section through the same
    // guarded path as the rail (selectSection). a refused move (a save in
    // flight, a dirty page kept) leaves the list up. the current section is
    // a no-op for selectSection but still opens here.
    openPhoneSection(id) {
      if (id !== this.activeTab) {
        this.selectSection(id)
        if (this.activeTab !== id) return
      }
      if (this.phoneDrill) this.drillTo('fwd')
      this.phoneList = false
      this.$nextTick(() => this.phoneSettle(this.sectionEl(id)))
    },
    // the header's back, one level up: from a category page to the
    // Categories table (backToList, the page's own back, which the phone
    // hides), from a section to the list. a save in flight holds it. section
    // drafts stay and keep their dots on the list.
    headerBack() {
      if (!this.headerMode) return
      if (this.catPageOpen) {
        this.backToList()
        return
      }
      if (!this.phoneDrill || this.phoneList) return
      if (this.categoriesBusy || this.pageBusy) return
      const from = this.activeTab
      this.drillTo('back')
      this.phoneList = true
      this.$nextTick(() => this.phoneSettle(document.getElementById('od-set-tab-' + from)))
    },
    drillTo(dir) {
      this.drillDir = dir
      this.drillSeq += 1
    },
    // iOS Safari paints :active only under a touch listener: the drill-in
    // rows' press feedback needs one, and it does nothing else
    tapHold() {},
    // a click on one of headerRail's actions, from the shell's header: the
    // open place's Save or Discard. a save that lands clean says so on the
    // button for a moment; one that fails brings its error into view under
    // the pinned header.
    async headerAction(id) {
      const s = this.sectionSave
      if (!s) return
      if (id === 'discard') {
        if (s.discardable && !s.busy) this.discardSection()
        return
      }
      if (id !== 'save' || !s.canSave) return
      const tab = this.activeTab
      const onPage = this.catPageOpen
      await this.saveSection()
      // the admin moved on mid-save: the answer belongs to the place it was
      // saved from, which keeps its dot and shows its error on return
      if (this.activeTab !== tab || this.catPageOpen !== onPage) return
      const after = this.sectionSave
      if (!after) return
      if (after.error) {
        this.$nextTick(() => this.revealSectionError())
      } else if (!after.dirty) {
        this.startSavedFlash()
      } else if (this.activeTab === 'departments' && this.departmentsBlankAttempted) {
        // a blank department refuses the save with only its card's hint
        this.$nextTick(() => this.revealFirst('.od-dept-name-error'))
      }
    },
    saveSection() {
      if (this.catPageOpen) return this.savePage()
      switch (this.activeTab) {
        case 'general': return this.saveGeneral()
        case 'categories': return this.saveEnabledLists()
        case 'departments': return this.saveDepartments()
        case 'form': return this.saveFormSection()
        case 'huddle': return this.saveHuddle()
        case 'import': return this.saveImport()
      }
      return null
    },
    // the Public form section's one Save: the form path when it is ready to
    // save, then Public access when it changed. both are decided at the
    // click, so a flip made while the path is out stays a draft
    async saveFormSection() {
      const slug = this.slugIsDraft && this.slugCanSave
      const access = this.accessDirty
      if (slug) await this.saveSlug()
      if (access && this.accessDirty) await this.saveAccess()
    },
    discardSection() {
      if (this.catPageOpen) return this.discardPage()
      switch (this.activeTab) {
        case 'general': return this.discardGeneral()
        case 'categories': return this.discardList()
        case 'departments': return this.discardDepartments()
        case 'form': return this.discardForm()
        case 'huddle': return this.discardHuddle()
        case 'import': return this.discardImport()
      }
      return null
    },
    // Discard, back to the last save, for the sections that had only a
    // Save. each restores from the baseline its save writes
    // (snapshotBaselines on load, the save itself after), and holds while
    // that save is in flight, since the baseline moves mid-save.
    discardGeneral() {
      const saved = this.savedForms.general
      if (!saved || this.savingGeneral) return
      this.form.duplicate_check_enabled = saved.duplicate_check_enabled
      this.form.status_change_emails_enabled = saved.status_change_emails_enabled
      this.form.email_subject_format = saved.email_subject_format
      this.generalError = ''
    },
    discardDepartments() {
      if (this.savingDepartments || this.savedSnapshots.departments === null) return
      this.form.departments = JSON.parse(this.savedSnapshots.departments)
      this.departmentsBlankAttempted = false
      this.departmentsError = ''
    },
    discardForm() {
      if (this.savingAccess || this.savingSlug) return
      if (this.savedSnapshots.access !== null) this.form.public_form_enabled = JSON.parse(this.savedSnapshots.access).on
      // the form.slug watcher clears the path's error and reseeds its check
      this.form.slug = this.savedSlug
      this.accessError = ''
    },
    discardHuddle() {
      if (this.savingHuddle || this.savedSnapshots.huddle === null) return
      const saved = JSON.parse(this.savedSnapshots.huddle)
      this.form.huddle_enabled = saved.enabled
      this.form.fa_brief_enabled = saved.fa
      this.form.huddle_tz = saved.tz
      this.huddleDays = saved.days.slice()
      this.huddleHour12 = saved.hour
      this.huddleMinute = saved.minute
      this.huddleAmPm = saved.ampm
      // fresh draft copies of the saved recipients
      this.adoptHuddleSubscribers(this.huddleSubscribers)
      this.huddleSubAdd = ''
      this.huddleError = ''
    },
    // the connected view only (the wizard has its own Cancel)
    discardImport() {
      if (this.savingImport || this.savedSnapshots.import === null) return
      const saved = JSON.parse(this.savedSnapshots.import)
      this.form.import_spreadsheet_id = saved.sheet
      this.form.import_tab = saved.tab
      this.form.import_enabled = saved.enabled
      this.importFreq = saved.freq
      this.form.import_tz = saved.tz
      this.importHour12 = saved.hour
      this.importMinute = saved.minute
      this.importAmPm = saved.ampm
      this.importError = ''
    },
    startSavedFlash() {
      this.headerSavedFlash = true
      clearTimeout(this._savedFlashTimer)
      this._savedFlashTimer = setTimeout(() => { this.headerSavedFlash = false }, 1600)
    },
    endSavedFlash() {
      clearTimeout(this._savedFlashTimer)
      this.headerSavedFlash = false
    },
    revealSectionError() {
      this.revealFirst(this.catPageOpen ? '.od-set-page-error' : '.od-set-section-error')
    },
    // scroll a message into view below the pinned header (the document's
    // scroll-padding-top clears the top bar and the header)
    revealFirst(selector) {
      const el = this.$el && this.$el.querySelector ? this.$el.querySelector(selector) : null
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
    },
    sectionEl(id) {
      return this.$el && this.$el.querySelector
        ? this.$el.querySelector('[aria-labelledby="od-set-tab-' + id + '"]')
        : null
    },
    // after a drill-in move: back to the top of the scroller (the document on
    // the Settings page, the modal body in the modal) and focus to the new
    // screen, so a screen reader isn't left on the row or button that just
    // went away. the section takes a programmatic tabindex for that.
    phoneSettle(focusEl) {
      const body = this.modalBody()
      if (body) body.scrollTop = 0
      if (!focusEl || !focusEl.focus) return
      if (focusEl.tagName === 'SECTION') focusEl.setAttribute('tabindex', '-1')
      focusEl.focus({ preventScroll: true })
    },
    // where a settings_tab deep link lands: { section, page }. a known
    // section id opens that section; 'distribution' (the huddle panel's
    // manage link, from when Notifications was a section) opens the Every
    // category page; anything else opens General (FI-2201).
    resolveInitialSection(id) {
      const want = String(id || '')
      if (want === 'distribution') return { section: 'categories', page: 'every' }
      if (want && this.tabDefs.some(t => t.id === want)) return { section: want, page: null }
      return { section: 'general', page: null }
    },
    // one seam for the discard confirm, so specs can answer it
    confirmDiscard() {
      return window.confirm('Discard unsaved changes?')
    },
    focusTab(id) {
      const el = typeof document !== 'undefined' && document.getElementById('od-set-tab-' + id)
      if (el && el.focus) el.focus()
    },
    onResize() {
      this.isMobileView = window.innerWidth <= 768
    },
    // silent is the page host's refresh on arrival (tenant_config_erm.vue
    // load_data(true), the settings shell's contract for every tab): once
    // hydrated, the data refetches and re-baselines behind the rail, the
    // sections and an open category page instead of unmounting them under the
    // spinner. the first load, a tenant switch, a wizard cancel and the
    // shell's discard on leave stay loud: the spinner, and the page reset.
    async loadAll(silent) {
      if (!this.tenant) return
      const quiet = !!silent && this.savedSnapshots.categories !== null
      if (!quiet) this.loading = true
      try {
        const res = await get_ops_desk_settings(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        this.publicShortUrl = data.public_short_url || ''
        const settings = data.settings || {}
        const distribution = data.distribution || {}
        const categories = data.categories || {}
        const departments = data.departments || {}
        const huddle = data.huddle_schedule || {}
        this.faTenantEnabled = data.fleet_assistant_enabled !== false
        this.incidentCategories = (data.available_incident_categories || []).slice()
        this.kudosCategories = (data.available_kudos_categories || []).slice()
        // FI-2173 an api that predates the Notes row still has notes on
        this.noteCategories = Array.isArray(data.available_note_categories)
          ? data.available_note_categories.slice() : ['note']
        this.categoryUsage = data.category_usage || {}
        // FI-2201 (#1815) the recipients load beside the import config and
        // the settings wait for both, so a Huddle Save never sees the rows
        // of the tenant this load replaces
        const subsLoaded = this.loadHuddleSubscribers()
        this.form.slug = settings.slug || ''
        this.savedSlug = settings.slug || ''
        // public_form_enabled defaults true (legacy tenants pre-date the
        // flag and were always accepting public submissions).
        this.form.public_form_enabled = settings.public_form_enabled !== false
        // FI-2201 (#1815) absent means on, the api's default
        this.form.duplicate_check_enabled = settings.duplicate_check_enabled !== false
        // FI-2087: same legacy-default shape as the access flag.
        this.form.status_change_emails_enabled = settings.status_change_emails_enabled !== false
        this.form.email_subject_format = settings.email_subject_format === 'readable' ? 'readable' : 'default'
        // seed the check state so the input shows "current slug" without
        // a flash of "checking…" on first render.
        if (this.savedSlug) {
          this.slugCheck = {
            slug: this.savedSlug,
            available: true,
            reason: 'owned_by_self',
            previewShortUrl: this.publicShortUrl || '',
          }
        }
        this.form.fi_user_ids = (distribution.fi_user_ids || []).map(String)
        this.form.email_only = (distribution.email_only || []).map(String)
        this.form.distribution_departments = (distribution.departments || []).map(String)
        this.form.enabled_incident = (categories.enabled_incident || []).slice()
        this.form.enabled_kudos = (categories.enabled_kudos || []).slice()
        this.form.enabled_note = this.enabledNoteFrom(categories)
        // FI-1936 routes ride the categories payload
        this.form.category_routes = this.hydrateRoutes(categories.category_routes)
        // FI-1944 the fleet's own categories and renamed built-ins; an api
        // that predates them has neither, which reads as none
        this.customCategories = Array.isArray(categories.custom_categories)
          ? categories.custom_categories.slice() : []
        this.labelOverrides = this.labelOverridesFrom(categories)
        // a reload (tenant switch, wizard cancel) drops any list save state
        // and closes whatever page was open: its draft was for the old data.
        // a silent refresh is the same tenant and keeps the page in place.
        if (!quiet) {
          this.savingList = false
          // FI-2201 (#1815) the list's unsaved switches and the huddle
          // recipients were the old data's
          this.listEdits = {}
          this.adoptHuddleSubscribers([])
          this.resetPageState()
          // the Add rows' drafts were typed for the old data too
          this.customDraft = { incident: '', kudos: '' }
          this.customError = { incident: '', kudos: '' }
          // FI-2201 (#1815) a General save time was the old data's
          this.generalSavedAt = null
        }
        // departments arrive as {name, members[]} entities; legacy rows may
        // still be bare strings, so coerce defensively.
        this.form.departments = (departments.departments || []).map(d => (
          typeof d === 'string'
            ? { name: d, members: [] }
            : { name: d.name, members: Array.isArray(d.members) ? d.members.slice() : [] }
        ))
        this.form.huddle_enabled = !!huddle.enabled
        this.form.huddle_cron = huddle.cron || '0 23 ? * SUN *'
        this.form.huddle_tz = huddle.tz || 'America/New_York'
        this.form.fa_brief_enabled = !!huddle.fa_brief_enabled
        this.hydrateHuddlePickerFromCron(this.form.huddle_cron)
        // FI-1964 import config loads with the rest so its dirty baseline is
        // snapshotted together with the other tabs.
        await this.loadImportConfig()
        await subsLoaded
        // fresh hydration is the new clean state for every tab
        this.snapshotBaselines()
        this.departmentsBlankAttempted = false
        this.clearSaveErrors()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings loadAll failed', e)
      } finally {
        if (!quiet) this.loading = false
      }
    },

    // the import config from what is saved. a failure here is soft — the
    // tab still renders with defaults. resolves true once it has loaded
    async loadImportConfig() {
      try {
        const ires = await get_ops_desk_import_config(this.tenant)
        const icfg = (ires && ires.value && ires.value.data) || {}
        this.importServiceAccount = icfg.service_account_email || ''
        this.form.import_spreadsheet_id = icfg.spreadsheet_id || ''
        this.form.import_tab = icfg.tab || 'Sheet1'
        this.form.import_enabled = !!icfg.enabled
        this.form.import_tz = icfg.tz || 'America/New_York'
        this.importTitle = icfg.spreadsheet_title || ''
        this.hydrateImportPickerFromCron(icfg.cron || '0 8 * * ? *')
        this.importLastRun = icfg.last_run_at
          ? { at: icfg.last_run_at, status: icfg.last_run_status, error: icfg.last_error }
          : null
        // no sheet saved yet -> guided connect wizard; otherwise the
        // compact management view (Change sheet re-enters the wizard).
        this.importWizardActive = !icfg.spreadsheet_id
        this.importWasConfigured = !!icfg.spreadsheet_id
        this.importInspect = null
        return true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings load import config failed', e)
        return false
      }
    },

    // baseline the dirty-tracked tabs to the current form state. called
    // after load and per tab after each successful save.
    snapshotBaselines() {
      this.savedSnapshots = {
        general: this.generalSnapshot,
        access: this.accessSnapshot,
        distribution: this.distributionSnapshot,
        categories: this.categoriesSnapshot,
        departments: this.departmentsSnapshot,
        huddle: this.huddleSnapshot,
        import: this.importSnapshot,
      }
      this.savedForms.distribution = this.distributionFormCopy()
      this.savedForms.general = this.generalFormCopy()
    },
    // the three distribution fields as fresh copies
    distributionFormCopy() {
      return {
        fi_user_ids: (this.form.fi_user_ids || []).slice(),
        email_only: (this.form.email_only || []).slice(),
        distribution_departments: (this.form.distribution_departments || []).slice(),
      }
    },
    // Discard on the Every page: the form goes back to what was last saved
    discardDistribution() {
      const saved = this.savedForms.distribution
      if (!saved) return
      this.form.fi_user_ids = saved.fi_user_ids.slice()
      this.form.email_only = saved.email_only.slice()
      this.form.distribution_departments = saved.distribution_departments.slice()
      this.distributionError = ''
    },
    // FI-2201 (#1815) the General tab's three fields, and their snapshot
    generalFormCopy() {
      return {
        duplicate_check_enabled: !!this.form.duplicate_check_enabled,
        status_change_emails_enabled: !!this.form.status_change_emails_enabled,
        email_subject_format: this.form.email_subject_format || 'default',
      }
    },
    generalSnapshotOf(f) {
      return JSON.stringify({
        dup: !!f.duplicate_check_enabled,
        sce: !!f.status_change_emails_enabled,
        esf: f.email_subject_format || 'default',
      })
    },

    clearSaveErrors() {
      this.accessError = ''
      this.generalError = ''
      this.distributionError = ''
      this.categoriesError = ''
      this.departmentsError = ''
      this.huddleError = ''
      this.importError = ''
      this.importRunError = ''
    },

    // tenant user roster -> typeahead candidates. cached once per page
    // load. errors are soft: the field falls back to free-form entry so
    // a transient user-list failure doesn't block saving distribution.
    async loadTenantUsers() {
      if (!this.tenant || this.tenantUsersLoading) return
      this.tenantUsersLoading = true
      try {
        const res = await get_users(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        const results = Array.isArray(data.results) ? data.results : []
        this.tenantUserCandidates = results
        this.tenantUsersLoaded = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings loadTenantUsers failed', e)
        this.tenantUsersLoaded = true
      } finally {
        this.tenantUsersLoading = false
      }
    },

    // the saved rows and a fresh draft of them. an answer for a tenant
    // switched away from is dropped
    async loadHuddleSubscribers() {
      const tenant = this.tenant
      let rows = []
      try {
        const res = await this.fetchHuddleSubscribers()
        rows = ((res && res.value && res.value.data) || {}).subscribers || []
      } catch (e) {
        // non-admins cannot list; the tab's admin gate keeps them out anyway
        rows = []
      }
      if (this.tenant !== tenant) return
      this.adoptHuddleSubscribers(rows)
    },
    adoptHuddleSubscribers(rows) {
      this.huddleSubscribers = rows
      this.huddleSubsDraft = rows.map(s => ({ ...s }))
    },
    // the huddle recipients' network seams, so a spec can stand in for them
    fetchHuddleSubscribers() {
      return get_huddle_subscribers(this.tenant)
    },
    patchHuddleSubscribers(payload) {
      return update_huddle_subscribers(this.tenant, payload)
    },
    // FI-2201 (#1815) Add and the row's remove edit the draft; the Huddle
    // Save sends them. an id already on the list is not added twice, and one
    // put back after a remove is its saved row again
    addHuddleSubscriber() {
      if (this.savingHuddle) return
      const id = (this.huddleSubAdd || '').trim()
      if (!id) return
      this.huddleSubAdd = ''
      const same = s => String(s.user_id || '').toLowerCase() === id.toLowerCase()
      if (this.huddleSubsDraft.some(same)) return
      const saved = this.huddleSubscribers.find(same)
      this.huddleSubsDraft = this.huddleSubsDraft.concat([saved ? { ...saved } : { user_id: id }])
    },
    removeHuddleSubscriber(s) {
      if (this.savingHuddle) return
      this.huddleSubsDraft = this.huddleSubsDraft.filter(r => r.user_id !== s.user_id)
    },

    readableLabel(v) {
      if (!v) return ''
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    // ---- FI-1944 (#1816) category names ----
    categoryLabel(slug) {
      return opsCategoryLabel(slug, this.categoryLabels)
    },
    isCustomCategory(slug) {
      return isCustomCategory(slug)
    },
    // a built-in with a name of the fleet's own (the row's Renamed tag)
    isRenamedCategory(slug) {
      return !isCustomCategory(slug) && !!(this.labelOverrides || {})[slug]
    },
    // the renamed built-ins off a categories row, as a fresh object. a row
    // without the map has none (a Reset may have cleared the last one), so
    // an old map never lingers past the row it came from
    labelOverridesFrom(categories) {
      const raw = categories && categories.label_overrides
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) return { ...raw }
      return {}
    },
    // the client half of the server's name rules, one sentence per rule and
    // the same sentences the server's 400 carries (the server has the last
    // word). own is the category being renamed ({kind, slug}); an add
    // passes its group only, so an off built-in can point at its switch
    categoryNameError(raw, own) {
      const name = cleanCategoryName(raw)
      if (!name) return 'Enter a name for the category.'
      if (name.length > CATEGORY_NAME_MAX) return 'Keep the name to 40 characters or fewer.'
      if (!/[a-z0-9À-ɏ]/i.test(name)) return 'Use at least one letter or number in the name.'
      const ownSlug = own && own.slug
      const lower = name.toLowerCase()
      const groups = [
        { kind: 'incident', cats: this.incidentCategories || [] },
        { kind: 'kudos', cats: this.kudosCategories || [] },
        { kind: 'note', cats: this.noteCategories || [] },
      ]
      const others = []
      groups.forEach(g => g.cats.forEach(slug => {
        if (slug !== ownSlug && !others.some(o => o.slug === slug)) others.push({ slug, kind: g.kind })
      }))
      // a built-in's key counts as its name while it keeps its original one,
      // so 'Driver-Error' is taken like 'Driver Error' (the server's rule)
      const wantedSlug = categoryNameSlug(name)
      const taken = others.find(o => this.categoryLabel(o.slug).toLowerCase() === lower
        || (!isCustomCategory(o.slug) && !this.isRenamedCategory(o.slug) && wantedSlug === o.slug))
      if (taken) {
        const current = this.categoryLabel(taken.slug)
        // on as the list shows it, unsaved switches included (FI-2201)
        const on = groups.some(g => g.cats.includes(taken.slug) && this.rowChecked(g.kind, taken.slug))
        if (!ownSlug && !isCustomCategory(taken.slug) && !on) {
          return current + ' is already a category. Turn it on in the list instead.'
        }
        return 'There is already a category called ' + current + '.'
      }
      // a built-in keeps its original name even once renamed, so a public
      // reporter who picks that name still lands in that category
      const original = others.find(o => !isCustomCategory(o.slug)
        && (opsCategoryLabel(o.slug).toLowerCase() === lower || wantedSlug === o.slug))
      if (original) return opsCategoryLabel(original.slug) + ' is the original name of another category. Pick another name.'
      return ''
    },

    // FI-2173 the Notes row off the settings payload: absent = on, [] = off.
    // never `|| []` on this key, or an api that predates it would read as
    // notes off and the next save would turn them off for real
    enabledNoteFrom(categories) {
      const raw = categories && categories.enabled_note
      return Array.isArray(raw) ? raw.map(String) : this.noteCategories.slice()
    },

    // ---- FI-1936 per-category recipients (Categories list and pages) -----
    // server rows -> editable rows. every list is a fresh array so the
    // pickers never mutate what the response object held.
    hydrateRoutes(list) {
      return (Array.isArray(list) ? list : [])
        .filter(r => r && typeof r === 'object')
        .map(r => ({
          category: String(r.category || ''),
          departments: (r.departments || []).map(String),
          extra_emails: (r.extra_emails || []).map(String),
        }))
    },
    // editable rows -> PATCH payload. blank rows (no category) are omitted;
    // a row with a category and no targets is sent as-is, the Save gate
    // (routesInvalid) is what keeps that from happening from here.
    routePayloadRows(rows) {
      return (rows || this.form.category_routes || [])
        .filter(r => !!String((r && r.category) || '').trim())
        .map(r => ({
          category: String(r.category).trim().toLowerCase(),
          departments: (r.departments || []).slice(),
          extra_emails: (r.extra_emails || []).slice(),
        }))
    },
    // the plain sentence under a routed category's name: who it notifies
    // instead of the default recipients. one outside address is named; more
    // than one is counted, since the addresses are on the page.
    routeSummary(cat) {
      const r = this.routesBySlug[cat]
      if (!r) return ''
      const parts = (r.departments || []).slice()
      const emails = r.extra_emails || []
      if (emails.length === 1) parts.push(emails[0])
      else if (emails.length > 1) parts.push(emails.length + ' outside addresses')
      const who = parts.length ? this.joinNames(parts) : 'no one'
      return 'Notifies ' + who + ' instead of the default recipients.'
    },
    // "A", "A and B", "A, B and C"
    joinNames(names) {
      if (names.length < 2) return names.join('')
      return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
    },
    // the Recipients cell of a routed category; null when it inherits
    routeChips(cat) {
      return this.routeChipsBySlug[cat] || null
    },
    // the kudos Other row has no sentence: its route belongs to the issue
    // Other row (single-owner rule), and the cell says so
    rowSummary(kind, cat) {
      if (kind === 'kudos' && cat === 'other') return ''
      return this.routeSummary(cat)
    },
    // dom ids for the bits that describe a row's switch
    rowId(kind, cat, part) {
      return 'od-set-cat-' + kind + '-' + cat + '-' + part
    },
    rowDescribedBy(kind, cat) {
      const ids = []
      if (this.categoryUsage[cat]) ids.push(this.rowId(kind, cat, 'count'))
      if (this.rowSummary(kind, cat)) ids.push(this.rowId(kind, cat, 'sub'))
      return ids.join(' ')
    },

    // hover copy for the per-category usage pill: says what the number is
    // and what a flip does to those events. the note pill says notes, since
    // a note has no category (FI-2173)
    usageTitle(cat) {
      const n = this.categoryUsage[cat] || 0
      if (cat === 'note') return this.noteArchiveSentence(n)
      const noun = n === 1 ? 'event uses' : 'events use'
      const them = n === 1 ? 'it' : 'them'
      return n + ' ' + noun + ' this category. Turning it off archives ' + them + ' until it is turned back on.'
    },
    // the note pill's title and the note page's archive note share one
    // sentence; with nothing filed yet there is no count to name
    noteArchiveSentence(n) {
      if (!n) return 'Turning notes off archives any notes until notes are turned back on.'
      if (n === 1) return '1 event is a note. Turning notes off archives it until notes are turned back on.'
      return n + ' events are notes. Turning notes off archives them until notes are turned back on.'
    },

    formatRelative(ts) {
      if (!ts) return ''
      return moment(ts).fromNow()
    },

    emailValidator(tag) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((tag || '').trim())
    },

    // FI-1938: the encoder for the current url. type 0 picks the smallest
    // version that fits; error level M survives a scuffed print. the same
    // modules feed the on-screen canvas and the download, so both scan alike.
    buildQr() {
      const qr = qrcodeGenerator(0, 'M')
      qr.addData(this.qrUrl)
      qr.make()
      return qr
    },
    // paint the modules onto a canvas at roughly `size` css px with a quiet
    // zone of `quiet` modules. cells are whole device pixels so the edges
    // stay crisp on retina screens; the css size is derived from that.
    paintQr(canvas, size, quiet, scale) {
      const qr = this.buildQr()
      const count = qr.getModuleCount()
      const total = count + quiet * 2
      const cell = Math.max(1, Math.floor((size * scale) / total))
      const px = cell * total
      canvas.width = px
      canvas.height = px
      canvas.style.width = `${px / scale}px`
      canvas.style.height = `${px / scale}px`
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, px, px)
      ctx.fillStyle = '#000'
      const offset = cell * quiet
      for (let r = 0; r < count; r++) {
        for (let c = 0; c < count; c++) {
          if (qr.isDark(r, c)) ctx.fillRect(offset + c * cell, offset + r * cell, cell, cell)
        }
      }
    },
    renderQr() {
      const canvas = this.$refs.qrCanvas
      if (!canvas || !this.qrUrl) return
      try {
        this.paintQr(canvas, 152, 2, window.devicePixelRatio || 1)
        this.qrError = ''
        this.qrDrawnFor = this.qrUrl
      } catch (e) {
        this.qrError = 'The QR code could not be drawn.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings renderQr failed', e)
      }
    },
    // a print-worthy png regardless of the on-screen size: 1024px keeps
    // the modules crisp on a letter-size sign, and the four-module quiet
    // zone is what scanners expect around a printed code
    downloadQr() {
      if (!this.qrUrl) return
      this.qrDownloading = true
      this.qrError = ''
      try {
        const off = document.createElement('canvas')
        this.paintQr(off, 1024, 4, 1)
        const a = document.createElement('a')
        a.href = off.toDataURL('image/png')
        a.download = `erm-form-${this.savedSlug || 'qr'}.png`
        document.body.appendChild(a)
        a.click()
        a.remove()
      } catch (e) {
        this.qrError = 'The QR code could not be saved.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings downloadQr failed', e)
      } finally {
        this.qrDownloading = false
      }
    },
    async copyPublicUrl() {
      const url = this.publicUrlPreview
      if (!url) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          // legacy fallback for older safari that gates clipboard api on a user
          // gesture only — execCommand still works inside a click handler.
          const ta = document.createElement('textarea')
          ta.value = url
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedAt = Date.now()
        setTimeout(() => { this.copiedAt = null }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings copyPublicUrl failed', e)
      }
    },

    async runSlugCheck(slug) {
      if (!slug) return
      const seq = ++this.slugCheckSeq
      this.slugChecking = true
      try {
        const res = await check_ops_desk_slug(this.tenant, slug)
        // drop responses that were superseded by a newer keystroke.
        if (seq !== this.slugCheckSeq) return
        const data = (res && res.value && res.value.data) || {}
        this.slugCheck = {
          slug: data.slug || slug,
          available: !!data.available,
          reason: data.reason || '',
          previewShortUrl: data.preview_short_url || '',
        }
      } catch (e) {
        if (seq !== this.slugCheckSeq) return
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings slug check failed', e)
        this.slugCheck = { slug, available: null, reason: '', previewShortUrl: '' }
      } finally {
        if (seq === this.slugCheckSeq) this.slugChecking = false
      }
    },

    // FI-1818 access toggle, saved by the Public form tab's Save (FI-2201,
    // #1815: no longer on flip). baselines what it sent, so a flip made
    // while the PATCH is out stays dirty
    patchAccess(enabled) {
      return update_ops_desk_access(this.tenant, enabled)
    },
    async saveAccess() {
      // an answer for a tenant switched away from is dropped, like saveGeneral
      const tenant = this.tenant
      const enabled = !!this.form.public_form_enabled
      this.savingAccess = true
      this.accessError = ''
      try {
        await this.patchAccess(enabled)
        if (this.tenant !== tenant) return
        this.savedSnapshots.access = JSON.stringify({ on: enabled })
        this.accessSavedAt = new Date()
      } catch (e) {
        if (this.tenant !== tenant) return
        this.accessSavedAt = null
        this.accessError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveAccess failed', e)
      } finally {
        this.savingAccess = false
      }
    },

    async saveSlug() {
      this.slugError = ''
      this.savingSlug = true
      try {
        const cleaned = (this.form.slug || '').trim().toLowerCase()
        const res = await update_ops_desk_slug(this.tenant, cleaned)
        const data = (res && res.value && res.value.data) || {}
        // server returns the freshly generated short URL — adopt it
        // immediately so the Copy / Open affordances point at the new
        // slug without waiting for a settings reload.
        if (typeof data.public_short_url === 'string') {
          this.publicShortUrl = data.public_short_url
        }
        this.savedSlug = cleaned
        // refresh the check state so the input drops out of "draft" mode
        // and the URL preview hint disappears.
        this.slugCheck = {
          slug: cleaned,
          available: true,
          reason: 'owned_by_self',
          previewShortUrl: this.publicShortUrl,
        }
        this.slugSavedAt = new Date()
      } catch (e) {
        // backend returns a 400 with the message in error_data.error_message
        // when the slug is invalid or already in use; surface verbatim.
        const msg = e && e.response && e.response.data
          && e.response.data.error_data
          && (e.response.data.error_data.error_message || e.response.data.error_data.message)
        this.slugError = msg || 'Could not save slug.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveSlug failed', e)
      } finally {
        this.savingSlug = false
      }
    },

    // the network seams of the Every page's save and the General save, so a
    // spec can stand in for them
    patchDistribution(payload) {
      return update_ops_desk_distribution(this.tenant, payload)
    },
    patchNotificationSettings(patch) {
      return update_ops_desk_notification_settings(this.tenant, patch)
    },
    patchGeneralSettings(patch) {
      return update_ops_desk_general_settings(this.tenant, patch)
    },
    // the snapshot shape of a form-shaped distribution copy
    distributionSnapshotOf(f) {
      return JSON.stringify({
        fi: f.fi_user_ids.slice().sort(),
        em: f.email_only.slice().sort(),
        dep: f.distribution_departments.slice().sort(),
      })
    },
    async saveDistribution() {
      this.savingDistribution = true
      this.distributionError = ''
      // what this call sends is what it baselines, never the live form:
      // the pickers stay open while the PATCH is out, and an edit made
      // in the meantime has to come out the other side still dirty
      const sent = this.distributionFormCopy()
      try {
        await this.patchDistribution({
          fi_user_ids: sent.fi_user_ids,
          email_only: sent.email_only,
          departments: sent.distribution_departments,
        })
        this.distributionSavedAt = new Date()
        this.savedForms.distribution = sent
        this.savedSnapshots.distribution = this.distributionSnapshotOf(sent)
      } catch (e) {
        this.distributionSavedAt = null
        this.distributionError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveDistribution failed', e)
      } finally {
        this.savingDistribution = false
      }
    },

    // FI-2201 (#1815) the General Save. the two email rules keep their route
    // (settings/notifications) and go first, then the duplicate check
    // (settings/general); each call sends only its keys that changed. what a
    // call sent is baselined as soon as it lands, so a failed second call
    // leaves only its own key dirty, and an edit made while a call is out
    // stays dirty. an answer for a tenant switched away from is dropped
    async saveGeneral() {
      const tenant = this.tenant
      const sent = this.generalFormCopy()
      const saved = this.savedForms.general || sent
      const notify = {}
      if (sent.status_change_emails_enabled !== saved.status_change_emails_enabled) {
        notify.status_change_emails_enabled = sent.status_change_emails_enabled
      }
      if (sent.email_subject_format !== saved.email_subject_format) {
        notify.email_subject_format = sent.email_subject_format
      }
      const general = {}
      if (sent.duplicate_check_enabled !== saved.duplicate_check_enabled) {
        general.duplicate_check_enabled = sent.duplicate_check_enabled
      }
      this.savingGeneral = true
      this.generalError = ''
      try {
        if (Object.keys(notify).length) {
          await this.patchNotificationSettings(notify)
          if (this.tenant !== tenant) return
          const next = { ...this.savedForms.general, ...notify }
          this.savedForms.general = next
          this.savedSnapshots.general = this.generalSnapshotOf(next)
        }
        if (Object.keys(general).length) {
          await this.patchGeneralSettings(general)
          if (this.tenant !== tenant) return
        }
        this.savedForms.general = sent
        this.savedSnapshots.general = this.generalSnapshotOf(sent)
        this.generalSavedAt = new Date()
      } catch (e) {
        if (this.tenant !== tenant) return
        this.generalSavedAt = null
        this.generalError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveGeneral failed', e)
      } finally {
        this.savingGeneral = false
      }
    },

    async saveCategories() {
      this.savingCategories = true
      this.categoriesError = ''
      try {
        const res = await update_ops_desk_categories(this.tenant, {
          enabled_incident: this.form.enabled_incident,
          enabled_kudos: this.form.enabled_kudos,
          enabled_note: this.form.enabled_note,
          // FI-1936: always sent, since an absent key means "keep" on the
          // server and an emptied list has to actually clear.
          category_routes: this.routePayloadRows(),
        })
        // re-hydrate from the response so anything the server dropped
        // (unknown slug, duplicate category) disappears instead of
        // lingering as a phantom edit.
        const cats = res && res.value && res.value.data && res.value.data.categories
        if (cats && Array.isArray(cats.category_routes)) {
          this.form.category_routes = this.hydrateRoutes(cats.category_routes)
        }
        this.categoriesSavedAt = new Date()
        this.savedSnapshots.categories = this.categoriesSnapshot
      } catch (e) {
        this.categoriesSavedAt = null
        this.categoriesError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveCategories failed', e)
      } finally {
        this.savingCategories = false
      }
    },

    // ---- FI-2166 categories list: the enabled lists ------------------------
    // FI-2201 (#1815) the switches, All / None and Restore defaults edit
    // listEdits, a draft over the saved lists in form; the list's Save sends
    // the draft in one PATCH and commits into form only once it has
    // succeeded, so a failed save leaves the form exactly as it was and the
    // draft still waiting.
    isEnabled(kind, slug) {
      const list = this.form[LIST_KEY[kind] || LIST_KEY.incident]
      return (list || []).includes(slug)
    },
    // what the row's switch shows: the draft's value for a row the admin
    // changed, the saved value otherwise
    rowChecked(kind, slug) {
      const edits = this.listEdits || {}
      const key = kind + ':' + slug
      return Object.prototype.hasOwnProperty.call(edits, key) ? !!edits[key] : this.isEnabled(kind, slug)
    },
    enabledListsCopy() {
      return {
        enabled_incident: (this.form.enabled_incident || []).slice(),
        enabled_kudos: (this.form.enabled_kudos || []).slice(),
        enabled_note: (Array.isArray(this.form.enabled_note) ? this.form.enabled_note : []).slice(),
      }
    },
    // pure: the enabled lists with one slug added or removed
    toggleEnabled(kind, slug, on) {
      const lists = this.enabledListsCopy()
      const key = LIST_KEY[kind] || LIST_KEY.incident
      lists[key] = withSlug(this[AVAIL_KEY[kind] || AVAIL_KEY.incident], lists[key], slug, on)
      return lists
    },
    // pure: the enabled lists with every row edit applied
    listsWithEdits(edits) {
      const lists = this.enabledListsCopy()
      Object.keys(edits || {}).forEach(k => {
        const at = k.indexOf(':')
        const kind = k.slice(0, at)
        if (!LIST_KEY[kind]) return
        lists[LIST_KEY[kind]] = withSlug(this[AVAIL_KEY[kind]], lists[LIST_KEY[kind]], k.slice(at + 1), !!edits[k])
      })
      return lists
    },
    // the three lists as the dirty check compares them: order is not an edit
    enabledListsSnapshotOf(lists) {
      const sorted = v => (Array.isArray(v) ? v : []).slice().sort()
      return JSON.stringify({
        inc: sorted(lists.enabled_incident),
        kud: sorted(lists.enabled_kudos),
        note: sorted(lists.enabled_note),
      })
    },
    // the list side's PATCH body: the three enabled lists and nothing else.
    // category_routes is deliberately absent, since the server keeps the
    // routes when the key is missing and the list never edits them.
    // enabled_note rides only when the lists carry it: on the server an
    // absent key keeps notes as they are and [] turns them off, so a caller
    // that never built the list must not send an empty one
    enabledListsPayload(lists) {
      const src = lists || this.enabledListsCopy()
      const out = {
        enabled_incident: (src.enabled_incident || []).slice(),
        enabled_kudos: (src.enabled_kudos || []).slice(),
      }
      if (Array.isArray(src.enabled_note)) out.enabled_note = src.enabled_note.slice()
      return out
    },
    // the one network seam for the list side, so a spec can stand in for it
    patchCategories(payload) {
      return update_ops_desk_categories(this.tenant, payload)
    },
    // the list's Save: the draft in one PATCH. what was sent is what gets
    // cleared, so an edit made while the call is out stays in the draft.
    // resolves true on success.
    async saveEnabledLists() {
      if (this.categoriesBusy || !this.listDirty) return false
      const sent = { ...this.listEdits }
      const lists = this.listsWithEdits(sent)
      this.savingList = true
      this.categoriesError = ''
      // a staff tenant switch mid-flight reloads form for the new tenant;
      // this call's answer belongs to the old one and must not land in it
      const tenant = this.tenant
      try {
        const res = await this.patchCategories(this.enabledListsPayload(lists))
        if (this.tenant !== tenant) return false
        // commit what the server kept: a slug it filtered out disappears
        // here instead of lingering as a phantom edit, and the routes
        // re-hydrate as saveCategories does
        this.adoptCategoriesResponse(res && res.value && res.value.data, lists)
        const left = { ...this.listEdits }
        Object.keys(sent).forEach(k => { if (left[k] === sent[k]) delete left[k] })
        this.listEdits = left
        this.categoriesSavedAt = new Date()
        this.savedSnapshots.categories = this.categoriesSnapshot
        return true
      } catch (e) {
        if (this.tenant !== tenant) return false
        // form was never touched, so there is nothing to roll back
        this.categoriesSavedAt = null
        this.categoriesError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveEnabledLists failed', e)
        return false
      } finally {
        this.savingList = false
      }
    },
    // Discard: the switches go back to what is saved
    discardList() {
      if (this.categoriesBusy) return
      this.listEdits = {}
      this.categoriesError = ''
    },
    // rows set on or off in the draft. a row set to its saved value carries
    // no edit, so putting a switch back leaves the list clean
    setRowEdits(kind, slugs, on) {
      const edits = { ...this.listEdits }
      slugs.forEach(slug => {
        const key = kind + ':' + slug
        if (!!on === this.isEnabled(kind, slug)) delete edits[key]
        else edits[key] = !!on
      })
      this.listEdits = edits
    },
    // a row's switch: an explicit handler rather than a watcher on the
    // arrays, since All / None change whole groups
    onRowToggle(kind, slug, on) {
      if (this.categoriesBusy) return
      this.setRowEdits(kind, [slug], on)
    },
    // All needs no confirm: it widens.
    // FI-2162 / #1740: skip never-offered slugs here
    selectAllCategories(kind) {
      if (this.categoriesBusy) return
      this.setRowEdits(kind, this[AVAIL_KEY[kind] || AVAIL_KEY.incident] || [], true)
    },
    // None archives a whole group's events once saved, so it confirms first
    // (the Remove-photo idiom from ops_desk.vue)
    async clearCategories(kind) {
      if (this.categoriesBusy) return
      const ok = await this.$bvModal.msgBoxConfirm(
        'Their events will be archived until you turn a category back on.',
        {
          title: kind === 'kudos' ? 'Turn off every kudos category?' : 'Turn off every issue category?',
          okTitle: 'Turn off', okVariant: 'danger', cancelTitle: 'Keep', centered: true,
        },
      ).catch(() => false)
      if (!ok || this.categoriesBusy) return
      this.setRowEdits(kind, this[AVAIL_KEY[kind] || AVAIL_KEY.incident] || [], false)
    },
    // the enabled sets only; FI-1936 routes are left alone on purpose, and so
    // are FI-1944 names (a page's Reset is the way back to an original name).
    // the default today is every available slug on, notes included (the
    // server's no-row default); #1740's opt-in default is a later change.
    async restoreDefaultCategories() {
      if (this.categoriesBusy) return
      const ok = await this.$bvModal.msgBoxConfirm(
        'Every issue and kudos category is turned on, notes are turned on, and any archived events come back when you save. Names and recipients are not changed.',
        { title: 'Restore the default categories?', okTitle: 'Restore', okVariant: 'danger', cancelTitle: 'Keep', centered: true },
      ).catch(() => false)
      if (!ok || this.categoriesBusy) return
      Object.keys(LIST_KEY).forEach(kind => {
        this.setRowEdits(kind, this[AVAIL_KEY[kind]] || [], true)
      })
    },

    // ---- FI-1944 (#1816) the categories answers and a fleet's own categories ----
    // every categories call (the PATCH, add, remove) answers with the row,
    // and an api with custom categories adds the available lists: commit
    // what came back. sent is what the call asked for, the fallback for an
    // enabled list or the routes an older answer leaves out
    adoptCategoriesResponse(data, sent) {
      const d = data || {}
      const was = sent || {}
      const lists = [
        ['incidentCategories', 'available_incident_categories'],
        ['kudosCategories', 'available_kudos_categories'],
        ['noteCategories', 'available_note_categories'],
      ]
      lists.forEach(([mine, key]) => {
        if (Array.isArray(d[key])) this[mine] = d[key].map(String)
      })
      const hasRow = !!d.categories && typeof d.categories === 'object'
      const cats = hasRow ? d.categories : {}
      if (Array.isArray(cats.enabled_incident)) this.form.enabled_incident = cats.enabled_incident.map(String)
      else if (Array.isArray(was.enabled_incident)) this.form.enabled_incident = was.enabled_incident.slice()
      if (Array.isArray(cats.enabled_kudos)) this.form.enabled_kudos = cats.enabled_kudos.map(String)
      else if (Array.isArray(was.enabled_kudos)) this.form.enabled_kudos = was.enabled_kudos.slice()
      // FI-2173 notes commit from the response only: an api that predates
      // the key never stored the flip, so the switch snaps back rather
      // than showing an off the server does not hold
      if (Array.isArray(cats.enabled_note)) this.form.enabled_note = cats.enabled_note.map(String)
      if (Array.isArray(cats.category_routes)) this.form.category_routes = this.hydrateRoutes(cats.category_routes)
      else if (Array.isArray(was.category_routes)) this.form.category_routes = this.hydrateRoutes(was.category_routes)
      if (Array.isArray(cats.custom_categories)) {
        this.customCategories = cats.custom_categories.slice()
        this.labelOverrides = this.labelOverridesFrom(cats)
      } else if (hasRow && cats.label_overrides) {
        this.labelOverrides = this.labelOverridesFrom(cats)
      }
    },
    // the two network seams of the Add row and the Remove block, so a spec
    // can stand in for them
    postCustomCategory(payload) {
      return add_ops_desk_custom_category(this.tenant, payload)
    },
    deleteCustomCategory(key) {
      return remove_ops_desk_custom_category(this.tenant, key)
    },
    focusAddInput(kind) {
      let el = this.$refs && this.$refs['addInput-' + kind]
      if (Array.isArray(el)) el = el[0]
      if (el && el.focus) el.focus()
    },
    // the Add row: the new category comes back on, before Other in its
    // group, and focus stays in the box for the next one. the server's
    // refusal (a 400) shows under the group; the client checks the same
    // rules first so most never leave the page
    async addCustomCategory(kind) {
      if (this.categoriesBusy || this.customAtLimit) return false
      const label = cleanCategoryName(this.customDraft[kind])
      if (!label) return false
      const refusal = this.categoryNameError(label, { kind })
      this.customError[kind] = refusal
      if (refusal) return false
      this.savingCustom = true
      this.categoriesError = ''
      // same tenant stamp as saveEnabledLists: a reload mid-flight owns form
      const tenant = this.tenant
      let added = false
      try {
        const res = await this.postCustomCategory({ label, entry_type: kind })
        if (this.tenant !== tenant) return false
        this.adoptCategoriesResponse(res && res.value && res.value.data)
        // the box stays live while the POST is out: a name typed in the
        // meantime is the next one, so only the one sent is cleared
        if (cleanCategoryName(this.customDraft[kind]) === label) this.customDraft[kind] = ''
        this.categoriesSavedAt = new Date()
        this.savedSnapshots.categories = this.categoriesSnapshot
        added = true
        return true
      } catch (e) {
        if (this.tenant !== tenant) return false
        this.customError[kind] = apiRefusal(e) || 'Did not add. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings addCustomCategory failed', e)
        return false
      } finally {
        this.savingCustom = false
        if (added) this.$nextTick(() => this.focusAddInput(kind))
      }
    },
    // the custom page's Remove, offered only while no event uses it. the
    // category leaves the list with its page, so a pending draft goes too
    async removeCustomCategory() {
      if (!this.isCustomPage || this.categoriesBusy || this.pageBusy) return false
      const { slug } = this.catPage
      const ok = await this.$bvModal.msgBoxConfirm(
        'It comes off the list and out of every form. No events use it, so nothing is archived.',
        { title: 'Remove ' + this.pageLabel + '?', okTitle: 'Remove', okVariant: 'danger', cancelTitle: 'Keep', centered: true },
      ).catch(() => false)
      if (!ok || !this.catPage || this.catPage.slug !== slug) return false
      this.savingCustom = true
      this.customRemoveError = ''
      this.categoriesError = ''
      const tenant = this.tenant
      let removed = false
      try {
        const res = await this.deleteCustomCategory(slug)
        if (this.tenant !== tenant) return false
        this.adoptCategoriesResponse(res && res.value && res.value.data)
        this.categoriesSavedAt = new Date()
        this.savedSnapshots.categories = this.categoriesSnapshot
        removed = true
      } catch (e) {
        if (this.tenant !== tenant) return false
        this.customRemoveError = apiRefusal(e) || 'Did not remove. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings removeCustomCategory failed', e)
      } finally {
        this.savingCustom = false
      }
      if (!removed) return false
      // Back asks about a dirty draft; this one belongs to a category that is
      // gone, so it is discarded first and the list gets focus back
      if (this.catPage && this.catPage.slug === slug) {
        this.discardPage()
        this.backToList()
      }
      return true
    },

    // ---- FI-2166 pages: a category's page and the default recipients page ----
    // the one scroll container: the modal body under the modal host
    // (erm_settings_modal.vue .od-settings-modal), the document under the
    // page host, where the settings shell scrolls the window. the callers
    // (stashListScroll, focusPage, backToList) only read and write
    // scrollTop, which both answer alike.
    modalBody() {
      if (this.page) return document.scrollingElement || document.documentElement
      const el = this.$el
      return el && el.closest ? el.closest('.modal-body') : null
    },
    stashListScroll() {
      const body = this.modalBody()
      this.listScrollTop = body ? body.scrollTop : 0
    },
    resetPageState() {
      this.catPage = null
      this.pageDraft = null
      this.pageBaseline = ''
      this.pageError = ''
      this.pageNameRefusal = null
      this.pageSavedAt = null
      this.listScrollTop = 0
      this.customRemoveError = ''
    },
    // the draft is copies, never the reactive route row, so editing it
    // leaves form untouched until Save commits. the kudos Other page has no
    // route of its own (single-owner rule), so its draft is never routed.
    seedPageDraft(kind, slug) {
      const own = !(kind === 'kudos' && slug === 'other')
      const r = own ? this.routesBySlug[slug] : null
      const draft = {
        enabled: this.isEnabled(kind, slug),
        routed: !!r,
        departments: r ? (r.departments || []).slice() : [],
        extra_emails: r ? (r.extra_emails || []).slice() : [],
      }
      // FI-1944 the Name field starts on the category's name as it stands;
      // Other and notes have none
      if (hasEditableName(kind, slug)) draft.label = opsCategoryLabel(slug, this.categoryLabels)
      return draft
    },
    // after open: the page starts at the top and focus lands on Back (the
    // page itself on a phone drill-in, where Back is in the host's header)
    focusPage() {
      const body = this.modalBody()
      if (body) body.scrollTop = 0
      const back = this.$refs.pageBack
      if (back && back.focus) {
        back.focus({ preventScroll: true })
        return
      }
      const pageEl = this.$el && this.$el.querySelector ? this.$el.querySelector('.od-set-page') : null
      if (pageEl) {
        pageEl.setAttribute('tabindex', '-1')
        pageEl.focus({ preventScroll: true })
      }
    },
    // FI-2201 (#1815) a category page seeds from the saved lists and saves
    // its own row, so the list's unsaved switches cannot ride along under
    // it: opening one from a dirty list asks first, like leaving a dirty
    // page, and yes drops them. a clean list drops edits a save underneath
    // made no-ops, so none comes back after the page's own save. the
    // default recipients page never touches the lists, so it keeps the
    // draft (openEveryPage does not ask). true when the page may open
    leaveListDraft() {
      if (this.catPage && this.catPage.kind !== 'every') return true
      if (!this.listDirty) {
        this.listEdits = {}
        return true
      }
      if (!this.confirmDiscard()) return false
      this.discardList()
      return true
    },
    openCategoryPage(kind, slug) {
      if (this.categoriesBusy) return
      if (!this.leaveListDraft()) return
      // opened from the list: remember where the list was. opened from
      // another page (kudos Other -> issue Other) the stash already holds it.
      if (!this.catPage) this.stashListScroll()
      this.pageDraft = this.seedPageDraft(kind, slug)
      this.pageBaseline = this.pageSnapshot
      this.pageError = ''
      this.pageNameRefusal = null
      this.pageSavedAt = null
      this.customRemoveError = ''
      if (this.phoneDrill) this.drillTo('fwd')
      this.catPage = { kind, slug }
      this.$nextTick(this.focusPage)
    },
    openEveryPage() {
      if (this.categoriesBusy) return
      if (!this.catPage) this.stashListScroll()
      this.pageDraft = null
      this.pageBaseline = ''
      this.pageError = ''
      if (this.phoneDrill) this.drillTo('fwd')
      this.catPage = { kind: 'every' }
      this.$nextTick(this.focusPage)
    },
    // the kudos Other page's pointer at the row that owns the route; the
    // only thing dirty there can be the switch, and that asks
    openIssueOtherPage() {
      if (this.categoriesBusy) return
      if (this.pageDirty && !this.confirmDiscard()) return
      this.discardPage()
      this.openCategoryPage('incident', 'other')
    },
    // a pick marks the draft routed; emptying both pickers un-routes it only
    // when the saved state was unrouted, so a saved route cannot be silenced
    // by hand (pageInvalid says so instead)
    syncPageRouted() {
      const d = this.pageDraft
      if (!d) return
      const has = (d.departments || []).length > 0 || (d.extra_emails || []).length > 0
      if (has) d.routed = true
      else if (!this.pageBaselineParsed.routed) d.routed = false
    },
    usePageDistributionList() {
      const d = this.pageDraft
      if (!d) return
      d.routed = false
      d.departments = []
      d.extra_emails = []
    },
    // FI-1944 the Name's Reset: a built-in goes back to its original name
    // on Save
    resetPageName() {
      if (!this.pageHasName || this.isCustomPage) return
      this.pageDraft.label = this.pageOriginalName
    },
    // pure: the enabled lists and route rows as they would be with the draft
    // applied. the slug's row is upserted while the draft is routed (even
    // target-less, so a saved target-less route survives a switch flip) and
    // removed when it is not; the kudos Other page never touches a row.
    applyPageDraft(kind, slug, draft) {
      const lists = this.toggleEnabled(kind, slug, !!draft.enabled)
      const routes = this.hydrateRoutes(this.form.category_routes)
      if (!(kind === 'kudos' && slug === 'other')) {
        const at = routes.findIndex(r => String(r.category || '').trim().toLowerCase() === slug)
        const row = {
          category: slug,
          departments: (draft.departments || []).slice(),
          extra_emails: (draft.extra_emails || []).slice(),
        }
        if (draft.routed) {
          if (at >= 0) routes.splice(at, 1, row)
          else routes.push(row)
        } else if (at >= 0) {
          routes.splice(at, 1)
        }
      }
      return {
        enabled_incident: lists.enabled_incident,
        enabled_kudos: lists.enabled_kudos,
        enabled_note: lists.enabled_note,
        category_routes: routes,
      }
    },
    // one categories PATCH carrying the three enabled lists and the routes.
    // form is committed from the response only on success; on failure the
    // draft stays dirty and form is exactly as it was.
    async savePage() {
      if (!this.catPage) return false
      if (this.catPage.kind === 'every') {
        if (!this.pageCanSave) return false
        await this.saveDistribution()
        return !this.distributionError
      }
      if (!this.pageCanSave) return false
      const { kind, slug } = this.catPage
      const draft = this.pageDraft
      const sentSnap = this.pageSnapshot
      const next = this.applyPageDraft(kind, slug, draft)
      this.pageSaving = true
      this.pageError = ''
      this.categoriesError = ''
      // same tenant stamp as saveEnabledLists: a reload mid-flight owns form
      const tenant = this.tenant
      const payload = {
        enabled_incident: next.enabled_incident,
        enabled_kudos: next.enabled_kudos,
        enabled_note: next.enabled_note,
        // always sent: an absent key means keep on the server and an
        // emptied row has to actually clear
        category_routes: this.routePayloadRows(next.category_routes),
      }
      // FI-1944 the Name rides only when it changed (absent means keep): a
      // fleet's own category under custom_labels, a built-in under
      // label_overrides, where '' goes back to the original name
      const sentName = this.pageNameChanged ? this.pageDraftName : null
      if (sentName !== null) {
        if (isCustomCategory(slug)) payload.custom_labels = { [slug]: sentName }
        else payload.label_overrides = { [slug]: sentName === this.pageOriginalName ? '' : sentName }
      }
      try {
        const res = await this.patchCategories(payload)
        if (this.tenant !== tenant) return false
        // what the server kept, with the draft's lists and routes as the
        // fallback (notes commit from the response only, FI-2173)
        this.adoptCategoriesResponse(res && res.value && res.value.data, next)
        this.categoriesSavedAt = new Date()
        this.savedSnapshots.categories = this.categoriesSnapshot
        // the baseline is what the server kept. the pickers stay open while
        // the PATCH is out, so a draft edited in the meantime is kept as it
        // is (dirty against the new baseline) rather than re-seeded over.
        // a draft that is no longer ours (the page closed under a reload)
        // is left alone
        if (this.pageDraft === draft) {
          const saved = this.seedPageDraft(kind, slug)
          this.pageBaseline = snapshotDraft(saved)
          if (this.pageSnapshot === sentSnap) this.pageDraft = saved
          this.pageSavedAt = new Date()
        }
        return true
      } catch (e) {
        this.pageSavedAt = null
        // FI-1944 a 400 on a save that sent a name is the server refusing
        // it (the only refusals this call has): its words go under Name
        // until the name changes. anything else is the rail's generic line
        const refusal = apiRefusal(e)
        if (refusal && sentName !== null) this.pageNameRefusal = { kind, slug, name: sentName, message: refusal }
        else this.pageError = refusal || 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings savePage failed', e)
        return false
      } finally {
        this.pageSaving = false
      }
    },
    // Discard: the draft goes back to its baseline; the Every page restores
    // the saved distribution form
    discardPage() {
      if (!this.catPage) return
      if (this.catPage.kind === 'every') {
        this.discardDistribution()
        return
      }
      this.pageDraft = this.seedPageDraft(this.catPage.kind, this.catPage.slug)
      this.pageBaseline = this.pageSnapshot
      this.pageError = ''
    },
    closePage() {
      this.catPage = null
      this.pageDraft = null
      this.pageBaseline = ''
      this.pageError = ''
    },
    // Back: a dirty page asks first. then the list returns at the scroll
    // position it was left at, with focus on the row's opener (the lead
    // band when the row is gone, e.g. a slug the server dropped).
    backToList() {
      if (this.categoriesBusy || this.pageBusy || !this.catPage) return
      if (this.pageDirty && !this.confirmDiscard()) return
      const page = this.catPage
      this.discardPage()
      if (this.phoneDrill) this.drillTo('back')
      this.closePage()
      this.$nextTick(() => {
        const body = this.modalBody()
        if (body) body.scrollTop = this.listScrollTop
        let opener = page.kind === 'every'
          ? this.$refs['chev-every']
          : this.$refs['chev-' + page.kind + '-' + page.slug]
        if (Array.isArray(opener)) opener = opener[0]
        if (opener && opener.focus) {
          opener.focus({ preventScroll: true })
          return
        }
        const lead = this.$el && this.$el.querySelector
          ? this.$el.querySelector('.od-set-list .od-set-lead') : null
        if (lead) {
          lead.setAttribute('tabindex', '-1')
          lead.focus({ preventScroll: true })
        }
      })
    },
    // the Huddle and Brief line. FI-2162 adds the restricted clause; keep
    // the inputs to what the page knows. a note page names notes, not a
    // category (FI-2173)
    huddleStatus(state, kind) {
      if (state && state.enabled) {
        return { ok: true, text: 'Included in the huddle recap and the Fleet Assistant Brief.' }
      }
      if (kind === 'note') {
        return { ok: false, text: 'Left out of the huddle recap and the Fleet Assistant Brief while notes are off.' }
      }
      return { ok: false, text: 'Left out of the huddle recap and the Fleet Assistant Brief while the category is off.' }
    },

    addDepartment(name) {
      const v = (name || '').trim()
      if (!v) return
      const lc = v.toLowerCase()
      // dedupe by name so a quick-add and a hand-typed entry can't collide.
      if ((this.form.departments || []).some(d => (d.name || '').toLowerCase() === lc)) return
      // FI-2201 (#1815) new ones go on top, next to the add controls above
      // the list, so the admin sees the card they are about to fill in
      this.form.departments = [{ name: v, members: [] }, ...(this.form.departments || [])]
    },
    // a stable card key per department object. an index key would hand a
    // card's member-box draft to its neighbour after a prepend or a remove.
    // the map is not reactive and never reaches the payload or a snapshot
    deptKey(dept) {
      if (!this._deptKeys) {
        this._deptKeys = new WeakMap()
        this._deptKeySeq = 0
      }
      let key = this._deptKeys.get(dept)
      if (!key) {
        this._deptKeySeq += 1
        key = 'dept-' + this._deptKeySeq
        this._deptKeys.set(dept, key)
      }
      return key
    },
    removeDepartment(i) {
      const next = (this.form.departments || []).slice()
      next.splice(i, 1)
      this.form.departments = next
    },
    commitDeptDraft() {
      const v = this.deptDraft.trim()
      if (!v) return
      this.addDepartment(v)
      this.deptDraft = ''
    },

    async saveDepartments() {
      this.departmentsError = ''
      // block the save while any card is unnamed; the per-card hint says
      // why. silently dropping the card would lose its member list.
      if ((this.form.departments || []).some(d => !(d.name || '').trim())) {
        this.departmentsBlankAttempted = true
        return
      }
      this.savingDepartments = true
      try {
        // trim before saving; the backend also dedupes and normalizes, so
        // re-hydrate from its response to keep the UI in lock-step with
        // what was stored.
        const payload = (this.form.departments || [])
          .map(d => ({ name: (d.name || '').trim(), members: (d.members || []).slice() }))
        const res = await update_ops_desk_departments(this.tenant, payload)
        const data = (res && res.value && res.value.data) || {}
        if (Array.isArray(data.departments)) {
          this.form.departments = data.departments.map(d => ({
            name: d.name,
            members: Array.isArray(d.members) ? d.members.slice() : [],
          }))
        }
        this.departmentsSavedAt = new Date()
        this.departmentsBlankAttempted = false
        this.savedSnapshots.departments = this.departmentsSnapshot
      } catch (e) {
        this.departmentsSavedAt = null
        this.departmentsError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveDepartments failed', e)
      } finally {
        this.savingDepartments = false
      }
    },

    // the huddle row's network seam, so a spec can stand in for it
    patchHuddleSchedule(payload) {
      return update_ops_desk_huddle_schedule(this.tenant, payload)
    },
    // a huddle snapshot less the Brief: the schedule fields alone
    huddleScheduleOf(snap) {
      const s = JSON.parse(snap || '{}')
      delete s.fa
      return JSON.stringify(s)
    },
    async saveHuddle() {
      // baseline what is sent, so an edit made while the PATCH is out (the
      // Brief switch stays live) stays dirty; an answer for a tenant
      // switched away from is dropped
      const tenant = this.tenant
      const sent = this.huddleSnapshot
      const baseline = this.savedSnapshots.huddle
      // FI-2201 (#1815) the Additional recipients the draft adds and drops,
      // by id against the saved rows, on their own call after the schedule.
      // the schedule goes when it or the Brief was edited, or when Save is
      // the answer to the advanced-cron warning with nothing else to send,
      // so a recipients-only Save never replaces an advanced cron
      const savedIds = (this.huddleSubscribers || []).map(s => s.user_id)
      const draftIds = (this.huddleSubsDraft || []).map(s => s.user_id)
      const subs = {
        add: draftIds.filter(id => !savedIds.includes(id)),
        remove: savedIds.filter(id => !draftIds.includes(id)),
      }
      const recipients = subs.add.length > 0 || subs.remove.length > 0
      const schedule = sent !== baseline || (this.huddleCronUnparseable && !recipients)
      // FI-2201 (#1815) only the Brief changed: the stored cron goes back as
      // it is. the picker would snap its minute, drop its day of month and
      // month, or replace an advanced one, and a Brief flip asked for none
      const briefOnly = baseline !== null && sent !== baseline
        && this.huddleScheduleOf(sent) === this.huddleScheduleOf(baseline)
      this.savingHuddle = true
      this.huddleError = ''
      try {
        if (schedule) {
          // otherwise recompose right before save so any picker tweak the
          // watcher hasn't synced yet still lands in the payload.
          const cron = briefOnly ? this.form.huddle_cron : this.composeHuddleCron()
          this.form.huddle_cron = cron
          await this.patchHuddleSchedule({
            cron: cron,
            tz: this.form.huddle_tz,
            enabled: this.form.huddle_enabled,
            fa_brief_enabled: this.form.fa_brief_enabled,
          })
          if (this.tenant !== tenant) return
          // once the picker's schedule is saved it becomes the source of
          // truth, so any legacy "advanced" warning clears. a Brief-only save
          // left the stored schedule, and its warning, as they were
          if (!briefOnly) this.huddleCronUnparseable = false
          this.savedSnapshots.huddle = sent
        }
        // the server answers with the fresh rows (emails resolved), which
        // are the new saved rows and the draft. a failure here leaves the
        // schedule saved and only the recipients dirty
        if (recipients) {
          const res = await this.patchHuddleSubscribers(subs)
          if (this.tenant !== tenant) return
          this.adoptHuddleSubscribers(((res && res.value && res.value.data) || {}).subscribers || [])
        }
        this.huddleSavedAt = new Date()
      } catch (e) {
        if (this.tenant !== tenant) return
        this.huddleSavedAt = null
        this.huddleError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveHuddle failed', e)
      } finally {
        this.savingHuddle = false
      }
    },

    toggleHuddleDay(day) {
      const i = this.huddleDays.indexOf(day)
      if (i >= 0) this.huddleDays.splice(i, 1)
      else this.huddleDays.push(day)
    },

    // parse the cron string the backend returned. picker handles minute,
    // hour, comma-separated day-of-week names — the same subset the
    // backend matcher supports. anything else trips the "advanced
    // expression" warning so the user knows saving will replace it.
    hydrateHuddlePickerFromCron(cron) {
      const fallback = () => {
        this.huddleCronUnparseable = !!(cron || '').trim()
        this.huddleDays = ['SUN']
        this.huddleHour12 = '11'
        this.huddleMinute = '00'
        this.huddleAmPm = 'pm'
      }
      const trimmed = (cron || '').trim()
      if (!trimmed) { fallback(); this.huddleCronUnparseable = false; return }
      const parts = trimmed.split(/\s+/)
      if (parts.length < 5) return fallback()
      const [minuteStr, hourStr, , , dowStr] = parts
      const minute = parseInt(minuteStr, 10)
      const hour = parseInt(hourStr, 10)
      if (!Number.isFinite(minute) || minute < 0 || minute > 59) return fallback()
      if (!Number.isFinite(hour) || hour < 0 || hour > 23) return fallback()
      const validDows = DAY_OPTIONS.map(d => d.value)
      const intToDow = { 1: 'SUN', 2: 'MON', 3: 'TUE', 4: 'WED', 5: 'THU', 6: 'FRI', 7: 'SAT' }
      const rawDows = (dowStr || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
      const dows = []
      for (const tok of rawDows) {
        if (validDows.includes(tok)) dows.push(tok)
        else if (intToDow[tok]) dows.push(intToDow[tok])
        else return fallback()
      }
      if (!dows.length) return fallback()
      this.huddleDays = Array.from(new Set(dows))
      // 24h -> 12h + AM/PM, with 12:xx handled correctly
      const ampm = hour >= 12 ? 'pm' : 'am'
      const display12 = ((hour + 11) % 12) + 1
      this.huddleHour12 = String(display12)
      // snap to the nearest 5-minute step so the select can match a value
      const snapped = Math.round(minute / 5) * 5
      this.huddleMinute = String(snapped % 60).padStart(2, '0')
      this.huddleAmPm = ampm
      this.huddleCronUnparseable = false
    },

    // compose the picker values back into AWS cron format. inverse of
    // hydrateHuddlePickerFromCron; called from saveHuddle and the
    // watcher so form.huddle_cron stays in sync for any downstream
    // consumer (e.g. the schedule label fallback).
    composeHuddleCron() {
      const dows = (this.huddleDays && this.huddleDays.length)
        ? DAY_OPTIONS.map(d => d.value).filter(v => this.huddleDays.includes(v))
        : ['SUN']
      let hour = parseInt(this.huddleHour12 || '12', 10)
      if (!Number.isFinite(hour)) hour = 12
      if (this.huddleAmPm === 'pm' && hour !== 12) hour += 12
      else if (this.huddleAmPm === 'am' && hour === 12) hour = 0
      const minute = parseInt(this.huddleMinute || '0', 10) || 0
      return `${minute} ${hour} ? * ${dows.join(',')} *`
    },

    // ---- FI-1964 sheet import ----

    // read a 400's human message out of the api error envelope (same shape
    // saveSlug reads); empty string when it isn't that shape.
    readApiError(e) {
      try {
        return e.response.data.error_data.error_message || ''
      } catch (x) {
        return ''
      }
    },

    importCount(kind) {
      return (this.importSummary && this.importSummary.counts && this.importSummary.counts[kind]) || 0
    },

    // cron the backend matcher understands. always on the hour: the
    // dispatcher runs once an hour on the minute-0 tick, so a nonzero minute
    // would never fire (the backend pins minute 0 too as a backstop).
    // hourly = hour wildcard, which matches every tick.
    composeImportCron() {
      if (this.importFreq === 'hourly') return '0 * * * ? *'
      let hour = parseInt(this.importHour12 || '8', 10)
      if (!Number.isFinite(hour)) hour = 8
      if (this.importAmPm === 'pm' && hour !== 12) hour += 12
      else if (this.importAmPm === 'am' && hour === 12) hour = 0
      return `0 ${hour} * * ? *`
    },

    // pull the frequency + hour out of a saved cron back into the picker. the
    // day/month fields are ignored: this ui only writes hourly or daily
    // schedules.
    hydrateImportPickerFromCron(cron) {
      const fallback = () => {
        this.importFreq = 'daily'
        this.importHour12 = '8'
        this.importMinute = '00'
        this.importAmPm = 'am'
      }
      const parts = (cron || '').trim().split(/\s+/)
      if (parts.length < 2) return fallback()
      if (parts[1] === '*' || parts[1] === '?') {
        // hour wildcard = hourly; the time picker keeps its defaults so
        // switching back to daily lands somewhere sensible.
        fallback()
        this.importFreq = 'hourly'
        return
      }
      const minute = parseInt(parts[0], 10)
      const hour = parseInt(parts[1], 10)
      if (!Number.isFinite(minute) || minute < 0 || minute > 59) return fallback()
      if (!Number.isFinite(hour) || hour < 0 || hour > 23) return fallback()
      this.importFreq = 'daily'
      this.importAmPm = hour >= 12 ? 'pm' : 'am'
      this.importHour12 = String(((hour + 11) % 12) + 1)
      // schedule is on-the-hour only; the stored minute is ignored on display.
      this.importMinute = '00'
    },

    async copyServiceAccount() {
      const v = this.importServiceAccount
      if (!v) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(v)
        }
        this.importSaCopied = true
        setTimeout(() => { this.importSaCopied = false }, 1500)
      } catch (e) {
        // clipboard blocked (older safari without a gesture); non-fatal.
      }
    },

    async saveImport() {
      this.savingImport = true
      this.importError = ''
      try {
        const res = await update_ops_desk_import_config(this.tenant, {
          spreadsheet_id: this.form.import_spreadsheet_id,
          tab: this.form.import_tab,
          cron: this.composeImportCron(),
          tz: this.form.import_tz,
          enabled: this.form.import_enabled,
        })
        const data = (res && res.value && res.value.data) || {}
        // reflect the server-normalized values (it extracts the bare id from a
        // pasted url and defaults the tab) so the saved snapshot matches.
        if (typeof data.spreadsheet_id === 'string') this.form.import_spreadsheet_id = data.spreadsheet_id
        if (data.tab) this.form.import_tab = data.tab
        if (typeof data.spreadsheet_title === 'string') this.importTitle = data.spreadsheet_title
        this.importSavedAt = new Date()
        this.savedSnapshots.import = this.importSnapshot
      } catch (e) {
        this.importSavedAt = null
        this.importError = this.readApiError(e) || 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveImport failed', e)
      } finally {
        this.savingImport = false
      }
    },

    // --- connect wizard ---

    resetImportInspect() {
      this.importInspect = null
      this.importTestResult = null
      this.importSummary = null
      this.importRunError = ''
    },

    async inspectImport() {
      if (!(this.form.import_spreadsheet_id || '').trim() || this.inspectingImport) return
      this.inspectingImport = true
      this.resetImportInspect()
      try {
        const res = await inspect_ops_desk_import(this.tenant, {
          spreadsheet_id: this.form.import_spreadsheet_id,
        })
        const data = (res && res.value && res.value.data) || {}
        this.importInspect = data
        if (data.ok) {
          // keep a previously chosen tab if it still exists, else preselect
          // the first tab; either way validate it right away so the admin
          // sees the columns check without another click.
          const titles = (data.tabs || []).map(t => t.title)
          if (!titles.includes(this.form.import_tab)) this.form.import_tab = titles[0] || 'Sheet1'
          this.testImport()
        }
      } catch (e) {
        this.importInspect = { ok: false, message: this.readApiError(e) || 'Could not reach the sheet. Try again.' }
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings inspectImport failed', e)
      } finally {
        this.inspectingImport = false
      }
    },

    pickImportTab(title) {
      this.form.import_tab = title
      this.importTestResult = null
      this.importSummary = null
      this.importRunError = ''
      this.testImport()
    },

    // preview reads the SAVED config server-side, so the wizard saves first
    // (with whatever the enabled toggle currently holds — off for a first
    // connect) and then dry-runs.
    async wizardPreview() {
      await this.saveImport()
      if (this.importError) return
      await this.previewImport()
    },

    async finishImportWizard() {
      await this.saveImport()
      if (this.importError) return
      this.importWizardActive = false
      this.importWasConfigured = true
      this.importInspect = null
    },

    changeImportSheet() {
      this.importWizardActive = true
      this.resetImportInspect()
    },

    // discard whatever the wizard touched and re-hydrate from the saved
    // config so the management view reflects what's actually live.
    // FI-2201 (#1815) the import config alone, not the whole settings: the
    // other sections' unsaved changes (the categories switches, the huddle
    // recipients among them) stay. the wizard stays up until the saved
    // config is back, so the view never shows the wizard's sheet as live
    async cancelImportWizard() {
      const tenant = this.tenant
      this.resetImportInspect()
      const loaded = await this.loadImportConfig()
      if (this.tenant !== tenant) return
      // a failed read still leaves the wizard, as the full reload did
      if (!loaded) this.importWizardActive = false
      this.savedSnapshots.import = this.importSnapshot
      this.importError = ''
    },

    async testImport() {
      this.testingImport = true
      this.importTestResult = null
      try {
        const res = await test_ops_desk_import(this.tenant, {
          spreadsheet_id: this.form.import_spreadsheet_id,
          tab: this.form.import_tab,
        })
        const data = (res && res.value && res.value.data) || {}
        this.importTestResult = {
          ok: !!data.ok,
          message: data.message || (data.ok ? 'Connected.' : 'Could not read the sheet.'),
        }
      } catch (e) {
        this.importTestResult = { ok: false, message: this.readApiError(e) || 'Could not reach the sheet. Try again.' }
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings testImport failed', e)
      } finally {
        this.testingImport = false
      }
    },

    async previewImport() {
      this.previewingImport = true
      this.importRunError = ''
      try {
        const res = await preview_ops_desk_import(this.tenant)
        this.importSummary = (res && res.value && res.value.data) || null
      } catch (e) {
        this.importRunError = this.readApiError(e) || 'Preview failed. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings previewImport failed', e)
      } finally {
        this.previewingImport = false
      }
    },

    async runImport() {
      this.runningImport = true
      this.importRunError = ''
      try {
        const res = await run_ops_desk_import(this.tenant, false)
        this.importSummary = (res && res.value && res.value.data) || null
        if (this.importSummary && !this.importSummary.dry_run) {
          this.importLastRun = { at: this.importSummary.ran_at, status: 'ok', error: '' }
        }
      } catch (e) {
        this.importRunError = this.readApiError(e) || 'Sync failed. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings runImport failed', e)
      } finally {
        this.runningImport = false
      }
    },
  },
}
</script>

<style scoped>
/* the host owns width + padding: the modal body under the settings modal,
   .fi-hz-content under the Settings page (page-mode rules below). the
   app-level #app rule sets text-align: center, which cascades into every
   label / help / caption inside the settings page; force a left baseline
   here so the tabs, form labels, checkboxes, and helper copy align flush. */
.od-set-shell {
  text-align: left;
}

.od-set-loading { text-align: center; padding: 32px; color: #6b7280; font-size: 13.5px; }

/* tab bar itself is fi_tab_bar.vue (FI-1983); its own class names mean
   navbar.vue's unscoped global .nav-tabs rules can't reach it, so the old
   !important wall is gone. */

/* FI-2166 desktop layout: the section rail flush against the dialog's left
   edge (the modal body drops its padding above the phone breakpoint, see
   erm_settings_modal.vue .od-settings-modal), the content pane restating the
   modal's own padding. the rail is the tenant settings shell's squared left
   nav (tenant_config_shell.vue .tc-nav-*) painted onto the shared tab bar's
   vertical mode; ::v-deep because the tab buttons live in the child. the
   Settings page host lives on the same rules plus the page-mode block below. */
.od-set-layout--rail { display: flex; align-items: stretch; }
.od-set-nav {
  flex: 0 0 250px;
  width: 250px;
  align-self: stretch;
  background: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}
.od-set-nav ::v-deep .fi-tabbar-tab {
  position: relative;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  border-radius: 0;
  transition: background-color 0.12s;
}
.od-set-nav ::v-deep .fi-tabbar-tab:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 16px; right: 16px; bottom: 0;
  height: 1px;
  background: #c8d8e8;
}
.od-set-nav ::v-deep .fi-tabbar-tab:hover { background: #d0dfee; color: #333; }
.od-set-nav ::v-deep .fi-tabbar-tab--active,
.od-set-nav ::v-deep .fi-tabbar-tab--active:hover {
  background: var(--fi-rail-accent);
  color: #fff;
  font-weight: 700;
  border-right: 3px solid #2e6da4;
}
.od-set-nav ::v-deep .fi-tabbar-tab--active::after { display: none; }
.od-set-nav ::v-deep .fi-tabbar-tab--active .fi-tabbar-dot { background: #fff; }
/* the ring sits inside the row: the rail's left edge is the dialog edge */
.od-set-nav ::v-deep .fi-tabbar-tab:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: -2px;
  background: #d0dfee;
}
.od-set-content { flex: 1 1 auto; min-width: 0; }
.od-set-layout--rail .od-set-content { padding: 0 18px 22px; }
/* the phone drill-in list (phoneDrill): one grouped card in the settings
   landing's language (tenant_config_shell .tc-ov-card): white card, 8px
   corners, an icon tile per row, hairlines that start after the tile, a
   chevron. while it shows, every section is hidden, the open one included
   (v-show only hides the others). midnight.css repaints it for the dark
   theme next to the .od-set-nav rules. */
.od-set-content--list > section { display: none !important; }
/* the section (phoneSettle) and a category page (focusPage) take focus on
   the way in on a phone; no ring for that */
.od-set-content > section:focus,
.od-set-page:focus { outline: none; }
.od-set-sections {
  background: #fff;
  border: 1px solid #d0d8e4;
  border-radius: var(--fi-radius-md, 8px);
  box-shadow: 0 1px 3px rgba(70, 130, 180, 0.08);
  overflow: hidden;
  margin-bottom: 14px;
}
.od-set-sections-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 64px;
  padding: 10px 14px;
  background: transparent;
  border: 0;
  border-radius: 0;
  text-align: left;
  color: #222;
  cursor: pointer;
  transition: background-color 0.12s;
}
/* starts after the tile: 14px padding + 34px tile + 12px gap */
.od-set-sections-row:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 60px; right: 0; bottom: 0;
  height: 1px;
  background: #e6ebf1;
}
/* hover only where there is one: a tap must not leave a row lit. a press
   darkens the row and nudges its chevron, so the tap registers before the
   section slides in (tapHold lets iOS Safari paint :active) */
.od-set-sections-row { -webkit-tap-highlight-color: transparent; }
@media (hover: hover) {
  .od-set-sections-row:hover { background: #f3f7fb; }
}
.od-set-sections-row:active { background: #e8eff7; }
.od-set-sections-row:active .od-set-sections-chev { transform: translateX(3px); }
.od-set-sections-row:focus { outline: none; }
.od-set-sections-row:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: -2px;
  background: #f3f7fb;
}
/* the settings landing's icon tile (.tc-ov-icon) */
.od-set-sections-icon {
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--fi-radius-md, 8px);
  background: rgba(70, 130, 180, 0.12);
  color: steelblue;
  font-size: 15px;
}
.od-set-sections-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.od-set-sections-label { font-size: 15px; font-weight: 600; line-height: 1.3; }
.od-set-sections-desc { font-size: 13px; line-height: 1.35; color: var(--fi-text-muted, #5c626e); margin-top: 2px; }
.od-set-sections-chev { flex-shrink: 0; font-size: 13px; color: #6f8091; transition: transform 0.12s ease; }
/* the unsaved-changes marker, the tab bar's dot */
.od-set-sections-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--fi-ops-accent, #4682b4);
  margin-left: 6px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}
/* the phone drill-in's moves (drillDir): the screen that appears slides in
   from the side it comes from, with a quick fade, like a native push. only
   the arriving screen moves (the one leaving is gone at once), and
   'backwards' fill leaves no transform behind once it lands, so the sticky
   table header keeps working */
@keyframes od-drill-in-fwd {
  from { opacity: 0; transform: translateX(28px); }
}
@keyframes od-drill-in-back {
  from { opacity: 0; transform: translateX(-28px); }
}
.od-set-content--fwd > section,
.od-set-content--fwd .od-set-page {
  animation: od-drill-in-fwd 0.26s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
}
.od-set-content--back > .od-set-sections,
.od-set-content--back .od-set-list {
  animation: od-drill-in-back 0.26s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
}
@media (prefers-reduced-motion: reduce) {
  .od-set-content--fwd > section,
  .od-set-content--fwd .od-set-page,
  .od-set-content--back > .od-set-sections,
  .od-set-content--back .od-set-list { animation: none; }
  .od-set-sections-chev { transition: none; }
}
/* the xl dialog is 800px wide between 992 and 1199: give the table the room.
   modal only: on the page the pane is the viewport minus the section nav,
   so the rail keeps its 250px down to the phone swap */
@media (max-width: 1199px) {
  .od-set-shell:not(.od-set-layout--page) .od-set-nav { flex-basis: 200px; width: 200px; }
}
/* FI-2172 page mode (tenant_config_erm.vue). the pane drops the modal's side
   padding and keeps the vertical rhythm: .fi-hz-content (App.vue) already
   pays the page's gutters and every section pads its own 20px. the rail
   holds a minimum height because nothing stretches it here (in the modal
   the dialog body does): a short section like Public form or Huddle would
   otherwise leave it a stub above a blank pane. 60vh reads as a rail on any
   viewport and never adds a scroll of its own. */
.od-set-layout--page .od-set-layout--rail .od-set-content { padding-left: 0; padding-right: 0; }
.od-set-layout--page .od-set-nav { min-height: 60vh; }
/* under horizon / midnight the page's header pins under the top bar
   (tenant_config_shell.vue, --tc-rail-h), and the section rail pins just
   below it, so a long section never scrolls the other sections out of
   reach. align-self keeps it at its own 60vh height, since a rail
   stretched to the section's full height would have nowhere to stick */
.fi-hz-root .od-set-layout--page .od-set-nav {
  position: sticky;
  top: calc(var(--fi-hz-top-offset, 52px) + var(--tc-rail-h, 0px));
  align-self: flex-start;
}
/* classic (site_theme original, html[data-fi-theme="original"]): the settings
   shell still mounts its own 250px left rail (tenant_config_shell.vue
   .tc-nav-*), the very rail this one restates, 24px to the left of it. narrow
   this one to the modal's step width and let it size to its content the way
   the shell's does, so the pair reads as rail and sub-rail, not twins.
   horizon and midnight never mount the shell rail. */
[data-fi-theme="original"] .od-set-layout--page .od-set-nav { flex-basis: 200px; width: 200px; min-height: 0; }
.od-set-section { padding: 12px 20px 16px 20px; }

/* section headers inside a tab (huddle uses these); new feature areas
   (insights, stats) slot in as additional sections. */
.od-set-sect-head {
  margin: 24px 0 8px 0;
  font-size: 13px;
  font-weight: 700;
  color: #1d1d1f;
  letter-spacing: -0.1px;
}

/* help-text: drop the chip-style container, just a quiet caption above
   the first field. less form-builder, more product. */
.od-set-help {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 16px 0;
  line-height: 1.5;
  background: none;
  border: none;
  padding: 0;
}
.od-set-label {
  display: block; margin: 12px 0 4px 0;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.od-set-label--small { font-size: 11px; margin-top: 16px; }
.od-set-input, .od-set-select { max-width: 380px; }

/* inline slug row: input + Save button side-by-side. mobile collapses
   the button under the input via flex-wrap. */
.od-set-slug-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  max-width: 480px;
  flex-wrap: wrap;
}
.od-set-slug-input { flex: 1 1 260px; min-width: 200px; }
.od-set-slug-save-btn { flex: 0 0 auto; align-self: stretch; }

.od-set-slug-saved-chip {
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  background: #e7f8ed;
  color: #166534;
  border: 1px solid #b5e3c5;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}
.od-set-slug-saved-chip .fa { font-size: 11px; }

/* access row layout. toggle + headline/sub stack so the explanation
   reads as the toggle's caption rather than a separate block. */
.od-set-access-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #e1e8f0;
  border-radius: 6px;
  background: #f8fafc;
  max-width: 640px;
  margin-top: 4px;
}
.od-set-access-toggle { flex-shrink: 0; margin-top: 2px; }
.od-set-access-text { flex: 1; min-width: 0; }
.od-set-access-headline { font-size: 13.5px; font-weight: 600; color: #1d1d1f; }
.od-set-access-sub { font-size: 12.5px; color: #6b7280; margin-top: 3px; line-height: 1.45; }

/* FI-2097 subject-style picker: a narrow control, not a full-width bar */
.od-set-subject-format { max-width: 260px; display: block; }
.od-set-subject-format-sub { margin-bottom: 4px; }
/* compact variant: same surface, tighter padding for inline use in the
   huddle tab where vertical breathing room is already supplied by the
   sections above and below. */
.od-set-access-row--compact { padding: 10px 12px; }

/* status banner directly under the input: green / red / neutral
   depending on the availability verdict. */
.od-set-slug-status {
  margin-top: 6px;
  font-size: 12.5px;
  line-height: 1.4;
  display: flex; align-items: center; gap: 2px;
}
.od-set-slug-status--ok { color: #166534; }
.od-set-slug-status--bad { color: #b91c1c; }
.od-set-slug-status--neutral { color: #6b7280; }

.od-set-url-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}
/* FI-1938: the scannable copy of the url. the canvas keeps a white ground on
   every theme so the code scans; the text column mirrors the access row's
   headline / sub pair below it. */
.od-set-qr {
  display: flex; align-items: center; gap: 14px;
  margin-top: 10px; max-width: 640px;
}
.od-set-qr-canvas {
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #c8d4df; border-radius: 6px;
  padding: 6px;
}
.od-set-qr-text { min-width: 0; }
.od-set-qr-headline { font-size: 14px; font-weight: 600; color: var(--fi-text, #1f2937); }
.od-set-qr-sub { font-size: 12px; color: var(--fi-text-muted, #6b7280); margin: 2px 0 8px; }
@media (max-width: 767px) {
  .od-set-qr { align-items: flex-start; }
}
.od-set-tags { max-width: 640px; }
.od-set-hint { font-size: 12px; color: #6b7280; margin-top: 4px; }
.od-set-error { font-size: 12.5px; color: #b91c1c; margin-top: 4px; }

/* inline failure box under a tab's save row */
.od-set-save-error {
  margin-top: 10px;
  background: #fbe3e5;
  border: 1px solid #f5c0c4;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  color: #b91c1c;
  max-width: 640px;
}

.od-set-url-box {
  background: #f3f4f6; border: 1px dashed #c8d4df; border-radius: 6px;
  padding: 10px 12px; max-width: 640px;
  display: flex; align-items: center; gap: 12px;
}
/* size the URL to match the form path input (16px) so the two visually
   anchor each other as the "this is what you typed → this is the URL
   it produced" pair. browser default shrinks <code> to ~87.5% of the
   parent, so set it explicitly. */
.od-set-url-box code {
  color: var(--fi-ops-accent-strong);
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  line-height: 1.3;
}
/* each path piece wraps as a unit; break-all inside is the fallback for
   a single piece wider than the box */
.od-set-url-seg {
  display: inline-block;
  max-width: 100%;
  word-break: break-all;
}
.od-set-url-actions {
  display: flex; gap: 6px; flex-shrink: 0; align-items: center;
}
/* small steelblue ghost buttons that sit cleanly inside the dashed URL
   box. matches the day-chip / filter-chip palette used elsewhere on the
   Ops Desk surface: white fill, light gray border by default, steelblue
   border + tinted background on hover. */
.od-set-url-btn {
  background: #fff !important;
  border: 1px solid #c8d4df !important;
  border-radius: 4px !important;
  padding: 4px 10px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: var(--fi-ops-eyebrow) !important;
  display: inline-flex !important;
  align-items: center;
  gap: 4px;
  text-decoration: none !important;
  transition: background .12s, color .12s, border-color .12s;
  line-height: 1.4;
}
.od-set-url-btn:hover,
.od-set-url-btn:focus {
  background: #eef4fb !important;
  border-color: var(--fi-rail-accent) !important;
  color: var(--fi-ops-accent-strong) !important;
  box-shadow: none !important;
}
.od-set-url-btn .fa { font-size: 11px; }

/* neutral schedule information, so the blue trio rather than success green */
.od-set-preview-box {
  background: #e7f1ff; border: 1px solid #bcd8fb; border-radius: 6px;
  padding: 8px 12px; font-size: 13px; color: #0b3d91;
  max-width: 380px;
}

/* one save-row pattern for every tab: optional secondary action (e.g.
   Restore defaults) parks on the left, the hint + Save group floats
   right via the primary group's auto margin. */
.od-set-actions {
  display: flex; align-items: center; gap: 10px;
  margin-top: 20px; padding-top: 14px;
  border-top: 1px solid #eef0f3;
}
.od-set-action-secondary {
  padding-left: 0 !important; color: var(--fi-ops-eyebrow) !important;
  text-decoration: none !important; font-size: 13px;
}
.od-set-action-secondary:hover { color: var(--fi-ops-accent-strong) !important; }
.od-set-actions-primary {
  display: flex; align-items: center; gap: 10px;
  margin-left: auto;
}
.od-set-saved-hint { font-size: 12px; color: #166534; }
.od-set-unsaved-hint { font-size: 12px; color: #6b7280; }

/* section lead: the tab's sentence in the house rail band (the drill-in
   .od-rail geometry, tokens only so every theme resolves it), no title
   because the rail row already names the section. FI-2166 categories adds
   the corner: Restore defaults and the list's save state, which FI-2201
   (#1815) gives a Save while the table has unsaved changes, so the top rows
   are never out of sight of it (the Save row under the table is the same
   Save). the band is Back's focus fallback when a row is gone, hence the
   focus rule. */
.od-set-lead {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 16px;
  background: var(--fi-ops-rail-bg, #eef2f7);
  border-left: 3px solid var(--fi-rail-accent, #2e6da4);
  border-radius: 0 6px 6px 0;
}
.od-set-lead:focus { outline: none; }
.od-set-lead-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--fi-text, #1f2937);
  max-width: 68ch;
}
.od-set-lead-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;
}
.od-set-rail-hint { font-size: 12px; color: #6b7280; white-space: nowrap; }
.od-set-rail-hint--ok { color: #166534; }
.od-set-rail-hint--bad { color: #b91c1c; }

/* the default recipients card between the lead band and the table: a bordered
   white card, not a tinted row, so it is not mistaken for table chrome. the
   eyebrow + name pair mirrors the page it opens (.od-rail-eyebrow); the
   border is the table's own neutral hairline rather than var(--fi-line-strong),
   whose warm tan reads beige between the cool lead band and the #f8f9fa
   thead (midnight re-inks it). the chevron is the card's one focusable
   control and keeps the family focus ring below */
.od-set-every {
  display: flex; flex-wrap: wrap; gap: 6px 20px; align-items: flex-start;
  position: relative;
  margin: 0 0 12px;
  padding: 10px 44px 10px 14px;
  background: var(--fi-surface, #fff);
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
}
.od-set-every:hover { border-color: var(--fi-ops-accent, #2e6da4); }
.od-set-every-main { flex: 1 1 300px; min-width: 0; }
.od-set-every-recip { flex: 1 1 220px; min-width: 0; font-size: 13px; }
.od-set-eyebrow {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .06em;
  color: var(--fi-text-muted, #6b7280);
  margin-bottom: 2px;
}
.od-set-every-name { font-size: 14px; font-weight: 600; color: var(--fi-text, #1f2937); }
.od-set-every-sub { margin: 2px 0 0; font-size: 12px; color: var(--fi-text-muted, #6b7280); }
.od-set-every .od-set-chev { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); }

/* the table is the event log's inline table (ops_desk.vue .od-table-inline)
   restated for a plain <table>: 13px, collapsed, no side borders, sticky
   uppercase header. stripes are painted by class (i % 2) rather than
   nth-child because the group and note rows sit inside the same tbody. the
   thead sticks to the host's scroll container (the modal body, or the window
   on the Settings page), so the table must not sit inside an overflow
   container of its own. */
.od-set-table { width: 100%; border-collapse: collapse; font-size: 13px; line-height: 1.5; }
.od-set-table th,
.od-set-table td {
  border-left: none; border-right: none;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px;
  vertical-align: middle;
  text-align: left;
}
.od-set-table tbody td { vertical-align: top; }
.od-set-table th:first-child,
.od-set-table td:first-child { padding-left: 12px; }
.od-set-table th:last-child,
.od-set-table td:last-child { padding-right: 12px; }
.od-set-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  font-weight: 700;
  position: sticky; top: 0; z-index: 2;
  /* a closed band: a rule on both edges, so the header reads as the table's
     own top rather than the bottom of whatever sits above it */
  box-shadow: inset 0 1px 0 0 #e0e0e0, inset 0 -2px 0 0 #ccc;
}
/* page mode under horizon: the top bar is fixed, so the header parks under
   it instead of sliding beneath it. the same variable App.vue's .fi-hz-root
   pads by; classic has no fixed bar (and no .fi-hz-root) and keeps top: 0. */
.fi-hz-root .od-set-layout--page .od-set-table thead th { top: calc(var(--fi-hz-top-offset, 52px) + var(--tc-rail-h, 0px)); }
/* the header sits over a fixed column no matter which chips a row carries.
   FI-2162's Who can see column shares this thead: revisit the split then */
.od-set-table th.od-set-col-recip { width: 40%; }
.od-set-row--stripe td { background: #f7f8fa; }
/* hover after the stripe so it wins on a striped row; category rows open
   a page (pass B) */
tr.od-set-row:hover td { background: #f0f5ff; cursor: pointer; }
/* group rows are dividers inside the table, weaker than the thead: the
   header is the only header-weight band, so it governs every row */
.od-set-grp td { background: var(--fi-surface, #fff); padding: 10px 8px 4px; border-bottom: 1px solid #e0e0e0; }
.od-set-grp td:first-child { padding-left: 12px; }
.od-set-grp-in {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  font-size: 11px; font-weight: 700; color: var(--fi-text-muted, #6b7280);
  text-transform: uppercase; letter-spacing: 0.06em;
}
.od-set-bulk {
  text-transform: none; letter-spacing: 0;
  font-size: 11px; font-weight: 600; color: #6b7280;
  display: inline-flex; align-items: center; gap: 2px;
}
.od-set-bulk-btn {
  background: transparent; border: none; padding: 0 4px;
  color: var(--fi-ops-accent-strong); cursor: pointer;
  font: inherit;
}
.od-set-bulk-btn:hover { color: var(--fi-rail-accent); text-decoration: underline; }
.od-set-bulk-btn:disabled { opacity: 0.5; cursor: default; text-decoration: none; }
.od-set-bulk-sep { color: #d6dde6; padding: 0 2px; }
.od-set-cell-tg { width: 46px; }
.od-set-table tbody td.od-set-cell-tg { padding-top: 4px; }
.od-set-cell-ch { width: 26px; text-align: right; }
.od-set-row-name { font-size: 13px; font-weight: 500; color: var(--fi-text, #2c2c2a); line-height: 1.5; }
.od-set-row-sub { font-size: 12px; color: var(--fi-text-muted, #5c626e); line-height: 1.4; }
/* an off category is archived: the muted name is the only extra signal */
.od-set-row--off .od-set-row-name { color: var(--fi-text-muted, #5c626e); }
.od-set-cell-recip { font-size: 13px; color: #212529; }
/* the inherited-value strings ("+N outside",
   "No one"...) read on every row: the faint rung midnight already maps
   this class to, now on the light themes too */
.od-set-cell-muted { color: var(--fi-text-faint, #74726c); }
.od-set-chips { display: inline-flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.od-set-chips .od-set-cell-muted { margin-left: 2px; }
/* department chips read as the same pills the pickers use: od_tag_input's
   pill rules under its own class names, so midnight.css's pill inks apply */
.od-tag-input-pill {
  display: inline-flex; align-items: center; gap: 4px;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 12px; font-weight: 600; line-height: 1.5;
  white-space: nowrap;
}
.od-tag-input-pill--info { background: #e0f2fe; color: #075985; }
/* the row opener */
.od-set-chev {
  background: none; border: none; padding: 2px 4px;
  color: #9aa4b1; cursor: pointer; font-size: 11px; line-height: 1;
}
.od-set-chev:hover { color: var(--fi-ops-accent-strong); }
.od-set-chev:disabled { opacity: 0.5; cursor: default; }
/* advisory row under the Issue group header when nothing is on; the amber
   trio of .od-set-archive-note */
.od-set-note-row td {
  background: #fff8ef; color: #7a5400;
  font-size: 12.5px; line-height: 1.5;
  padding-top: 8px; padding-bottom: 8px;
}
/* usage pill: quiet tally beside categories that have events, so an admin
   sees what a flip will archive or restore. muted on purpose -- it is
   context, not a call to action. */
/* the Default chip: a row inheriting the default recipients wears it in the
   Recipients column, the count pill's fill so it reads as a fact, not a call
   to action; the title spells it out on hover */
.od-set-default-chip {
  display: inline-block;
  padding: 0 8px;
  border-radius: 999px;
  background: #e8edf3;
  color: #5b6572;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  cursor: default;
}
.od-set-cat-count {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e8edf3;
  color: #5b6572;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  vertical-align: 1px;
  cursor: default;
}
/* FI-1944 (#1816) the Custom / Renamed tag after a row's name: the count
   pill's quiet fill, so it reads as a fact about the row */
.od-set-custom-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e8edf3;
  color: #5b6572;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  vertical-align: 1px;
  cursor: default;
}
/* the group's Add row: a name box and Add on one line that wraps on a
   phone, the refusal or the limit line under it */
.od-set-add-row td { padding-top: 8px; padding-bottom: 10px; }
.od-set-add { display: flex; flex-wrap: wrap; gap: 8px; align-items: stretch; max-width: 480px; }
.od-set-add-input { flex: 1 1 200px; min-width: 0; }
/* the category page's Name, above the form switch */
.od-set-name-block { margin-bottom: 16px; }
.od-set-name-block .od-set-label { margin-top: 0; }
.od-set-name-input { max-width: 380px; }
/* Reset wears the page's themed link (od-set-action-secondary) at the
   hint's size */
.od-set-name-reset { margin-left: 6px; padding: 0; font-size: 12px; vertical-align: baseline; }
/* Remove on a custom page: the event page's Delete idiom (a danger link) */
.od-set-remove-cat { padding-left: 0; }
.od-set-remove-cat.od-act-danger:not(:disabled) { color: var(--fi-danger); }

/* huddle recipients: compact rows, remove and inline add edit a draft that
   rides the Huddle Save (FI-2201) */
.od-set-sub-list { max-width: 480px; margin-bottom: 8px; }
.od-set-sub-row {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 2px; font-size: 13px;
  border-bottom: 1px solid #edf1f6;
}
.od-set-sub-user { color: #6b7a8c; font-size: 12px; }
.od-set-sub-remove {
  margin-left: auto; border: none; background: transparent;
  color: #9aa4b1; cursor: pointer; padding: 2px 6px;
}
.od-set-sub-remove:hover { color: #c0392b; }
.od-set-sub-empty { color: #6b7a8c; font-size: 13px; padding: 4px 2px; }
.od-set-sub-add { display: flex; gap: 8px; max-width: 480px; }
.od-set-sub-input { max-width: 320px; }

/* FI-2166 category page. the header is the ERM event page's detail rail
   (ops_desk.vue .od-rail*), restated here under the same class names so
   midnight.css's rail rules apply to the portaled modal and the Settings
   page for free: round back button, eyebrow + title stacked, the actions in
   the right corner. */
.od-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}
.od-rail-back {
  background: #fff;
  border: 1px solid #c8d4df;
  width: 32px; height: 32px;
  border-radius: 50%;
  color: var(--fi-rail-accent);
  cursor: pointer;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.od-rail-back:hover { background: var(--fi-rail-accent); color: #fff; }
.od-rail-back:disabled { opacity: 0.5; cursor: default; }
.od-rail-back:disabled:hover { background: #fff; color: var(--fi-rail-accent); }
.od-rail-text {
  display: flex; flex-direction: column; justify-content: center;
  min-height: 32px;
  min-width: 0;
  flex: 1 1 auto;
}
.od-rail-eyebrow {
  font-size: 11px; line-height: 1.4;
  font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  text-indent: -0.5px;
  color: var(--fi-text-muted, #6b7a8c);
  margin-bottom: 1px;
}
.od-rail-title {
  margin: 0; padding: 0;
  font-size: 19px; line-height: 1.2;
  font-weight: 700; color: #1d1d1f;
  letter-spacing: -0.1px;
  display: flex; align-items: center; gap: 8px;
}
.od-rail-actions {
  display: inline-flex; align-items: center; gap: 6px;
  flex-shrink: 0;
}
.od-rail-actions .od-set-rail-hint { margin-right: 4px; }
/* the save error of the place in the header (horizon / midnight), first
   thing under the pinned header. the document's scroll-padding-top (the
   settings shell) clears the top bar and the header; the margin is the
   breathing room under them */
.od-set-page-error,
.od-set-section-error {
  margin: 0 0 12px;
  scroll-margin-top: 12px;
}
.od-set-section-error { margin: 12px 20px; }
.od-dept-name-error { scroll-margin-top: 12px; }
.od-set-page-body { min-width: 0; }
.od-set-form-col { max-width: 640px; }
/* the lead hint under a page label reads a step larger than a field hint */
.od-set-hint--lead { font-size: 12.5px; margin: 0 0 4px 0; line-height: 1.45; }
.od-set-hint--bad { color: #b91c1c; }
/* the Huddle and Brief line: the slug-status idiom, one glyph + one sentence */
.od-set-status-line {
  margin-top: 6px;
  font-size: 12.5px;
  line-height: 1.4;
  display: flex; align-items: flex-start; gap: 2px;
}
.od-set-status-line .fa { margin-top: 2px; }
.od-set-status-line--ok { color: #166534; }
.od-set-status-line--bad { color: #b91c1c; }

/* archive advisory on the category page while its switch is off */
.od-set-archive-note {
  margin-top: 12px;
  background: #fff8ef;
  border: 1px solid #f0d9b5;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12.5px;
  color: #7a5400;
  line-height: 1.5;
  max-width: 640px;
}

/* department entities: a stack of cards, each with a name field, a remove
   control, and a member tag-input. the custom-add row + quick-add chips sit
   above the list (FI-2201); its margin collapses into the Save row's. */
.od-dept-list {
  display: flex; flex-direction: column; gap: 10px;
  max-width: 640px;
  margin-bottom: 14px;
}
.od-dept-card {
  border: 1px solid #e1e8f0;
  border-radius: 6px;
  background: #f8fafc;
  padding: 12px 14px;
}
.od-dept-card-head {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 4px;
}
.od-dept-name-input { flex: 1; }
.od-dept-name-input ::v-deep .form-control {
  font-weight: 600;
}
.od-dept-remove {
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #e3c9cc;
  width: 30px; height: 30px;
  border-radius: 4px;
  color: #b45a5a;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .12s, color .12s, border-color .12s;
}
.od-dept-remove:hover { background: #fbeaea; border-color: #d99; color: #a23030; }
.od-dept-name-error {
  margin: 2px 0 8px 0;
  background: #fbe3e5;
  border: 1px solid #f5c0c4;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  color: #b91c1c;
}
.od-dept-member-empty {
  font-size: 12px; color: #6b7280;
  margin-top: 4px;
}
.od-dept-empty {
  font-size: 12.5px; color: #6b7280;
  padding: 12px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  max-width: 640px;
  margin-bottom: 14px;
}
.od-dept-suggest-row {
  display: flex; flex-wrap: wrap; gap: 6px;
  max-width: 640px;
}
.od-dept-chip {
  display: inline-flex; align-items: center; gap: 4px;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12.5px; font-weight: 600;
  line-height: 1.4;
  border: 1px solid;
  transition: background .12s, border-color .12s, color .12s;
}
.od-dept-chip--suggest {
  background: #f8fafc; color: #4b5563; border-color: #d6dde6;
  cursor: pointer;
}
.od-dept-chip--suggest:hover {
  background: #eef4fb; border-color: #b8c8da;
}
.od-dept-chip--suggest .fa { font-size: 10px; opacity: 0.7; }

.od-dept-custom-row {
  display: flex; gap: 8px;
  max-width: 480px;
  align-items: stretch;
}
.od-dept-custom-input { flex: 1; }

/* huddle picker: day chips + time picker. compact, mobile-friendly. */
.od-huddle-day-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin: 4px 0 4px 0;
}
.od-huddle-day-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 4px 12px; font-size: 12.5px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
  min-width: 48px;
}
.od-huddle-day-chip:hover { background: #eef4fb; border-color: #b8c8da; }
.od-huddle-day-chip:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}
.od-huddle-day-chip--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.od-huddle-day-chip--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }

.od-huddle-when-row {
  display: flex; gap: 24px; flex-wrap: wrap;
  margin-top: 4px;
}
.od-huddle-when-col { flex: 0 0 auto; min-width: 220px; }
.od-huddle-time-picker {
  display: flex; align-items: center; gap: 6px;
  max-width: 240px;
}
.od-huddle-time-sel { flex: 0 0 auto; width: auto !important; min-width: 64px; }
.od-huddle-time-sel--ampm { min-width: 70px; }
.od-huddle-time-colon { font-weight: 700; color: #4b5563; padding: 0 2px; }

.od-huddle-advanced-warning {
  margin-top: 12px;
  background: #fff8ef; border: 1px solid #f0d9b5; border-radius: 6px;
  padding: 8px 12px; font-size: 12.5px; color: #7a5400;
  line-height: 1.5;
}
.od-huddle-advanced-warning code {
  color: #7a5400; background: rgba(0,0,0,0.05); padding: 0 4px; border-radius: 3px;
}

/* FI-1964 import tab. colors come from themed --fi-* tokens (or token text
   colors) so the tab reads correctly in Classic, Horizon, and Midnight
   without a per-theme override. */
.od-imp-sa {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.od-imp-sa-email {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; overflow-wrap: anywhere; color: var(--fi-text);
}
.od-imp-copy { padding: 0 6px; flex: 0 0 auto; }
.od-imp-help-sub { margin-top: 4px; }
/* the status/count inks in this import panel are mixed toward --fi-text rather
   than used raw. midnight.css has no od-imp rules at all, so these sit
   straight on --fi-surface, where raw --fi-success is 3.03:1, --fi-danger
   2.40:1 and --fi-ops-accent 3.41:1 -- all under the 4.5:1 floor these 12-13px
   labels need. the mix deepens them on the light themes and lightens them on
   midnight, clearing both (success 6.71:1, danger 5.46:1, accent 6.82:1).
   same trick as .ad-failure-reason. */
.od-imp-test-ok { color: color-mix(in srgb, var(--fi-success) 45%, var(--fi-text)); }
.od-imp-run-label { margin-top: 24px; }
.od-imp-summary { margin-top: 12px; }
.od-imp-summary-head { font-weight: 600; color: var(--fi-text); margin-bottom: 6px; }
.od-imp-counts {
  display: flex; flex-wrap: wrap; gap: 12px; font-size: 12px; margin-bottom: 6px;
}
.od-imp-c { color: var(--fi-text-muted); }
.od-imp-c--new { color: color-mix(in srgb, var(--fi-success) 45%, var(--fi-text)); font-weight: 600; }
.od-imp-c--update { color: color-mix(in srgb, var(--fi-ops-accent) 45%, var(--fi-text)); font-weight: 600; }
.od-imp-applied { font-size: 12px; color: var(--fi-text-muted); margin-bottom: 6px; }
.od-imp-issues {
  margin: 6px 0 0; padding-left: 18px; font-size: 12px; color: var(--fi-text-muted);
}
.od-imp-issues li { margin-bottom: 2px; }
.od-imp-issue-more { font-style: italic; }

/* connect wizard: numbered steps. steps appear progressively (v-if), so
   there's no connector rail, the circles alone carry the sequence. */
.od-imp-step { position: relative; padding-left: 34px; margin-top: 18px; }
.od-imp-step-head {
  font-weight: 600; color: var(--fi-text); font-size: 13px; margin-bottom: 6px;
}
.od-imp-step-num {
  position: absolute; left: 0; top: -2px; width: 24px; height: 24px;
  border-radius: 50%; background: var(--fi-ops-accent); color: #fff;
  font-size: 12px; font-weight: 700; display: inline-flex;
  align-items: center; justify-content: center;
}
.od-imp-step-body { min-width: 0; }
.od-imp-connect-row { display: flex; gap: 8px; align-items: flex-start; }
.od-imp-connect-row .od-set-input { flex: 1 1 auto; }
.od-imp-connect-row .btn { flex: 0 0 auto; }
.od-imp-tab-list { display: flex; flex-direction: column; gap: 6px; max-width: 420px; }
.od-imp-tab-opt {
  display: flex; align-items: center; gap: 8px; margin: 0; padding: 7px 10px;
  border: 1px solid var(--fi-line); border-radius: 6px; cursor: pointer;
  font-size: 13px; color: var(--fi-text); background: var(--fi-surface);
}
.od-imp-tab-opt:hover { border-color: var(--fi-ops-accent); }
.od-imp-tab-opt--active {
  border-color: var(--fi-ops-accent);
  box-shadow: 0 0 0 1px var(--fi-ops-accent);
}
.od-imp-tab-opt .fa { color: var(--fi-text-muted); }
.od-imp-tab-radio { position: absolute; opacity: 0; pointer-events: none; }
.od-imp-tab-name { overflow-wrap: anywhere; }
.od-imp-cancel-wizard { padding-left: 0; font-size: 12px; }
.od-imp-c--muted { color: var(--fi-text-muted); }
.od-imp-skip-note { margin-bottom: 6px; }
.od-imp-attn {
  margin-top: 10px; padding-top: 8px; border-top: 1px solid var(--fi-line);
}
.od-imp-attn-head { font-weight: 600; font-size: 13px; color: color-mix(in srgb, var(--fi-danger) 45%, var(--fi-text)); }
.od-imp-err-group { margin-top: 6px; font-size: 12px; }
.od-imp-err-group-head { font-weight: 600; color: color-mix(in srgb, var(--fi-danger) 45%, var(--fi-text)); }
.od-imp-err-group-items { color: var(--fi-text-muted); overflow-wrap: anywhere; }
.od-imp-connected {
  display: flex; align-items: center; gap: 10px;
}
/* the glyph is 20px so 3:1 applies and raw success technically cleared it at
   3.03:1, but it is mixed like its siblings above so the panel's success ink
   stays one colour rather than two. */
.od-imp-connected-icon { font-size: 20px; color: color-mix(in srgb, var(--fi-success) 45%, var(--fi-text)); flex: 0 0 auto; }
.od-imp-connected-text { min-width: 0; flex: 1 1 auto; }
.od-imp-connected-title { font-weight: 600; color: var(--fi-text); overflow-wrap: anywhere; }
.od-imp-connected-sub { font-size: 12px; color: var(--fi-text-muted); }
.od-imp-change { flex: 0 0 auto; padding: 0 6px; font-size: 12px; }

/* family focus standard for the custom (non-bootstrap) controls, matching
   the list page's treatment. the sheet-tab option is a label wrapping a
   visually hidden radio, so its ring keys off the radio's focus state. */
.od-set-bulk-btn:focus-visible,
.od-set-chev:focus-visible,
.od-rail-back:focus-visible,
.od-dept-remove:focus-visible,
.od-dept-chip--suggest:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}
.od-imp-tab-opt:has(.od-imp-tab-radio:focus-visible) {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}

/* phone: the same table with the Recipients column gone (the sentence under
   the name carries it), taller cells and 44px tap boxes on the openers and
   the All / None buttons; the row switch itself goes to md in the template.
   the card collapses to eyebrow + name + pills (the page it opens carries
   the sentence) with its opener parked in the top-right corner */
@media (max-width: 768px) {
  .od-set-col-recip { display: none; }
  .od-set-every { padding: 10px 44px 10px 12px; }
  .od-set-every-sub { display: none; }
  .od-set-every .od-set-chev { top: 2px; right: 2px; transform: none; width: 44px; height: 44px; margin: 0; }
  .od-set-table tbody td { padding-top: 10px; padding-bottom: 10px; }
  .od-set-table tbody td.od-set-cell-tg { padding-top: 8px; }
  /* the 44px All / None boxes give the divider its height: ~50px band. the
     one-row Notes group has no boxes, so the label itself holds the same
     height, else its row reads as tacked onto the group above */
  .od-set-table tbody tr.od-set-grp td { padding-top: 3px; padding-bottom: 3px; }
  .od-set-grp-in { min-height: 44px; }
  .od-set-cell-ch { width: 48px; }
  .od-set-chev {
    min-width: 44px; min-height: 44px; margin: -10px 0;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .od-set-bulk-btn { min-height: 44px; padding: 0 8px; }
  /* ios safari zooms into a focused field under 16px */
  .od-set-add-input, .od-set-name-input { font-size: 16px; }
  /* 44px tap boxes on the Add row (box and button share the height) and on
     the Name's Reset, whose negative margins keep its hint line's height */
  .od-set-add-input, .od-set-add .btn { min-height: 44px; }
  .od-set-name-reset {
    display: inline-flex; align-items: center;
    min-height: 44px; margin: -13px 0 -13px 6px;
  }
}

/* below 1200 the band's actions take their own row on both hosts: the
   sentence's measure is under 500px there (the 800px dialog, or the
   horizon pane beside the 264px sidebar), so beside the sentence the save
   state would cost it up to two lines and shift the table on the first
   switch */
@media (max-width: 1199px) {
  .od-set-lead { flex-wrap: wrap; row-gap: 8px; }
  .od-set-lead-actions { width: 100%; margin-left: 0; }
}

/* a phone page gives the sections the page's own width: the horizon shell
   already pays the gutters, so the section's side padding only narrowed the
   content inside the header rail's edges (the modal host that needed it
   retired) */
@media (max-width: 768px) {
  /* horizon / midnight only: classic has no .fi-hz-content gutters, so the
     section's padding is its only margin there */
  .fi-hz-root .od-set-layout--page .od-set-section { padding-left: 0; padding-right: 0; }
  .fi-hz-root .od-set-layout--page .od-set-section-error { margin-left: 0; margin-right: 0; }
}
@media (max-width: 600px) {
  .od-set-input, .od-set-select, .od-set-tags, .od-set-url-box, .od-set-preview-box { max-width: 100%; }
  /* the page rail on a phone: tighter band, icon-only Save / Discard with
     thumb-sized boxes (sized in erm-mobile.css), the primary action in the
     right corner (the same treatment as the event page's rail). the lead
     band shares the shape so list -> page -> list keeps one band */
  .od-rail, .od-set-lead { padding: 10px 12px; gap: 10px; }
  .od-set-lead { row-gap: 8px; }
  /* level with the 40px rail buttons */
  .od-rail-back { width: 40px; height: 40px; }
  .od-rail-title { font-size: 17px; }
  .od-rail-actions { flex-wrap: wrap; gap: 6px; }
  /* button box + glyph size on phones live in src/assets/css/erm-mobile.css,
     shared with the log, the detail page and the board */
  .od-rail .od-btn-label { display: none; }
  .od-rail-actions .od-act-right { order: 1; }
}
</style>
